import React, { useEffect, useRef } from 'react'
import {
  BoxGeometry,
  BufferGeometry,
  Color,
  DirectionalLight,
  DoubleSide,
  Euler,
  Float32BufferAttribute,
  Fog,
  Group,
  HemisphereLight,
  InstancedMesh,
  Line,
  LineBasicMaterial,
  RingGeometry,
  CircleGeometry,
  ConeGeometry,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshLambertMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  Points,
  PointsMaterial,
  Quaternion,
  Scene,
  Vector3,
  WebGLRenderer,
} from 'three'
import {
  ACORN,
  SQUIRREL_BODY,
  VOXEL,
  buildAcornGeometry,
  buildHeartGeometry,
  buildMushroom,
  buildSpiderBody,
  buildSquirrelGeometries,
  buildWolfBody,
  buildPepper,
  buildFeather,
  buildSleepZ,
  buildCrocBody,
  buildScorpionBody,
  buildIguanaBody,
  buildDragonfly,
  buildNutGeometry,
  NUT_KINDS,
  stampDecor,
  meshDecor,
  DECOR,
  buildWolfLeg,
  buildFrog,
  buildSnakeHead,
  buildSnakeSegment,
  SNAKE_SEGMENT,
  WOLF_HIP,
  buildTree,
  damageTree,
  meshTree,
  TREE_HITS,
  TREE_VOXEL,
} from './squirrel-models'
import { createSound } from './squirrel-sound'

// Pixel heart for the lives display
const HEART = 'M1 0h2v1h1V0h2v1h1v2H6v1H5v1H4v1H3V5H2V4H1V3H0V1h1z'
// and a pixel acorn (cap and nut) for the +1 when one is eaten
const ACORN_CAP = 'M3 0h1v1h2v1h1v1H0V2h1V1h2z'
const ACORN_NUT = 'M1 3h5v2H5v1H4v1H3V6H2V5H1z'
const SVG_NS = 'http://www.w3.org/2000/svg'

// World units are CSS px in the flat view: x matches the screen, y is up from
// the nav's top border, and z runs back from that border (0) into the woods.
const SQ_W = SQUIRREL_BODY[0].length * VOXEL
const SQ_H = (SQUIRREL_BODY.length + 2) * VOXEL
const SQ_D = 6 * VOXEL
const MOUTH = SQ_W / 2 - 4 // Mouth offset from the squirrel's centre
const AC_R = (ACORN.length * VOXEL) / 2 // Acorn collision radius
const DEPTH = 700 // How far back the woods go

const GRAVITY = 900
const RUN_SPEED = 150
const PLAYER_SPEED = 170
const SPRINT_SPEED = 260 // holding Shift
const JUMP_SPEED = 290
const STEP = 5 // highest ledge it walks up without jumping
const MAX_ACORNS = 6
const MAX_TREE_ACORNS = 26
const HANGING_STAY = 0.2 // chance a ripe acorn never falls, so it has to be climbed for
const LIVES = 5
const BEST_KEY = 'nutsHighScore'

// Nut mode: the page drops off the bottom of the screen and the camera swings
// from side-on (flat pixel art) to behind the squirrel, like a third-person game
const STAGE_SCALE = 1.1 // page scale as it drops
const STAGE_OPACITY = 0.7
const stageShift = () => window.innerHeight + 40 // px the page drops by
const FLAT_FOV = 0.4 // degrees: far away and narrow is all but orthographic
const GAME_FOV = 45
const GAME_PITCH = (20 * Math.PI) / 180
const GAME_DISTANCE = 170
const INTRO_TIME = 2 // seconds the squirrel keeps itself busy on entering
const COUNTER_SIZE = 16 // px, Silkscreen is crispest in multiples of 8
const PROMPT_TIME = 10000
const TOAST_TIME = 2000
const DAY_LENGTH = 150 // seconds for a full day and night

// Each biome has its own creatures. All of them are slower than the squirrel
// walks, except wolves, which it can only outrun by sprinting.
//   wander/chase: speeds; sight: how close before it gives chase; reach: how
//   close before it crouches (windup seconds) and pounces; leap/lift: pounce
//   speed along the ground and up; day/night: how many roam about
const ENEMY_KINDS = {
  spider: { hp: 2, wander: 45, chase: 90, sight: 140, reach: 55, windup: 0.3, cooldown: 1.4, leap: 190, lift: 200, leapTime: 0.6, day: 3, night: 8, shadow: [14, 12] },
  wolf: { hp: 3, wander: 60, chase: 210, sight: 260, reach: 50, windup: 0.2, cooldown: 1.6, leap: 260, lift: 150, leapTime: 0.45, day: 2, night: 4, shadow: [26, 12] },
  frog: { hp: 1, wander: 30, chase: 80, sight: 160, reach: 85, windup: 0.45, cooldown: 1.2, leap: 240, lift: 280, leapTime: 0.75, day: 4, night: 6, shadow: [14, 14] },
  croc: { hp: 3, wander: 25, chase: 75, sight: 150, reach: 60, windup: 0.35, cooldown: 1.6, leap: 220, lift: 70, leapTime: 0.35, day: 3, night: 5, shadow: [34, 10] },
  scorpion: { hp: 2, wander: 35, chase: 100, sight: 150, reach: 50, windup: 0.3, cooldown: 1.3, leap: 220, lift: 90, leapTime: 0.4, day: 3, night: 6, shadow: [20, 14] },
  iguana: { hp: 2, wander: 40, chase: 120, sight: 160, reach: 50, windup: 0.25, cooldown: 1.4, leap: 230, lift: 140, leapTime: 0.5, day: 3, night: 2, shadow: [28, 10] },
  snake: { hp: 2, wander: 35, chase: 110, sight: 170, reach: 45, windup: 0.35, cooldown: 1.3, leap: 280, lift: 60, leapTime: 0.3, day: 3, night: 6, shadow: [24, 8] },
}

// Biomes: the first run always starts in the sunny meadow; a new run after
// dying or resetting starts somewhere else. Walking far enough crosses into
// neighbouring biomes, laid out in big cells of the world.
const BIOME_KINDS = ['meadow', 'fog', 'rain', 'storm', 'jungle', 'desert', 'tropical']
const BIOME_CELL = 12000 // rough size of a biome region: about 30 seconds' walk from the middle to the edge
const BIOMES = {
  meadow: { name: 'Meadow', enemy: 'spider', sky: '#87c5f0', skyMix: 0, light: 1, fog: 1, rain: 0, storm: 0, ground: '#5f6e2c', grass: ['#6d7f2e', '#7f8d34', '#5c6e28', '#8a8438'] },
  fog: { name: 'Foggy hollow', enemy: 'wolf', sky: '#aab3b8', skyMix: 0.85, light: 0.8, fog: 0.35, rain: 0, storm: 0, ground: '#56603e', grass: ['#68715a', '#737c62', '#5d6650', '#7a7d5e'] },
  rain: { name: 'Rainy woods', enemy: 'frog', sky: '#66778a', skyMix: 0.7, light: 0.7, fog: 0.7, rain: 1, storm: 0, ground: '#48652a', grass: ['#4f7a2a', '#5c8a30', '#46702a', '#5f8a3a'] },
  jungle: { name: 'Rainforest', enemy: 'croc', sky: '#8fc9a8', skyMix: 0.45, light: 0.9, fog: 0.65, rain: 0, storm: 0, ground: '#355e22', grass: ['#2f7a2a', '#3a8f30', '#28682a', '#4aa03a'] },
  desert: { name: 'Desert', enemy: 'scorpion', sky: '#f0d7a0', skyMix: 0.35, light: 1.05, fog: 0.9, rain: 0, storm: 0, ground: '#cfae70', grass: ['#c9a866', '#b89a5a', '#a8a050', '#d9b97a'] },
  // enemy can be a list: both kinds roam, sharing the numbers
  tropical: { name: 'Tropical shore', enemy: ['snake', 'iguana'], sky: '#7fd4f0', skyMix: 0.3, light: 1, fog: 1, rain: 0, storm: 0, ground: '#d6bf86', grass: ['#5aa03a', '#6ab04a', '#4a9030', '#7ac050'] },
  storm: { name: 'Thunderstorm', enemy: 'snake', sky: '#262b35', skyMix: 0.9, light: 0.45, fog: 0.6, rain: 1.6, storm: 1, ground: '#3c4d22', grass: ['#3f5226', '#4a5e2c', '#36481f', '#50612e'] },
}

// Endless woods, grown in square chunks around the squirrel
const CHUNK = 320
const LOAD_RADIUS = 900
const DROP_RADIUS = 1150
const SPIDER_LEASH = 900 // creatures further than this wander back near the squirrel

// Climbing trees, and hearts that grow in them while lives are down
const CLIMB_SPEED = 90
const MAX_HEARTS = 2

// Pouncing, and spiders that take two bites
const POUNCE_TIME = 0.42
const POISON_TIME = 2.5 // seconds it glows green after eating a mushroom
const SUPER_CHANCE = 0.02 // of a tree's acorns being a rainbow super nut
const SUPER_TIME = 10 // seconds of psychedelic invincibility
const SUPER_WARNING = 3 // final seconds it flashes, faster and faster
const POUNCE_COOLDOWN = 0.6
const COMBO_TIME = 3.5 // seconds to eat the next nut and keep a streak going
// Streaks of 3 nuts score double, 6 or more triple
const comboMultiplier = (streak) => (streak >= 6 ? 3 : streak >= 3 ? 2 : 1)
const GLIDE_FALL = 55 // fastest it sinks while gliding with Space held
const CHARGED_TIME = 15 // seconds a lightning-struck creature glows, quicker and sparking
const SHOT_SPEED = 170 // creatures' ranged attacks, in wet weather
const SPIDER_DEATH = 1 // seconds a beaten creature takes to flip and shrink away

const KEYS = {
  w: [0, -1],
  arrowup: [0, -1],
  s: [0, 1],
  arrowdown: [0, 1],
  a: [-1, 0],
  arrowleft: [-1, 0],
  d: [1, 0],
  arrowright: [1, 0],
}

const SKY_DAY = new Color('#87c5f0')
const SKY_NIGHT = new Color('#0b1230')
const SKY_DUSK = new Color('#f19a5c')
const SUN_DAY = new Color('#fff4dc')
const SUN_NIGHT = new Color('#8fa6ff')
const BOLT_WHITE = new Color('#eef2ff')

// What grows in each biome: tree kinds (repeats make one likelier), leaf
// colours as [leaves, highlight] pairs, ground plants as [kind, count, colours],
// and the biome's own nut
const FLORA = {
  meadow: {
    trees: ['oak', 'pine', 'birch', 'wide', 'shrub'],
    plants: [['flower', 26, ['red', 'yellow', 'white', 'purple']], ['rock', 3, ['rock']]],
    boulders: [1, ['#8d8a82', '#6e8a4a']], // grey, mossy on top
    litter: ['#c8612a', '#a83a24', '#d9a23a'],
    nut: 'acorn',
  },
  fog: {
    trees: ['spruce', 'spruce', 'dead', 'dead', 'birch'],
    foliage: [['#55635a', '#6f7a70'], ['#4a564d', '#626c60'], ['#646a5a', '#7e8470']],
    plants: [['rock', 9, ['rock']], ['paleShroom', 7, ['pale']], ['branch', 4, ['branch']]],
    boulders: [2, ['#7a7d78', '#9aa08e']], // pale with lichen
    litter: ['#7a6a52', '#6a5e4a', '#8a8470'],
    nut: 'chestnut',
  },
  rain: {
    trees: ['oak', 'willow', 'willow', 'wide', 'shrub'],
    foliage: [['#3f7a2a', '#5c9a3a'], ['#2f6b35', '#4c8a4a'], ['#5a8a2a', '#7aa63e']],
    plants: [['fern', 16, ['fern']], ['rock', 5, ['rock']], ['flower', 6, ['white', 'yellow']]],
    litter: ['#8aa63e', '#6a8a2a', '#a8a24a'],
    nut: 'walnut',
  },
  storm: {
    trees: ['spruce', 'pine', 'dead', 'wide'],
    foliage: [['#2c4a2a', '#3d5e33'], ['#4a3a2a', '#5e4a33'], ['#33452a', '#4a5a36']],
    plants: [['rock', 10, ['darkRock']], ['branch', 7, ['branch']], ['fern', 4, ['fern']]],
    boulders: [2, ['#55534f', '#3f3d3a']], // dark slate
    litter: ['#5e4a33', '#4a3a2a', '#6a5a3a'],
    nut: 'hazelnut',
  },
  desert: {
    trees: ['saguaro', 'saguaro', 'barrel', 'prickly', 'organpipe'],
    foliage: [['#4f8a3f', '#6fa85a'], ['#3f7a4a', '#5c9a63'], ['#6a8a3a', '#8aa64e']],
    plants: [['rock', 8, ['sandRock']], ['tumbleweed', 5, ['tumbleweed']], ['bones', 2, ['bone']], ['flower', 6, ['yellow', 'red']]],
    boulders: [2, ['#c48a52', '#a8683a']], // banded red sandstone
    litter: ['#c9955a', '#e3c78a', '#b88a55'],
    grassDensity: 0.3, // sparse, dry tufts
    nut: 'cactusfruit',
  },
  tropical: {
    trees: ['palm', 'palm', 'palm', 'shrub'],
    foliage: [['#2f9a3a', '#4cc04a'], ['#3aa060', '#5cc07a']],
    plants: [['shell', 14, ['shell', 'white']], ['starfish', 5, ['starfish']], ['fern', 6, ['jungleLeaf']], ['rock', 3, ['sandRock']]],
    boulders: [1, ['#9a8f7f', '#c9c2b2']], // weathered, salt-whitened
    litter: ['#f2e2b0', '#ffffff', '#f7b6c2'],
    grassDensity: 0.45,
    lagoons: true, // pools of water ringed with sand
    nut: 'coconut',
  },
  jungle: {
    trees: ['kapok', 'kapok', 'palm', 'palm', 'shrub'],
    foliage: [['#2f8a3a', '#4cb04a'], ['#1f7a4a', '#3aa060'], ['#4a9a2a', '#7ac040']],
    plants: [['fern', 18, ['jungleLeaf']], ['bigLeaf', 10, ['jungleLeaf', 'fern']], ['flower', 12, ['pink', 'orange', 'red']]],
    litter: ['#ff5fa2', '#f2c230', '#ff8a2a', '#7ac040'],
    extraTrees: 3, // a denser canopy
    nut: 'coconut',
  },
}
const NUT_COLOURS = {
  acorn: null, // the acorn keeps its theme-tinted materials for the flat view
  chestnut: ['#7a3b1c', '#a65a2e', '#d8c49a'],
  walnut: ['#b88a55', '#7a5530', '#b88a55'],
  hazelnut: ['#a86b34', '#6e4320', '#8a9a4a'],
  coconut: ['#6b4a2e', '#3a2618', '#6b4a2e'],
  cactusfruit: ['#d8305a', '#f2c4d0', '#4f8a3f'],
}
const DECOR_COLOURS = ['#4f7a2a', '#d8343a', '#f2c230', '#f2efe6', '#8a5ad0', '#7d7f80', '#5a7a3a', '#3f8a3a', '#2f9a4a', '#ff5fa2', '#ff8a2a', '#6b4a2a', '#d8d2c0', '#55585c', '#c9955a', '#efe8d8', '#9a7a4a', '#f7b6c2', '#ff7a3a']

// Ambient wildlife per biome, as how many of each (1 = the usual amount)
//   birds by day and bats by night, with the colours their flocks come in
const WILDLIFE = {
  meadow: { fireflies: 1, beetles: 1, butterflies: 1, bees: 1, dragonflies: 0.3, birds: 1, bats: 0.6, leaves: 1, mist: 0, splashes: 0, birdColours: ['#7a5a3a', '#5a4a3a', '#a07850', '#3a3a3a'], wingColours: ['#f2d06b', '#ffffff', '#e09a3e', '#9ad0ff', '#d9532b'], leafColours: ['#d9692b', '#f0a13a', '#b8322a', '#e3b23c'] },
  fog: { fireflies: 0.4, beetles: 0.6, butterflies: 0, bees: 0, dragonflies: 0, birds: 0.5, bats: 1.2, leaves: 0.4, mist: 1, splashes: 0, birdColours: ['#2a2a2e', '#3a3a40'], wingColours: ['#d8d8d0'], leafColours: ['#7a7a6a', '#8a8470'] },
  rain: { fireflies: 0.3, beetles: 0.6, butterflies: 0, bees: 0, dragonflies: 1, birds: 0.2, bats: 0.4, leaves: 0.3, mist: 0.35, splashes: 1, birdColours: ['#3a3a40'], wingColours: ['#ffffff'], leafColours: ['#5c9a3a', '#3f7a2a'] },
  storm: { fireflies: 0, beetles: 0.3, butterflies: 0, bees: 0, dragonflies: 0, birds: 0, bats: 0.8, leaves: 1.6, mist: 0.2, splashes: 1.6, birdColours: ['#2a2a2e'], wingColours: ['#ffffff'], leafColours: ['#3d5e33', '#5e4a33', '#7a6a3a'] },
  desert: { fireflies: 0, beetles: 1, butterflies: 0.2, bees: 0, dragonflies: 0, birds: 0.6, bats: 0.8, leaves: 0, mist: 0, splashes: 0, birdColours: ['#3a2a20', '#4a3a2a'], wingColours: ['#f2d06b'], leafColours: ['#c9955a'] },
  tropical: { fireflies: 0.6, beetles: 0.5, butterflies: 1, bees: 0.3, dragonflies: 0.8, birds: 1.2, bats: 0.5, leaves: 0.2, mist: 0, splashes: 0, birdColours: ['#f2f2f2', '#e8e8e8', '#e8382c', '#2a8af0'], wingColours: ['#ff8a2a', '#2f7bff', '#f5c400'], leafColours: ['#4cc04a'] },
  jungle: { fireflies: 1.5, beetles: 1, butterflies: 1.6, bees: 0.4, dragonflies: 1, birds: 1.3, bats: 0.7, leaves: 0.3, mist: 0.5, splashes: 0, birdColours: ['#e8382c', '#2a8af0', '#2fbf4a', '#f5c400', '#ff7a1a'], wingColours: ['#2f7bff', '#00c2ff', '#ff8a2a', '#c43cff'], leafColours: ['#2f8a3a', '#4cb04a'] },
}

// Autumn foliage, as [leaves, highlight] pairs
const FOLIAGE = [
  ['#d9692b', '#f0a13a'],
  ['#b8322a', '#d9532b'],
  ['#e3b23c', '#f2d06b'],
  ['#c4802f', '#e09a3e'],
  ['#8f2d1f', '#c0442c'],
  ['#a8572a', '#cf7a35'],
]

// Difficulty: how many lives, how many creatures and how quick they are, and
// how long between hearts. Chosen at the top right or on the pause card.
// The rest sharpen the creatures: how far they spot the squirrel (sight),
// how quick their pounces come (attack scales windup and cooldown), how far
// they spring from (reach), extra hits to kill (hp), how far ahead of a
// running squirrel they aim (lead), how many are found asleep (sleep), how
// soon they spit again (shots, and whether they spit in fine weather too),
// how soon they come back (respawn), and how many turn up ahead (ahead).
// Bosses: bossPace scales the waits before and between their attacks and how
// long the warnings last, bossCount how many shots, spikes or rocks each
// attack has, bossDaze how long they stay dazed, and bossSpit the hits after
// which they also spit while prowling. While lives are below the level's
// starting lives, a heart drops nearby about every heartDrop seconds, and
// when they're down to half or less, power-ups turn up lowPowers times as
// often again.
const CALM = { sight: 1, attack: 1, reach: 1, hp: 0, lead: 0, sleep: 0.55, shots: 1, spitDry: false, respawn: 1, ahead: 0 }
const DIFFICULTIES = {
  easy: { ...CALM, name: 'Easy', lives: 5, creatures: 0.7, speed: 0.85, hearts: 0.7, bossPace: 1.3, bossCount: 0.8, bossDaze: 1.25, bossSpit: 3, heartDrop: 18, lowPowers: 1.5 },
  normal: { ...CALM, name: 'Normal', lives: 5, creatures: 1, speed: 1, hearts: 1, bossPace: 1, bossCount: 1, bossDaze: 1, bossSpit: 2, heartDrop: 32, lowPowers: 0.8 },
  hard: { ...CALM, name: 'Hard', lives: 4, creatures: 1.5, speed: 1.12, hearts: 1.5, bossPace: 0.8, bossCount: 1.25, bossDaze: 0.85, bossSpit: 1, heartDrop: 55, lowPowers: 0.4 },
  insane: {
    name: 'Insane',
    lives: 3,
    creatures: 2.6,
    speed: 1.3,
    hearts: 2,
    sight: 1.7,
    attack: 0.5,
    reach: 1.35,
    hp: 1,
    lead: 1,
    sleep: 0.15,
    shots: 0.5,
    spitDry: true,
    respawn: 0.4,
    ahead: 0.65,
    bossPace: 0.55,
    bossCount: 1.6,
    bossDaze: 0.7,
    bossSpit: 0,
    heartDrop: 150,
    lowPowers: 0.1,
  },
}
const DIFFICULTY_LEVELS = Object.keys(DIFFICULTIES)
const DIFFICULTY_KEY = 'nutsDifficulty'
const BONUS_HEARTS = 2 // most extra heart slots won from bosses in a run
const MAX_LIVES = Math.max(...DIFFICULTY_LEVELS.map((d) => DIFFICULTIES[d].lives)) + BONUS_HEARTS
const MAX_CREATURES = Math.max(...DIFFICULTY_LEVELS.map((d) => DIFFICULTIES[d].creatures))

const stored = (key) => {
  try {
    return window.localStorage.getItem(key)
  } catch (e) {
    return null
  }
}
const store = (key, value) => {
  try {
    window.localStorage.setItem(key, value)
  } catch (e) {
    // Storage blocked: it just won't be remembered
  }
}
const readDifficulty = () => {
  const level = stored(DIFFICULTY_KEY)
  return DIFFICULTIES[level] ? level : 'easy'
}
// Each difficulty keeps its own best score
const bestKey = (level) => (level === 'easy' ? BEST_KEY : `${BEST_KEY}:${level}`)
const readBest = (level) => parseInt(stored(bestKey(level)), 10) || 0
const saveBest = (level, best) => store(bestKey(level), best)

// Kept outside the component so the squirrel survives page transitions
const world = {
  squirrel: null,
  acorns: [],
  crumbs: [],
  spawnIn: 1.5,
  staged: false,
  time: 0.08, // Time of day, 0-1; starts in the morning
  seed: 1997, // The woods; a new one grows after each game over
  eaten: new Set(), // mushrooms eaten this run, so regrown chunks leave them out
  felled: new Map(), // pounces each tree has taken this run, so regrown chunks match
  harvested: new Set(), // trees that have grown their one acorn this run
  startBiome: 'meadow',
  biomeStart: { x: 0, z: 0 }, // where the run began, at the middle of its biome
  score: 0,
  lives: LIVES,
}

const rand = (min, max) => min + Math.random() * (max - min)
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const lerp = (a, b, t) => a + (b - a) * t
const ease = (t) => t * t * (3 - 2 * t)
const flat = (v) => Math.hypot(v.x, v.z)
// Theme colours can be 8-digit hex, which three.js doesn't parse
const solid = (colour) => (/^#[0-9a-f]{8}$/i.test(colour) ? colour.slice(0, 7) : colour)

// Seeded so the woods grow the same way until the game is lost
const seeded = (seed) => () => {
  seed = (seed + 0x6d2b79f5) | 0
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}

const Squirrel = () => {
  const canvasRef = useRef(null)
  const counterRef = useRef(null)
  const promptRef = useRef(null)
  const toastRef = useRef(null)
  const hintRef = useRef(null)
  const exitRef = useRef(null)
  const overlayRef = useRef(null)
  const popupsRef = useRef(null)
  const soundRef = useRef(null)
  const pauseRef = useRef(null)
  const levelRef = useRef(null)
  const infoRef = useRef(null)
  const fullRef = useRef(null)
  const bossRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const counter = counterRef.current
    const prompt = promptRef.current
    const toast = toastRef.current
    const hint = hintRef.current
    const exitBar = exitRef.current
    const overlay = overlayRef.current
    const popups = popupsRef.current
    const soundButton = soundRef.current
    const pauseButton = pauseRef.current
    const levelButton = levelRef.current
    const infoButton = infoRef.current
    const fullButton = fullRef.current
    const levelChoices = [...overlay.querySelectorAll('.nut-overlay__levels button')]
    const sound = createSound()
    const countEl = counter.querySelector('.nuts-eaten__count')
    const bestEl = counter.querySelector('.nuts-eaten__best')
    const comboEl = counter.querySelector('.nuts-eaten__combo')
    const powersEl = counter.querySelector('.nuts-eaten__powers')
    const bossBar = bossRef.current
    const bossNameEl = bossBar.querySelector('.boss-bar__title')
    const bossFill = bossBar.querySelector('.boss-bar__fill')
    // The streak multiplier next to the score
    function showCombo() {
      const sq = world.squirrel
      const mult = sq ? comboMultiplier(sq.streak || 0) : 1
      comboEl.textContent = mult > 1 ? `×${mult}` : ''
    }
    const lifeIcons = [...counter.querySelectorAll('.nuts-eaten__life')]
    const nav = document.querySelector('nav')
    const stage = document.querySelector('.stage')
    const cursorEl = document.querySelector('#cursor')
    if (!nav) return undefined

    let renderer
    try {
      renderer = new WebGLRenderer({ canvas, alpha: true, antialias: false })
    } catch (e) {
      return undefined // No WebGL: no squirrel
    }
    renderer.autoClear = false

    if (!DIFFICULTIES[world.difficulty]) world.difficulty = readDifficulty()
    const level = () => DIFFICULTIES[world.difficulty]
    const maxLives = () => level().lives + (world.bonusLives || 0)

    // ----- Pointer capture -----
    // In nut mode the cursor is captured so it can't wander out of the window.
    // The browser frees it on Escape, which leaves nut mode. Pausing frees it
    // too so the buttons can be used, and carrying on captures it again.
    let freedOnPurpose = false
    let switchedFullAt = 0
    function lockPointer() {
      const el = document.documentElement
      if (touchOnly || !world.staged || document.pointerLockElement || !el.requestPointerLock) return
      try {
        const request = el.requestPointerLock()
        if (request && request.catch) request.catch(() => {})
      } catch (e) {
        // Not allowed just now (no click or key press behind it); stays free
      }
    }
    function releasePointer() {
      if (!document.pointerLockElement) return
      freedOnPurpose = true
      document.exitPointerLock()
    }
    function onLockChange() {
      const locked = !!document.pointerLockElement
      document.documentElement.classList.toggle('nut-locked', locked)
      if (locked) return
      if (freedOnPurpose) {
        freedOnPurpose = false
        return
      }
      // Switching fullscreen with L or the button can free it too: take it back
      if (Date.now() - switchedFullAt < 1500) {
        if (!world.paused) lockPointer()
        return
      }
      // Freed by the browser: Escape leaves nut mode, while switching away
      // from the window just pauses (see onBlur)
      setTimeout(() => {
        if (world.staged && document.hasFocus() && !document.hidden) setStaged(false)
      }, 120)
    }
    let best = readBest(world.difficulty)
    const bestLabel = counter.querySelector('.nuts-eaten__label')
    const showScore = () => {
      countEl.textContent = world.score
      bestEl.textContent = best
      bestLabel.textContent = `${level().name} best`
      lifeIcons.forEach((h, i) => {
        h.classList.toggle('lost', i >= world.lives)
        h.style.display = i < maxLives() ? '' : 'none'
      })
    }
    if (world.lives > maxLives()) world.lives = maxLives()
    showScore()

    // Nut mode is desktop only; touch screens just get the flat squirrel
    const touchOnly = window.matchMedia('(hover: none)').matches
    const mouse = { x: -9999, y: -9999 }
    const keys = new Set()
    let sprinting = false // Shift held
    let spaceHeld = false // for gliding
    // ground: nav's top edge on screen, p: progress into nut mode, e: eased p
    const view = { ground: 0, p: 0, e: 0, restGround: 0 }
    const bounds = { x0: 0, x1: 0 }
    let lastCounter = ''
    let promptTimer
    let toastTimer
    let frame
    let last = performance.now()

    // ----- Materials -----
    const themeMain = new Color()
    const themeBg = new Color()
    // Theme-coloured and flat in the flat view, naturally coloured and lit in nut mode
    const tinted = []
    const tint = (natural, role = 'main') => {
      const m = new MeshLambertMaterial()
      m.userData = { natural: new Color(natural), role }
      tinted.push(m)
      return m
    }
    const squirrelMats = [tint('#9c5326'), tint('#140d08', 'bg'), tint('#c7753a')]
    const acornMats = [tint('#c68642'), tint('#7a4a22', 'bg'), tint('#5c3a1e')]
    // Super nuts cycle through the rainbow and glow
    const superMats = [0, 0.33, 0.66].map((offset) => {
      const m = new MeshLambertMaterial()
      m.userData.offset = offset
      return m
    })
    const lambert = (colour) => new MeshLambertMaterial({ color: colour })
    const treeMats = [
      lambert('#6b4226'),
      lambert('#d9692b'),
      lambert('#f0a13a'),
      lambert('#e8e4da'),
      lambert('#2b2724'),
      lambert('#3f7a2a'), // vines
      lambert('#a07c4c'), // palm bark
      lambert('#6b6660'), // dead grey bark
      lambert('#e0306a'), // cactus fruit
      lambert('#ffd23f'), // cactus flowers
      lambert('#8d8a82'), // stone
      lambert('#6e8a4a'), // moss on stone
    ]
    const decorMats = DECOR_COLOURS.map((c) => lambert(c))
    // Every biome's nut, in its own colours
    const nutGeos = {}
    const nutMats = {}
    NUT_KINDS.forEach((kind) => {
      nutGeos[kind] = kind === 'acorn' ? null : buildNutGeometry(kind)
      nutMats[kind] = NUT_COLOURS[kind] ? NUT_COLOURS[kind].map((c) => lambert(c)) : null
    })
    const mushroomMats = [lambert('#d62828'), lambert('#f4efe6')]
    const spiderMats = [lambert('#1c1a1f'), new MeshBasicMaterial({ color: '#ff2a2a' })]
    const hurtMat = new MeshBasicMaterial({ color: '#ff3030' })
    const glowMat = new MeshBasicMaterial({ color: '#9ff4ff' }) // charged by lightning
    const groundMat = lambert('#5f6e2c')
    const grassMat = lambert('#ffffff')
    const shadowMat = new MeshBasicMaterial({ color: 0x000000, transparent: true, depthWrite: false })
    const starMat = new PointsMaterial({
      color: 0xffffff,
      size: 2,
      sizeAttenuation: false,
      transparent: true,
      fog: false,
    })
    const allMats = [
      ...squirrelMats,
      ...acornMats,
      ...superMats,
      ...treeMats,
      ...mushroomMats,
      ...spiderMats,
      hurtMat,
      groundMat,
      grassMat,
      shadowMat,
      starMat,
    ]

    const readColours = () => {
      const styles = getComputedStyle(document.documentElement)
      themeMain.set(solid(styles.getPropertyValue('--main-color').trim() || '#000'))
      themeBg.set(solid(styles.getPropertyValue('--background-color').trim() || '#fff'))
    }

    // ----- Scene -----
    const scene = new Scene()
    scene.fog = new Fog(0xffffff, 1e6, 2e6)
    const camera = new PerspectiveCamera(FLAT_FOV, 1, 1, 1000)
    const hemi = new HemisphereLight(0xffffff, 0x3a5a2a, 0)
    const sun = new DirectionalLight(0xffffff, 0)
    scene.add(hemi, sun, sun.target)

    const geometries = []
    const keep = (g) => {
      geometries.push(g)
      return g
    }
    const squirrelGeo = buildSquirrelGeometries()
    keep(squirrelGeo.body)
    squirrelGeo.legs.forEach(keep)
    const acornGeo = keep(buildAcornGeometry())
    const shadowGeo = keep(new PlaneGeometry(1, 1).rotateX(-Math.PI / 2))
    const crumbGeo = keep(new BoxGeometry(VOXEL, VOXEL, VOXEL))

    const shadowMesh = () => new Mesh(shadowGeo, shadowMat)

    const squirrelGroup = new Group()
    squirrelGroup.rotation.order = 'YXZ'
    const squirrelBody = new Mesh(squirrelGeo.body, squirrelMats)
    const squirrelLegs = squirrelGeo.legs.map((g) => new Mesh(g, squirrelMats))
    squirrelGroup.add(squirrelBody, ...squirrelLegs)
    const squirrelShadow = shadowMesh()
    scene.add(squirrelGroup, squirrelShadow)

    // Everything that only exists in nut mode grows out of the ground with it
    const woods = new Group()
    scene.add(woods)

    // Ground and stars travel with the squirrel
    const ground = new Mesh(keep(new PlaneGeometry(6000, 6000).rotateX(-Math.PI / 2)), groundMat)
    const stars = new Points(keep(new BufferGeometry()), starMat)
    woods.add(ground, stars)
    const scatterStars = () => {
      const random = seeded(42)
      const starPos = []
      for (let i = 0; i < 400; i++) {
        const a = random() * Math.PI * 2
        const up = 0.01 + random() * random() * 0.6
        const across = 1800 * Math.sqrt(1 - up * up)
        starPos.push(Math.cos(a) * across, up * 1800, Math.sin(a) * across)
      }
      stars.geometry.setAttribute('position', new Float32BufferAttribute(starPos, 3))
    }
    scatterStars()

    const spiderBodyGeo = keep(buildSpiderBody())
    const spiderLegGeo = keep(
      new BoxGeometry(VOXEL * 0.8, VOXEL * 0.8, VOXEL * 4).translate(0, 0, VOXEL * 2),
    )

    const wolfBodyGeo = keep(buildWolfBody())
    const wolfLegGeo = keep(buildWolfLeg())
    const frogGeo = keep(buildFrog())
    const snakeHeadGeo = keep(buildSnakeHead())
    const snakeSegGeo = keep(buildSnakeSegment())
    const wolfMats = [lambert('#7b8088'), lambert('#454951'), new MeshBasicMaterial({ color: '#ffd23f' })]
    const frogMats = [lambert('#4f8a3a'), lambert('#c9d27a'), new MeshBasicMaterial({ color: '#ffcc33' }), lambert('#2f5a26')]
    const snakeMats = [lambert('#3b3f2a'), lambert('#c9a227'), new MeshBasicMaterial({ color: '#ff3b3b' }), new MeshBasicMaterial({ color: '#ff3355' })]
    const SNAKE_SEGMENTS = 6
    const crocBodyGeo = keep(buildCrocBody())
    const crocLegGeo = keep(new BoxGeometry(VOXEL * 1.6, VOXEL * 2, VOXEL * 1.6).translate(0, -VOXEL, 0))
    const scorpionGeo = keep(buildScorpionBody())
    const scorpionMats = [lambert('#c99a4a'), lambert('#5a3a1a'), new MeshBasicMaterial({ color: '#1a1a1a' })]
    const iguanaGeo = keep(buildIguanaBody())
    const iguanaMats = [lambert('#4a9a3a'), lambert('#ff8a2a'), new MeshBasicMaterial({ color: '#1a1a1a' }), lambert('#c9d27a')]
    const crocMats = [lambert('#4a6a32'), lambert('#2f4520'), new MeshBasicMaterial({ color: '#ffd23f' }), lambert('#f2efe6')]

    // One creature of a biome. parts are the meshes that flash red when hurt.
    const makeEnemy = (kind) => {
      const group = new Group()
      // Yaw first, then roll about its own length (for flipping over)
      group.rotation.order = 'YXZ'
      const parts = []
      const add = (parent, mesh) => {
        parent.add(mesh)
        parts.push([mesh, mesh.material])
        return mesh
      }
      const legs = []
      const segs = []
      let body
      if (kind === 'spider') {
        body = add(group, new Mesh(spiderBodyGeo, spiderMats))
        for (let i = 0; i < 8; i++) {
          const side = i < 4 ? 1 : -1
          const pivot = new Group()
          pivot.position.set((-1.5 + (i % 4)) * VOXEL * 1.1, VOXEL * 2, side * VOXEL)
          const leg = add(pivot, new Mesh(spiderLegGeo, spiderMats[0]))
          leg.rotation.x = side * 0.5
          if (side < 0) leg.rotation.y = Math.PI
          group.add(pivot)
          legs.push(pivot)
        }
      } else if (kind === 'wolf') {
        body = add(group, new Mesh(wolfBodyGeo, wolfMats))
        ;[[3.5, 1.5], [3.5, -1.5], [-4.5, 1.5], [-4.5, -1.5]].forEach(([x, z]) => {
          const pivot = new Group()
          pivot.position.set(x * VOXEL, WOLF_HIP * VOXEL, z * VOXEL)
          add(pivot, new Mesh(wolfLegGeo, wolfMats[1]))
          group.add(pivot)
          legs.push(pivot)
        })
      } else if (kind === 'frog') {
        body = add(group, new Mesh(frogGeo, frogMats))
      } else if (kind === 'scorpion') {
        body = add(group, new Mesh(scorpionGeo, scorpionMats))
        for (let i = 0; i < 6; i++) {
          const side = i < 3 ? 1 : -1
          const pivot = new Group()
          pivot.position.set((-1.5 + (i % 3) * 1.4) * VOXEL, VOXEL * 1.6, side * VOXEL * (side > 0 ? 2 : 1))
          const leg = add(pivot, new Mesh(spiderLegGeo, scorpionMats[1]))
          leg.rotation.x = side * 0.7
          if (side < 0) leg.rotation.y = Math.PI
          group.add(pivot)
          legs.push(pivot)
        }
      } else if (kind === 'croc' || kind === 'iguana') {
        const iguana = kind === 'iguana'
        body = add(group, new Mesh(iguana ? iguanaGeo : crocBodyGeo, iguana ? iguanaMats : crocMats))
        ;(iguana ? [[2, 2.5], [2, -3.5], [-3, 2.5], [-3, -3.5]] : [[3, 3], [3, -3], [-4, 3], [-4, -3]]).forEach(([x, z]) => {
          const pivot = new Group()
          pivot.position.set(x * VOXEL, 2 * VOXEL, z * VOXEL)
          add(pivot, new Mesh(crocLegGeo, iguana ? iguanaMats[0] : crocMats[0]))
          group.add(pivot)
          legs.push(pivot)
        })
      } else {
        body = add(group, new Mesh(snakeHeadGeo, snakeMats))
        for (let i = 0; i < SNAKE_SEGMENTS; i++) {
          segs.push(add(group, new Mesh(snakeSegGeo, snakeMats[i % 2 ? 1 : 0])))
        }
      }
      const shadow = shadowMesh()
      woods.add(group, shadow)
      return {
        kind,
        cfg: ENEMY_KINDS[kind],
        group,
        body,
        parts,
        legs,
        segs,
        shadow,
        hp: ENEMY_KINDS[kind].hp,
        flashFor: 0,
        knock: new Vector3(),
        pos: new Vector3(),
        heading: rand(0, Math.PI * 2),
        phase: rand(0, 10),
        alive: false,
        deadFor: 0,
        respawnIn: rand(0, 3),
        wanderTo: null,
      }
    }
    // Every creature, a pool of each kind; only the current biome's come out
    const spiders = []
    Object.keys(ENEMY_KINDS).forEach((kind) => {
      for (let i = 0; i < Math.ceil(ENEMY_KINDS[kind].night * MAX_CREATURES); i++) {
        const en = makeEnemy(kind)
        en.index = i
        spiders.push(en)
      }
    })

    // ----- Biomes -----
    // Big cells around where the run began, each with its own biome. The
    // starting cell is centred on the start, so it's a long walk to the next.
    const biomeOfCell = (i, j) => {
      const random = seeded(Math.imul(world.seed ^ 0x5bd1e995, 31) ^ Math.imul(i, 73856093) ^ Math.imul(j, 19349663))
      const jitter = [(random() - 0.5) * 0.5 * BIOME_CELL, (random() - 0.5) * 0.5 * BIOME_CELL]
      const kind = BIOME_KINDS[Math.floor(random() * BIOME_KINDS.length)]
      if (i === 0 && j === 0) return { x: 0, z: 0, kind: world.startBiome }
      return { x: i * BIOME_CELL + jitter[0], z: j * BIOME_CELL + jitter[1], kind }
    }
    // Biome at a point in generation coordinates
    const biomeAt = (gx, gz) => {
      const lx = gx - world.biomeStart.x
      const lz = gz - world.biomeStart.z
      const ci = Math.round(lx / BIOME_CELL)
      const cj = Math.round(lz / BIOME_CELL)
      let best = null
      let bestD = Infinity
      for (let i = ci - 1; i <= ci + 1; i++) {
        for (let j = cj - 1; j <= cj + 1; j++) {
          const cell = biomeOfCell(i, j)
          const d = Math.hypot(cell.x - lx, cell.z - lz)
          if (d < bestD) {
            bestD = d
            best = cell.kind
          }
        }
      }
      return best
    }
    let biome = 'meadow' // where the squirrel is now
    // Weather that rolls in wherever the squirrel is: after a while of clear
    // skies, rain or a thunderstorm passes over, then clears again
    let weather = 'clear'
    let weatherFor = rand(60, 110)
    const updateWeather = (dt) => {
      if (!world.staged || world.paused) return
      weatherFor -= dt
      if (weatherFor > 0) return
      if (weather === 'clear') {
        weather = Math.random() < 0.45 ? 'storm' : 'rain'
        weatherFor = rand(35, 70)
      } else {
        weather = 'clear'
        weatherFor = rand(60, 120)
      }
    }
    // What the sky's doing: a rainy or stormy biome, or weather passing over
    const wetness = () => {
      if (!world.staged) return 'clear'
      if (biome === 'storm' || weather === 'storm') return 'storm'
      if (biome === 'rain' || weather === 'rain') return 'rain'
      return 'clear'
    }
    // The look of the weather, eased towards the current biome's
    const atmo = {
      sky: new Color(BIOMES.meadow.sky),
      skyMix: 0,
      light: 1,
      fog: 1,
      rain: 0,
      storm: 0,
      ground: new Color(BIOMES.meadow.ground),
    }
    const biomeSky = {}
    const biomeGround = {}
    const biomeGrass = {}
    const biomeLitter = {}
    BIOME_KINDS.forEach((k) => {
      biomeSky[k] = new Color(BIOMES[k].sky)
      biomeGround[k] = new Color(BIOMES[k].ground)
      biomeGrass[k] = BIOMES[k].grass.map((c) => new Color(c))
      biomeLitter[k] = FLORA[k].litter.map((c) => new Color(c))
    })
    const startRun = (kind) => {
      const s = world.squirrel
      world.startBiome = kind
      world.biomeStart = { x: (s ? s.pos.x : 0) + origin.x, z: (s ? s.pos.z : 0) + origin.z }
      biome = kind
      weather = 'clear'
      weatherFor = rand(60, 110)
    }

    // Rain: streaks falling around the squirrel
    const RAIN_DROPS = 700
    const rainMat = new MeshBasicMaterial({ color: '#c3d2e2', transparent: true, opacity: 0.55 })
    const rainMesh = new InstancedMesh(keep(new BoxGeometry(0.35, 11, 0.35)), rainMat, RAIN_DROPS)
    rainMesh.frustumCulled = false
    rainMesh.count = 0
    woods.add(rainMesh)
    const drops = Array.from({ length: RAIN_DROPS }, () => new Vector3(rand(-380, 380), rand(0, 320), rand(-420, 260)))
    const rainQuat = new Quaternion().setFromEuler(new Euler(0, 0, 0.12))
    const rainScale = new Vector3(1, 1, 1)
    const rainAt = new Vector3()
    const updateRain = (dt) => {
      const s = world.squirrel
      const amount = view.p > 0.5 ? clamp(atmo.rain / 1.6, 0, 1) : 0
      rainMesh.count = Math.round(RAIN_DROPS * amount)
      if (!rainMesh.count) return
      const fall = (420 + atmo.rain * 160) * dt
      for (let i = 0; i < rainMesh.count; i++) {
        const d = drops[i]
        d.y -= fall
        d.x -= fall * 0.12
        if (d.y < 0) {
          d.set(rand(-380, 380), rand(260, 340), rand(-420, 260))
        }
        rainAt.set(s.pos.x + d.x, d.y, s.pos.z + d.z)
        bugMatrix.compose(rainAt, rainQuat, rainScale)
        rainMesh.setMatrixAt(i, bugMatrix)
      }
      rainMesh.instanceMatrix.needsUpdate = true
    }

    // Storms: lightning flashes, a jagged bolt far off, then thunder
    const boltMat = new LineBasicMaterial({ color: '#f4f7ff', fog: false, transparent: true })
    const bolt = new Line(keep(new BufferGeometry()), boltMat)
    bolt.frustumCulled = false
    bolt.visible = false
    woods.add(bolt)
    let lightningIn = 6
    let flash = 0
    const updateLightning = (dt) => {
      flash = Math.max(0, flash - dt * 2.5)
      bolt.visible = flash > 0.35 && view.p > 0.5
      boltMat.opacity = clamp(flash, 0, 1)
      if (!world.staged || view.p < 0.5 || atmo.storm < 0.5 || world.paused) return
      lightningIn -= dt
      if (lightningIn > 0) return
      lightningIn = rand(4, 11)
      flash = 1
      const s = world.squirrel
      let base = new Vector3(s.pos.x + rand(-700, 700), 0, s.pos.z - rand(700, 1300))
      // Now and then it hits a creature nearby, leaving it charged and glowing
      const nearby = spiders.filter((sp) => sp.alive && !sp.leaving && flat(sp.pos.clone().sub(s.pos)) < 420)
      if (nearby.length && Math.random() < 0.55) {
        const struck = nearby[Math.floor(Math.random() * nearby.length)]
        struck.chargedFor = CHARGED_TIME
        base = struck.pos.clone()
        sound.play('zap')
      }
      const points = []
      let x = base.x
      for (let y = 700; y >= 0; y -= 70) {
        points.push(x, y, base.z)
        x += rand(-45, 45)
      }
      bolt.geometry.setAttribute('position', new Float32BufferAttribute(points, 3))
      sound.play('thunder', rand(0.3, 1.4))
    }

    // ----- Endless woods -----
    // Each chunk grows from the run's seed and its coordinates, so walking back
    // over old ground grows the same trees. Far chunks fade and are dropped.
    const grassGeo = keep(new BoxGeometry(VOXEL, 1, VOXEL).translate(0, 0.5, 0))
    // Late-season grass with fallen leaves among it
    const greens = ['#6d7f2e', '#7f8d34', '#5c6e28', '#8a8438'].map((c) => new Color(c))
    const chunks = new Map()
    let trees = []
    let mushrooms = []
    let waters = [] // tropical pools, in world coordinates
    // Where generation coordinates sit in the world; moved by recentre()
    const origin = { x: 0, z: 0 }

    const chunkSeed = (cx, cz) =>
      Math.imul(world.seed, 73856093) ^ Math.imul(cx, 19349663) ^ Math.imul(cz, 83492791)

    // Keep the line the squirrel walks in the flat view clear
    const inClearingAt = (x, z) => x > bounds.x0 - 60 && x < bounds.x1 + 60 && z > -130 && z < 90
    const lagoonGeo = keep(new CircleGeometry(1, 28).rotateX(-Math.PI / 2))
    const waterMat = new MeshLambertMaterial({ color: '#2fb2d6', emissive: '#0a4a5e', transparent: true, opacity: 0.85 })
    const sandMat = lambert('#ecd9a0')
    const buildChunk = (cx, cz, grown) => {
      const random = seeded(chunkSeed(cx, cz))
      const gx = cx * CHUNK
      const gz = cz * CHUNK
      const group = new Group()
      group.position.set(gx - origin.x, 0, gz - origin.z)
      const chunk = { cx, cz, group, trees: [], mushrooms: [], grass: null, grow: grown ? 1 : 0 }
      const chunkBiome = world.staged ? biomeAt(gx + CHUNK / 2, gz + CHUNK / 2) : 'meadow'
      const grassOf = world.staged ? biomeGrass[chunkBiome] : greens
      const flora = FLORA[chunkBiome]
      // Tropical shores have pools of water ringed with sand
      chunk.water = []
      if (flora.lagoons) {
        const pools = random() < 0.65 ? 1 : 2
        for (let i = 0; i < pools; i++) {
          const w = { lx: random() * CHUNK, lz: random() * CHUNK, rx: 45 + random() * 50, rz: 35 + random() * 40 }
          if (inClearingAt(gx + w.lx, gz + w.lz)) continue
          chunk.water.push(w)
          const sand = new Mesh(lagoonGeo, sandMat)
          sand.position.set(w.lx, 0.15, w.lz)
          sand.scale.set(w.rx + 22, 1, w.rz + 22)
          const water = new Mesh(lagoonGeo, waterMat)
          water.position.set(w.lx, 0.45, w.lz)
          water.scale.set(w.rx, 1, w.rz)
          group.add(sand, water)
        }
      }
      const wet = (x, z, pad = 0) => chunk.water.some((w) => Math.hypot((x - w.lx) / (w.rx + pad), (z - w.lz) / (w.rz + pad)) < 1)
      const litterOf = biomeLitter[chunkBiome]
      // Keep the line the squirrel walks in the flat view clear
      const inClearing = inClearingAt
      const spots = [] // every tree's spot, felled or not, so the layout stays put
      const clear = (x, z, gap) =>
        !inClearing(gx + x, gz + z) &&
        !wet(x, z, 20) &&
        spots.every((t) => Math.hypot(t.lx - x, t.lz - z) > gap) &&
        chunk.mushrooms.every((m) => Math.hypot(m.lx - x, m.lz - z) > gap * 0.6)

      // Some biomes have boulders among the trees: solid, stood on, smashed in three
      const rocks = flora.boulders ? Math.floor(random() * (flora.boulders[0] + 1)) : 0
      const treeCount = 2 + Math.floor(random() * 4) + (flora.extraTrees || 0) + rocks
      for (let i = 0, tries = 0; i < treeCount && tries < 40; tries++) {
        const x = random() * CHUNK
        const z = random() * CHUNK
        if (!clear(x, z, 80)) continue
        const built = buildTree(random, i < rocks ? 'boulder' : flora.trees[Math.floor(random() * flora.trees.length)])
        // Own materials in its own leaf colours, so it can fade when it blocks the camera
        const palette = flora.foliage || FOLIAGE
        const leaves = palette[Math.floor(random() * palette.length)]
        const mats = treeMats.map((m, j) => {
          const c = m.clone()
          if (j === 1 || j === 2) c.color.set(leaves[j - 1])
          if ((j === 10 || j === 11) && flora.boulders) c.color.set(flora.boulders[1][j - 10])
          c.transparent = true
          return c
        })
        const rot = Math.floor(random() * 4) * (Math.PI / 2)
        const id = `${cx},${cz},t${i}`
        spots.push({ lx: x, lz: z })
        i++
        const hits = world.felled.get(id) || 0
        if (hits >= TREE_HITS) {
          built.geometry.dispose()
          mats.forEach((m) => m.dispose())
          continue
        }
        if (hits > 0) {
          built.voxels = damageTree(built.voxels, hits).voxels
          built.geometry.dispose()
          Object.assign(built, meshTree(built.voxels, built.offset))
          // Down to a stump: nothing up there to stand on but its top
          if (hits >= 2 || built.kind === 'boulder') built.canopyY = Math.min(built.canopyY, Math.max(0, ...built.tops.values()))
        }
        const mesh = new Mesh(built.geometry, mats)
        mesh.position.set(x, 0, z)
        mesh.rotation.y = rot
        group.add(mesh)
        chunk.trees.push({ ...built, id, hits, chunk, lx: x, lz: z, x: 0, z: 0, rot, mesh, mats, fade: 1 })
      }

      const shroomCount = random() < 0.35 ? 1 : 0 // roughly one every three chunks
      for (let i = 0, tries = 0; i < shroomCount && tries < 30; tries++) {
        const x = random() * CHUNK
        const z = random() * CHUNK
        if (!clear(x, z, 40)) continue
        const geometry = buildMushroom(random)
        const id = `${cx},${cz},${i}`
        i++
        if (world.eaten.has(id)) {
          geometry.dispose()
          continue
        }
        const mesh = new Mesh(geometry, mushroomMats)
        mesh.position.set(x, 0, z)
        group.add(mesh)
        chunk.mushrooms.push({ id, lx: x, lz: z, x: 0, z: 0, mesh, chunk })
      }

      // Flowers, ferns, rocks and the like, all in one mesh
      const decor = new Map()
      flora.plants.forEach(([kind, count, colours]) => {
        for (let i = 0; i < count; i++) {
          const x = random() * CHUNK
          const z = random() * CHUNK
          const colour = DECOR[colours[Math.floor(random() * colours.length)]]
          if (inClearing(gx + x, gz + z) || wet(x, z, 4) || spots.some((t) => Math.hypot(t.lx - x, t.lz - z) < 16)) continue
          stampDecor(decor, kind, Math.round(x / VOXEL), Math.round(z / VOXEL), random, colour)
        }
      })
      if (decor.size) {
        chunk.decor = new Mesh(meshDecor(decor), decorMats)
        group.add(chunk.decor)
      }

      const tufts = Math.round(((CHUNK * CHUNK) / 90) * (flora.grassDensity || 1))
      const grass = new InstancedMesh(grassGeo, grassMat, tufts)
      grass.frustumCulled = false
      const m = new Matrix4()
      for (let i = 0; i < tufts; i++) {
        const sx = 0.5 + random() * 0.5
        const sy = 1.5 + random() * 4
        const sz = 0.5 + random() * 0.5
        const px = random() * CHUNK
        const pz = random() * CHUNK
        // None growing in the water
        if (chunk.water.length && wet(px, pz, 8)) m.makeScale(0.001, 0.001, 0.001)
        else m.makeScale(sx, sy, sz)
        m.setPosition(px, 0, pz)
        grass.setMatrixAt(i, m)
        const pile = random() < 0.15 ? litterOf : grassOf
        grass.setColorAt(i, pile[Math.floor(random() * pile.length)])
      }
      group.add(grass)
      chunk.grass = grass
      woods.add(group)
      return chunk
    }

    const dropChunk = (chunk) => {
      woods.remove(chunk.group)
      chunk.trees.forEach((t) => {
        t.mesh.geometry.dispose()
        t.mats.forEach((m) => m.dispose())
      })
      chunk.mushrooms.forEach((m) => m.mesh.geometry.dispose())
      if (chunk.decor) chunk.decor.geometry.dispose()
      chunk.grass.dispose()
    }

    // World positions of everything solid in the loaded chunks
    const indexChunks = () => {
      trees = []
      waters = []
      mushrooms = []
      chunks.forEach((c) => {
        const { x, z } = c.group.position
        c.trees.forEach((t) => {
          t.x = x + t.lx
          t.z = z + t.lz
          trees.push(t)
        })
        ;(c.water || []).forEach((w) => waters.push({ x: x + w.lx, z: z + w.lz, rx: w.rx, rz: w.rz }))
        c.mushrooms.forEach((mu) => {
          mu.x = x + mu.lx
          mu.z = z + mu.lz
          mushrooms.push(mu)
        })
      })
    }

    // Load chunks near the squirrel (a couple a frame once playing) and fade out far ones
    const updateChunks = (dt) => {
      const s = world.squirrel
      const gx = s.pos.x + origin.x
      const gz = s.pos.z + origin.z
      const grown = chunks.size === 0
      const reach = Math.ceil(LOAD_RADIUS / CHUNK)
      const ccx = Math.floor(gx / CHUNK)
      const ccz = Math.floor(gz / CHUNK)
      const gap = (cx, cz) => Math.hypot((cx + 0.5) * CHUNK - gx, (cz + 0.5) * CHUNK - gz)
      let built = 0
      let changed = false
      for (let dx = -reach; dx <= reach; dx++) {
        for (let dz = -reach; dz <= reach; dz++) {
          const cx = ccx + dx
          const cz = ccz + dz
          const k = `${cx},${cz}`
          if (chunks.has(k) || gap(cx, cz) > LOAD_RADIUS || (!grown && built >= 2)) continue
          chunks.set(k, buildChunk(cx, cz, grown))
          built++
          changed = true
        }
      }
      chunks.forEach((c, k) => {
        const far = gap(c.cx, c.cz) > DROP_RADIUS
        c.grow = clamp(c.grow + (far ? -dt : dt) * 1.5, 0, 1)
        if (far && c.grow === 0) {
          dropChunk(c)
          chunks.delete(k)
          changed = true
        }
      })
      if (changed) indexChunks()
    }

    // Height of a tree's canopy at a world point, or 0 if the point misses it
    const treeTopAt = (t, x, z) => {
      const dx = x - t.x
      const dz = z - t.z
      const cos = Math.cos(t.rot)
      const sin = Math.sin(t.rot)
      const lx = dx * cos - dz * sin
      const lz = dx * sin + dz * cos
      const vx = Math.floor(lx / TREE_VOXEL - t.offset[0])
      const vz = Math.floor(lz / TREE_VOXEL - t.offset[2])
      return t.tops.get(`${vx},${vz}`) || 0
    }

    // What it would stand on at (x, z): the highest treetop no more than a
    // step above its feet, or the forest floor
    const groundAt = (x, z, y) => {
      let best = 0
      let tree = null
      trees.forEach((t) => {
        const reach = t.canopyRadius + TREE_VOXEL * 2
        if (Math.abs(x - t.x) > reach || Math.abs(z - t.z) > reach) return
        const top = treeTopAt(t, x, z)
        if (top > best && top <= y + STEP) {
          best = top
          tree = t
        }
      })
      return { height: best, tree }
    }

    const clearChunks = () => {
      chunks.forEach(dropChunk)
      chunks.clear()
      indexChunks()
    }

    // ----- Sun and moon -----
    // They sweep low across the far horizon, ahead of the camera
    const sunMesh = new Mesh(keep(new BoxGeometry(70, 70, 6)), new MeshBasicMaterial({ color: '#ffd75e', fog: false }))
    const sunGlow = new Mesh(
      keep(new BoxGeometry(110, 110, 2)),
      new MeshBasicMaterial({ color: '#ffe9a8', fog: false, transparent: true, opacity: 0.3 }),
    )
    sunGlow.position.z = -4
    sunMesh.add(sunGlow)
    const moonMesh = new Mesh(keep(new BoxGeometry(50, 50, 6)), new MeshBasicMaterial({ color: '#e9eef7', fog: false }))
    const craterMat = new MeshBasicMaterial({ color: '#c3cad8', fog: false })
    ;[
      [-10, 8, 12],
      [12, -6, 9],
      [4, 14, 6],
    ].forEach(([x, y, size]) => {
      const crater = new Mesh(keep(new BoxGeometry(size, size, 1)), craterMat)
      crater.position.set(x, y, 3.5)
      moonMesh.add(crater)
    })
    woods.add(sunMesh, moonMesh)

    // ----- Critters -----
    // Harmless wildlife kept in a ring around the squirrel: fireflies, beetles,
    // butterflies, bees, dragonflies, birds by day and bats by night, falling
    // leaves, drifting mist and rain splashes. How much of each depends on the
    // biome (WILDLIFE), eased in and out as biomes change.
    const fireflyMat = new MeshBasicMaterial({ color: '#ffffff' })
    const fireflyLit = new Color('#f4ff8a')
    const fireflyDark = new Color('#3a3d24')
    const fireflyGlow = new Color()
    const beetleMat = lambert('#ffffff')
    const butterflyMat = new MeshBasicMaterial({ color: '#ffffff', side: DoubleSide })
    const beeMat = new MeshBasicMaterial({ color: '#ffffff' })
    const dragonflyMat = new MeshBasicMaterial({ color: '#ffffff' })
    const flyerMat = lambert('#ffffff')
    const wingMat = new MeshLambertMaterial({ color: '#ffffff', side: DoubleSide })
    const leafMat = new MeshLambertMaterial({ color: '#ffffff', side: DoubleSide })
    const mistMat = new MeshBasicMaterial({ color: '#e8ecef', transparent: true, opacity: 0, depthWrite: false })
    const splashMat = new MeshBasicMaterial({ color: '#dfe8f2', transparent: true, opacity: 0.7 })
    const swarm = (geometry, material, count) => {
      const mesh = new InstancedMesh(keep(geometry), material, count)
      mesh.frustumCulled = false
      // Colours first: the colour buffer is sized from the count when it's made
      for (let i = 0; i < count; i++) mesh.setColorAt(i, new Color('#ffffff'))
      woods.add(mesh)
      return {
        mesh,
        max: count,
        bugs: Array.from({ length: count }, () => ({ pos: null, phase: rand(0, 100), heading: rand(0, 7) })),
      }
    }
    const fireflies = swarm(new BoxGeometry(1.2, 1.2, 1.2), fireflyMat, 75)
    const beetles = swarm(new BoxGeometry(2.4, 1.2, 1.6).translate(0, 0.6, 0), beetleMat, 40)
    const butterflies = swarm(new PlaneGeometry(3.4, 2.4), butterflyMat, 24)
    const bees = swarm(new BoxGeometry(1, 0.8, 0.8), beeMat, 20)
    const dragonflies = swarm(buildDragonfly(), dragonflyMat, 16)
    const flyers = swarm(new BoxGeometry(2.8, 1.4, 1.4), flyerMat, 24)
    // Two wings per flyer, hinged at the body and reaching out sideways
    const wings = swarm(new PlaneGeometry(2.4, 3.6).rotateX(-Math.PI / 2).translate(0, 0, 2.2), wingMat, 48)
    const leaves = swarm(new PlaneGeometry(2.2, 2.2), leafMat, 90)
    const mist = swarm(new BoxGeometry(46, 10, 46), mistMat, 26)
    const splashes = swarm(new BoxGeometry(2.4, 0.6, 2.4), splashMat, 60)
    const beetleColours = ['#2b2420', '#3a3a2a', '#1f2a1f', '#b8322a'].map((c) => new Color(c))
    const beeColours = ['#f5c400', '#2a2216'].map((c) => new Color(c))
    const dragonflyColours = ['#2fd0ff', '#3aff8a', '#ff4a6a', '#4a6aff'].map((c) => new Color(c))
    const batColour = new Color('#2a2430')
    fireflies.bugs.forEach((b, i) => fireflies.mesh.setColorAt(i, fireflyLit))
    beetles.bugs.forEach((b, i) => beetles.mesh.setColorAt(i, beetleColours[i % beetleColours.length]))
    bees.bugs.forEach((b, i) => bees.mesh.setColorAt(i, beeColours[i % 2]))
    dragonflies.bugs.forEach((b, i) => dragonflies.mesh.setColorAt(i, dragonflyColours[i % dragonflyColours.length]))
    const wildColours = {}
    BIOME_KINDS.forEach((k) => {
      const w = WILDLIFE[k]
      wildColours[k] = {
        birds: w.birdColours.map((c) => new Color(c)),
        wings: w.wingColours.map((c) => new Color(c)),
        leaves: w.leafColours.map((c) => new Color(c)),
      }
    })
    // How much of each kind is about, eased towards the biome's amounts
    const wild = { ...WILDLIFE.meadow }
    const WILD_KEYS = ['fireflies', 'beetles', 'butterflies', 'bees', 'dragonflies', 'birds', 'bats', 'leaves', 'mist', 'splashes']

    const bugMatrix = new Matrix4()
    const bugQuat = new Quaternion()
    const bugEuler = new Euler(0, 0, 0, 'YXZ')
    const bugScale = new Vector3()
    const unit = new Vector3(1, 1, 1)
    let clock = 0

    const updateCritters = (dt, day) => {
      clock += dt
      const s = world.squirrel
      const { e } = view
      const here = world.staged ? biome : 'meadow'
      const colours = wildColours[here]
      const settle = 1 - Math.exp(-0.6 * dt)
      const wetNow = wetness()
      WILD_KEYS.forEach((k) => {
        let want = WILDLIFE[here][k]
        if (wetNow !== 'clear') {
          if (k === 'splashes') want = Math.max(want, WILDLIFE[wetNow].splashes)
          if (k === 'butterflies' || k === 'bees') want *= 0.15
          if (k === 'leaves' && wetNow === 'storm') want = Math.max(want, 1.2)
        }
        wild[k] = lerp(wild[k], want, settle)
      })
      const pick = (list) => list[Math.floor(Math.random() * list.length)]
      // Puts a critter somewhere new near the squirrel once it's left behind;
      // says whether it moved, so it can be recoloured for the biome
      const keepNear = (b, height, reach = 380) => {
        if (b.pos && flat(b.pos.clone().sub(s.pos)) < reach) return false
        const a = rand(0, Math.PI * 2)
        const r = b.pos ? rand(reach * 0.55, reach * 0.95) : rand(30, reach * 0.95)
        b.pos = new Vector3(s.pos.x + Math.cos(a) * r, height, s.pos.z + Math.sin(a) * r)
        return true
      }
      const place = (swarmOf, i, b, scale, pitch = 0, roll = 0) => {
        bugEuler.set(pitch, b.heading, roll)
        bugQuat.setFromEuler(bugEuler)
        bugScale.copy(scale)
        bugMatrix.compose(b.pos, bugQuat, bugScale)
        swarmOf.mesh.setMatrixAt(i, bugMatrix)
      }
      const show = (swarmOf, amount, base) => {
        swarmOf.mesh.count = Math.min(swarmOf.max, Math.round(base * amount))
        swarmOf.mesh.visible = swarmOf.mesh.count > 0 && view.p > 0.01
        return swarmOf.mesh.count
      }
      const done = (swarmOf) => {
        swarmOf.mesh.instanceMatrix.needsUpdate = true
        if (swarmOf.mesh.instanceColor) swarmOf.mesh.instanceColor.needsUpdate = true
      }
      const night = 1 - day

      // Fireflies drift and blink after dark
      const fireflyCount = show(fireflies, wild.fireflies * (night > 0.02 ? 1 : 0), 50)
      for (let i = 0; i < fireflyCount; i++) {
        const b = fireflies.bugs[i]
        keepNear(b, rand(4, 40))
        b.pos.x += Math.sin(clock * 0.7 + b.phase) * 10 * dt
        b.pos.z += Math.cos(clock * 0.6 + b.phase * 1.3) * 10 * dt
        b.pos.y = clamp(b.pos.y + Math.sin(clock * 1.1 + b.phase) * 6 * dt, 3, 45)
        // Same size throughout; they blink and fade in brightness instead
        const lit = Math.sin(clock * 2.3 + b.phase * 7) > 0.2 ? 1 : 0.15
        place(fireflies, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, 0.6 * e)))
        fireflies.mesh.setColorAt(i, fireflyGlow.copy(fireflyDark).lerp(fireflyLit, lit * night))
      }
      done(fireflies)

      // Beetles amble about on the ground, now and then changing course
      const beetleCount = show(beetles, wild.beetles, 30)
      for (let i = 0; i < beetleCount; i++) {
        const b = beetles.bugs[i]
        keepNear(b, 0)
        if (Math.random() < dt * 0.5) b.heading += rand(-1.5, 1.5)
        b.pos.x += Math.cos(b.heading) * 9 * dt
        b.pos.z -= Math.sin(b.heading) * 9 * dt
        place(beetles, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)))
      }
      done(beetles)

      // Butterflies flap lazy loops by day, in the biome's colours
      const butterflyCount = show(butterflies, wild.butterflies * (day > 0.02 ? 1 : 0), 14)
      for (let i = 0; i < butterflyCount; i++) {
        const b = butterflies.bugs[i]
        if (keepNear(b, 12) || b.biome !== here) {
          b.biome = here
          butterflies.mesh.setColorAt(i, pick(colours.wings))
        }
        b.heading += Math.sin(clock * 0.5 + b.phase) * dt
        b.pos.x += Math.cos(b.heading) * 22 * dt
        b.pos.z -= Math.sin(b.heading) * 22 * dt
        b.pos.y = 10 + Math.sin(clock * 1.3 + b.phase) * 6
        const flap = 0.2 + 0.8 * Math.abs(Math.sin(clock * 18 + b.phase))
        const size = Math.max(0.001, day * e)
        place(butterflies, i, b, bugScale.set(flap * size, size, size), -Math.PI / 2)
      }
      done(butterflies)

      // Bees buzz in jittery loops close to the flowers by day
      const beeCount = show(bees, wild.bees * (day > 0.3 ? 1 : 0), 20)
      for (let i = 0; i < beeCount; i++) {
        const b = bees.bugs[i]
        if (keepNear(b, 4, 260)) b.home = b.pos.clone()
        const t = clock * 3 + b.phase
        b.pos.set(b.home.x + Math.sin(t * 1.7) * 8, 4 + Math.abs(Math.sin(t * 2.3)) * 6, b.home.z + Math.cos(t * 1.3) * 8)
        b.heading = t * 1.7
        place(bees, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)))
      }
      done(bees)

      // Dragonflies hover, then dart off somewhere close by
      const dragonflyCount = show(dragonflies, wild.dragonflies * (day > 0.2 ? 1 : 0), 12)
      for (let i = 0; i < dragonflyCount; i++) {
        const b = dragonflies.bugs[i]
        if (keepNear(b, rand(6, 18), 300) || !b.to) b.to = b.pos.clone()
        if (b.pos.distanceTo(b.to) < 1 && Math.random() < dt * 1.2) {
          b.to = b.pos.clone().add(new Vector3(rand(-40, 40), rand(-4, 4), rand(-40, 40)))
          b.to.y = clamp(b.to.y, 5, 22)
        }
        const step = b.to.clone().sub(b.pos)
        const gap = step.length()
        if (gap > 0.5) {
          b.heading = Math.atan2(-step.z, step.x)
          b.pos.addScaledVector(step, Math.min(1, (90 * dt) / gap))
        }
        b.pos.y += Math.sin(clock * 9 + b.phase) * 0.05
        place(dragonflies, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)))
      }
      done(dragonflies)

      // Birds wheel high overhead by day; bats flit low and erratic by night
      const bats = night > 0.5
      const flyerCount = show(flyers, bats ? wild.bats : wild.birds, 14)
      wings.mesh.count = flyerCount * 2
      wings.mesh.visible = flyers.mesh.visible
      for (let i = 0; i < flyerCount; i++) {
        const b = flyers.bugs[i]
        if (!b.centre || flat(b.centre.clone().sub(s.pos)) > 520 || b.bat !== bats || b.biome !== here) {
          b.bat = bats
          b.biome = here
          // Out ahead of the camera, where they're in view against the sky
          b.centre = new Vector3(s.pos.x + rand(-320, 320), 0, s.pos.z - rand(bats ? 60 : 180, bats ? 260 : 560))
          b.radius = bats ? rand(30, 90) : rand(60, 180)
          b.height = bats ? rand(20, 50) : rand(45, 110)
          b.speed = (bats ? rand(1.4, 2.4) : rand(0.35, 0.7)) * (Math.random() < 0.5 ? 1 : -1)
          b.angle = rand(0, Math.PI * 2)
          const colour = bats ? batColour : pick(colours.birds)
          flyers.mesh.setColorAt(i, colour)
          wings.mesh.setColorAt(i * 2, colour)
          wings.mesh.setColorAt(i * 2 + 1, colour)
        }
        b.angle += b.speed * dt
        const wobble = bats ? Math.sin(clock * 5 + b.phase) * 12 : Math.sin(clock * 0.8 + b.phase) * 6
        b.pos = b.pos || new Vector3()
        b.pos.set(
          b.centre.x + Math.cos(b.angle) * (b.radius + wobble),
          b.height + Math.sin(clock * 1.3 + b.phase) * 4,
          b.centre.z + Math.sin(b.angle) * (b.radius + wobble),
        )
        // Facing along the circle it flies
        const dir = Math.sign(b.speed)
        b.heading = Math.atan2(-Math.cos(b.angle) * dir, -Math.sin(b.angle) * dir)
        const size = Math.max(0.001, e)
        place(flyers, i, b, bugScale.copy(unit).multiplyScalar(size))
        // Birds flap, then glide; bats never stop flapping
        const beat = bats ? Math.sin(clock * 22 + b.phase) : Math.sin(clock * 9 + b.phase) * (Math.sin(clock * 0.7 + b.phase) > 0 ? 1 : 0.15)
        const flap = beat * 0.7
        bugEuler.set(flap, b.heading, 0)
        bugQuat.setFromEuler(bugEuler)
        bugMatrix.compose(b.pos, bugQuat, bugScale.copy(unit).multiplyScalar(size))
        wings.mesh.setMatrixAt(i * 2, bugMatrix)
        bugEuler.set(flap, b.heading + Math.PI, 0)
        bugQuat.setFromEuler(bugEuler)
        bugMatrix.compose(b.pos, bugQuat, bugScale)
        wings.mesh.setMatrixAt(i * 2 + 1, bugMatrix)
      }
      done(flyers)
      done(wings)

      // Leaves flutter down, blown sideways in a storm
      const leafCount = show(leaves, wild.leaves, 50)
      const wind = here === 'storm' ? 70 : 8
      for (let i = 0; i < leafCount; i++) {
        const b = leaves.bugs[i]
        if (keepNear(b, rand(40, 140), 320) || b.pos.y <= 0.5 || b.biome !== here) {
          if (b.pos.y <= 0.5) b.pos.y = rand(60, 140)
          b.biome = here
          leaves.mesh.setColorAt(i, pick(colours.leaves))
        }
        const fall = here === 'storm' ? 34 : 14
        b.pos.y = Math.max(0, b.pos.y - fall * dt)
        b.pos.x += (Math.sin(clock * 1.6 + b.phase) * 12 + wind) * dt
        b.pos.z += Math.cos(clock * 1.2 + b.phase) * 6 * dt
        b.heading += dt * 2
        place(leaves, i, b, bugScale.copy(unit).multiplyScalar(Math.max(0.001, e)), Math.sin(clock * 3 + b.phase) * 1.2, Math.cos(clock * 2 + b.phase))
      }
      done(leaves)

      // Low banks of mist drifting through
      const mistCount = show(mist, wild.mist > 0.02 ? 1 : 0, 26)
      mistMat.opacity = clamp(wild.mist, 0, 1) * 0.16 * view.p
      for (let i = 0; i < mistCount; i++) {
        const b = mist.bugs[i]
        keepNear(b, rand(3, 10), 420)
        b.pos.x += Math.sin(clock * 0.1 + b.phase) * 6 * dt
        b.pos.z += Math.cos(clock * 0.08 + b.phase) * 4 * dt
        place(mist, i, b, bugScale.set(1 + Math.sin(b.phase) * 0.4, 1, 1 + Math.cos(b.phase) * 0.4).multiplyScalar(Math.max(0.001, e)))
      }
      done(mist)

      // Raindrops splashing up off the ground
      const splashCount = show(splashes, world.paused ? 0 : wild.splashes, 36)
      for (let i = 0; i < splashCount; i++) {
        const b = splashes.bugs[i]
        b.life = (b.life || 0) - dt
        if (b.life <= 0 || !b.pos) {
          b.life = rand(0.15, 0.35)
          b.max = b.life
          b.pos = new Vector3(s.pos.x + rand(-260, 260), 0.3, s.pos.z + rand(-320, 120))
        }
        const t = 1 - b.life / b.max
        place(splashes, i, b, bugScale.set(0.4 + t, 1 - t * 0.8, 0.4 + t).multiplyScalar(Math.max(0.001, e)))
      }
      done(splashes)
    }

    // ----- Poison bubbles -----
    const bubbleGeo = keep(new BoxGeometry(1.6, 1.6, 1.6))
    const bubbleMat = new MeshBasicMaterial({ color: '#7dff6a', transparent: true, opacity: 0.75 })
    const bubbles = []
    let bubbleIn = 0
    const POISON_GREEN = new Color('#5dff4a')

    const updateBubbles = (dt) => {
      const s = world.squirrel
      bubbleIn -= dt
      if (s.poisonFor > 0 && bubbleIn <= 0) {
        bubbleIn = 0.07
        let b = bubbles.find((x) => x.life <= 0)
        if (!b) {
          b = { mesh: new Mesh(bubbleGeo, bubbleMat), life: 0 }
          woods.add(b.mesh)
          bubbles.push(b)
        }
        b.life = rand(0.6, 1)
        b.mesh.position.set(s.pos.x + rand(-10, 10), s.pos.y + rand(6, 16), s.pos.z + rand(-6, 6))
        b.rise = rand(18, 34)
      }
      bubbles.forEach((b) => {
        b.life -= dt
        b.mesh.visible = b.life > 0
        if (b.life <= 0) return
        b.mesh.position.y += b.rise * dt
        b.mesh.position.x += Math.sin(b.life * 12) * 6 * dt
        b.mesh.scale.setScalar(Math.max(0.001, Math.min(1, b.life * 2) * view.e))
      })
    }

    // ----- Hearts -----
    const heartGeo = keep(buildHeartGeometry())
    const heartMat = new MeshLambertMaterial({ color: '#e23b4a', emissive: '#6a1018' })
    let hearts = []
    let dropIn = 12
    let heartIn = 4

    const clearHearts = () => {
      hearts.forEach((h) => woods.remove(h.mesh))
      hearts = []
    }

    // Meshes for acorns and crumbs, created as needed for the shared world state
    const acornMeshes = new Map()
    const meshFor = (a) => {
      let m = acornMeshes.get(a)
      if (!m) {
        const kind = a.kind || 'acorn'
        const geometry = nutGeos[kind] || acornGeo
        m = { body: new Mesh(geometry, a.super ? superMats : nutMats[kind] || acornMats), shadow: shadowMesh() }
        scene.add(m.body, m.shadow)
        acornMeshes.set(a, m)
      }
      return m
    }
    const crumbMeshes = []

    // ----- Layout -----
    const resize = () => {
      renderer.setPixelRatio(window.devicePixelRatio || 1)
      renderer.setSize(window.innerWidth, window.innerHeight, false)
      bounds.x0 = nav.offsetLeft
      bounds.x1 = nav.offsetLeft + nav.offsetWidth
    }

    // Derive nut mode progress from how far the nav has travelled, so the
    // camera stays in step with the page's CSS transition
    const measure = () => {
      const rect = nav.getBoundingClientRect()
      const inset = nav.offsetLeft
      view.restGround = nav.offsetTop
      const zoomGround = view.restGround * STAGE_SCALE + stageShift()
      view.p = clamp((rect.top - view.restGround) / (zoomGround - view.restGround), 0, 1)
      view.e = ease(view.p)
      view.ground = rect.top

      const counterKey = `${inset}|${rect.top}|${view.p}`
      if (counterKey !== lastCounter) {
        lastCounter = counterKey
        counter.style.left = `${inset}px`
        counter.style.height = `${Math.min(rect.top, view.restGround + 40 * view.p)}px`
        counter.style.fontSize = `${COUNTER_SIZE * (1 + view.p)}px`
      }
    }

    // A dolly zoom from a far, narrow side-on view (world x = screen x, y = 0
    // on the nav's top edge) to a chase camera behind the squirrel. The frame
    // shrinks from the whole window to the strip above the nav as it goes.
    const follow = new Vector3()
    const updateCamera = (dt) => {
      const W = window.innerWidth
      const H = window.innerHeight
      const { e, ground } = view
      const s = world.squirrel
      const aim = new Vector3(s.pos.x, 14 + s.pos.y * 0.7, s.pos.z - 30)
      if (e === 0) follow.copy(aim)
      else follow.lerp(aim, Math.min(1, dt * 6))

      const target = new Vector3(W / 2, 0, 0).lerp(follow, e)
      const fov = Math.exp(lerp(Math.log(FLAT_FOV), Math.log(GAME_FOV), e))
      const half = Math.tan(((fov / 2) * Math.PI) / 180)
      const gameView = 2 * GAME_DISTANCE * Math.tan(((GAME_FOV / 2) * Math.PI) / 180)
      const visible = Math.min(ground, H)
      const frameH = Math.max(1, lerp(H, visible, e))
      const distance = lerp(H, gameView, e) / (2 * half)
      const pitch = GAME_PITCH * e
      camera.position.set(
        target.x,
        target.y + Math.sin(pitch) * distance,
        target.z + Math.cos(pitch) * distance,
      )
      camera.lookAt(target)
      camera.fov = fov
      camera.aspect = W / frameH
      camera.near = Math.max(0.5, distance - 2000)
      camera.far = distance + 4000
      camera.setViewOffset(W, frameH, 0, frameH / 2 - lerp(visible, visible / 2, e), W, H)
      camera.updateProjectionMatrix()

      scene.fog.near = distance + lerp(1e5, 280 * atmo.fog, e)
      scene.fog.far = scene.fog.near + lerp(1e5, 600 * atmo.fog, e)
    }

    // ----- Staging -----
    const showPrompt = (show) => {
      clearTimeout(promptTimer)
      prompt.classList.remove('centered')
      prompt.classList.toggle('show', show)
      if (show) promptTimer = setTimeout(() => prompt.classList.remove('show'), PROMPT_TIME)
    }
    const showToast = (text) => {
      clearTimeout(toastTimer)
      showPrompt(false)
      toast.textContent = text
      toast.classList.add('show')
      toastTimer = setTimeout(() => toast.classList.remove('show'), TOAST_TIME)
    }

    const setStaged = (staged) => {
      const s = world.squirrel
      if (staged && !world.staged) {
        showPrompt(true)
        // A fresh run each time; it finishes what it was doing before handing over
        world.score = 0
        world.lives = maxLives()
        showScore()
        // Grow the woods afresh around wherever it stands, in the meadow
        origin.x = 0
        origin.z = 0
        world.bonusLives = 0
        if (s) {
          s.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
          s.shield = false
          s.streak = 0
        }
        world.eaten = new Set()
        world.felled = new Map()
        world.harvested = new Set()
        startRun('meadow')
        clearChunks()
        clearHearts()
        if (s) s.introFor = INTRO_TIME
        lockPointer()
      }
      if (!staged && world.staged) {
        showPrompt(false)
        if (s) s.superFor = 0
        clearBoss()
        clearPickups()
        world.paused = false
        pauseButton.classList.remove('paused')
        showOverlay(null)
        toast.classList.remove('show')
        keys.clear()
        releasePointer()
        if (s) {
          if (s.deadFor > 0) respawn(s, false)
          s.climb = null
          recentre()
        }
        clearHearts()
      }
      world.staged = staged
      if (stage) {
        stage.style.transform = staged ? `translateY(${stageShift()}px) scale(${STAGE_SCALE})` : ''
        stage.style.opacity = staged ? STAGE_OPACITY : ''
      }
      counter.classList.toggle('show', staged)
      exitBar.classList.toggle('show', staged)
      soundButton.classList.toggle('show', staged)
      pauseButton.classList.toggle('show', staged)
      levelButton.classList.toggle('show', staged)
      infoButton.classList.toggle('show', staged)
      fullButton.classList.toggle('show', staged)
      document.documentElement.classList.toggle('nut-mode', staged)
      updateHover()
    }

    // Over the strip above the nav: a dot cursor and, out of nut mode, the invitation
    function updateHover() {
      const overStrip = mouse.y >= 0 && mouse.y < view.ground
      if (cursorEl) cursorEl.classList.toggle('in-sky', world.staged || overStrip)
      hint.classList.toggle('show', !world.staged && overStrip && !touchOnly)
    }

    readColours()
    resize()
    measure()

    if (!world.squirrel) {
      world.squirrel = {
        pos: new Vector3(bounds.x0 + (bounds.x1 - bounds.x0) * 0.3, 0, -SQ_D / 2),
        vel: new Vector3(),
        heading: 0,
        phase: 0,
        target: null,
        introFor: 0,
        eatFor: 0,
        wanderTo: null,
        idleFor: 1,
        deadFor: 0,
        safeFor: 0,
        attackFor: 0,
        attackCd: 0,
        tilt: 0,
        climb: null,
      }
    }
    follow.set(world.squirrel.pos.x, 14, world.squirrel.pos.z - 30)
    if (touchOnly) world.staged = false
    setStaged(world.staged)

    // ----- Acorns -----
    const spawnAcorn = (pos, isSuper = false) => {
      world.acorns.push({
        kind: world.staged ? FLORA[biome].nut : 'acorn',
        pos,
        vel: new Vector3(rand(-30, 30), -rand(20, 60), rand(-30, 30)),
        quat: new Quaternion(),
        super: isSuper,
      })
    }
    // Whether a point is in the camera's view (with a margin)
    const onScreen = (x, y, z) => {
      const at = new Vector3(x, y, z).project(camera)
      return at.z < 1 && Math.abs(at.x) < 1.15 && Math.abs(at.y) < 1.15
    }
    // Flat view: from the sky just above the screen. Nut mode: from a tree.
    const dropAcorn = () => {
      const s = world.squirrel
      if (view.p > 0.5) {
        // Each tree grows just the one acorn, and only where it can't be
        // seen appearing: off screen, or far off in the haze
        const near = trees.filter((t) => {
          if (t.hits >= 2 || t.kind === 'boulder' || world.harvested.has(t.id)) return false
          const d = Math.hypot(t.x - s.pos.x, t.z - s.pos.z)
          return d < LOAD_RADIUS - 40 && (d > 640 || !onScreen(t.x, t.canopyY, t.z))
        })
        const tree = near[Math.floor(Math.random() * near.length)]
        if (!tree) return
        world.harvested.add(tree.id)
        // Ripens on top of the crown, on the side facing the camera so it's
        // in view; most drop after a while, some stay put
        const cos = Math.cos(tree.rot)
        const sin = Math.sin(tree.rot)
        const toCamera = new Vector3(camera.position.x - tree.x, 0, camera.position.z - tree.z).normalize()
        const spots = [...tree.tops.entries()].map(([column, top]) => {
          const [vx, vz] = column.split(',').map(Number)
          const lx = (vx + 0.5 + tree.offset[0]) * TREE_VOXEL
          const lz = (vz + 0.5 + tree.offset[2]) * TREE_VOXEL
          const x = cos * lx + sin * lz
          const z = -sin * lx + cos * lz
          return { x: tree.x + x, z: tree.z + z, top, front: x * toCamera.x + z * toCamera.z }
        })
        spots.sort((a, b) => b.front - a.front)
        const spot = spots[Math.floor(Math.random() * Math.max(1, Math.ceil(spots.length * 0.25)))]
        spawnAcorn(new Vector3(spot.x, spot.top + AC_R, spot.z), Math.random() < SUPER_CHANCE)
        const acorn = world.acorns[world.acorns.length - 1]
        acorn.vel.set(0, 0, 0)
        acorn.hang = tree
        acorn.hangHits = tree.hits
        acorn.fallIn = Math.random() < HANGING_STAY ? Infinity : rand(1.5, 5)
      } else {
        spawnAcorn(
          new Vector3(
            rand(bounds.x0 + AC_R, bounds.x1 - AC_R),
            view.ground + AC_R * 2,
            clamp(s.pos.z + rand(-40, 40), -DEPTH + AC_R, -AC_R),
          ),
        )
      }
    }

    // Push a circle out of tree trunks, optionally bouncing its velocity;
    // returns the last trunk it touched
    const dodgeTrunks = (pos, radius, vel, bounce) => {
      let touched = null
      trees.forEach((t) => {
        if (pos.y >= t.canopyY) return
        const dx = pos.x - t.x
        const dz = pos.z - t.z
        const d = Math.hypot(dx, dz)
        const min = t.trunkRadius + radius
        if (d >= min || d === 0) return
        pos.x = t.x + (dx / d) * min
        pos.z = t.z + (dz / d) * min
        if (vel) {
          const n = new Vector3(dx / d, 0, dz / d)
          const into = vel.dot(n)
          if (into < 0) vel.addScaledVector(n, -into * (1 + bounce))
        }
        touched = t
      })
      return touched
    }

    const spin = new Vector3()
    const turn = new Quaternion()
    const updateAcorns = (dt) => {
      const xMin = bounds.x0 + AC_R
      const xMax = bounds.x1 - AC_R
      const zMin = -DEPTH + AC_R
      const zMax = -AC_R
      // The flat view keeps acorns along the nav; the woods have no edges
      const fenced = !world.staged
      if (!fenced) {
        const s = world.squirrel
        world.acorns = world.acorns.filter((a) => flat(a.pos.clone().sub(s.pos)) < LOAD_RADIUS)
      }
      world.acorns.forEach((a) => {
        if (a.magnet) {
          a.pos.addScaledVector(a.vel, dt)
          return
        }
        if (a.hang) {
          // Only starts to loosen once the squirrel is close
          if (flat(a.pos.clone().sub(world.squirrel.pos)) < 300) a.fallIn -= dt
          if (a.fallIn > 0 && trees.includes(a.hang) && a.hang.hits === a.hangHits) return
          a.hang = null
          a.vel.set(rand(-20, 20), 0, rand(-20, 20))
        }
        a.vel.y -= GRAVITY * dt
        a.pos.addScaledVector(a.vel, dt)
        if (!fenced) {
          // No walls
        } else if (a.pos.x < xMin || a.pos.x > xMax) {
          a.pos.x = clamp(a.pos.x, xMin, xMax)
          a.vel.x *= -0.6
        }
        if (fenced && (a.pos.z < zMin || a.pos.z > zMax)) {
          a.pos.z = clamp(a.pos.z, zMin, zMax)
          a.vel.z *= -0.6
        }
        if (view.p > 0.5 && a.pos.y < 60) dodgeTrunks(a.pos, AC_R, a.vel, 0.6)
        if (a.pos.y <= AC_R) {
          a.pos.y = AC_R
          if (a.vel.y < -80) {
            // Lumpy acorns kick off sideways when they hit the ground
            const impact = -a.vel.y
            a.vel.y = impact * 0.4
            a.vel.x += rand(-1, 1) * impact * 0.2
            a.vel.z += rand(-1, 1) * impact * 0.2
          } else {
            a.vel.y = 0
            const friction = Math.exp(-1.5 * dt)
            a.vel.x *= friction
            a.vel.z *= friction
            if (flat(a.vel) < 4) {
              a.vel.x = 0
              a.vel.z = 0
            }
          }
        }
        // Roll: spin about the axis perpendicular to travel
        spin.set(a.vel.z, 0, -a.vel.x).divideScalar(AC_R)
        const angle = spin.length() * dt
        if (angle > 0) {
          turn.setFromAxisAngle(spin.normalize(), angle)
          a.quat.premultiply(turn)
        }
      })

      // Nudge overlapping acorns apart
      for (let i = 0; i < world.acorns.length; i++) {
        for (let j = i + 1; j < world.acorns.length; j++) {
          const a = world.acorns[i]
          const b = world.acorns[j]
          if (a.hang || b.hang) continue
          const d = b.pos.clone().sub(a.pos)
          const dist = d.length()
          if (dist > 0 && dist < AC_R * 2) {
            d.divideScalar(dist)
            const push = (AC_R * 2 - dist) / 2
            a.pos.addScaledVector(d, -push)
            b.pos.addScaledVector(d, push)
            const closing = b.vel.clone().sub(a.vel).dot(d)
            if (closing < 0) {
              a.vel.addScaledVector(d, closing * 0.9)
              b.vel.addScaledVector(d, -closing * 0.9)
            }
          }
        }
      }
    }

    const updateCrumbs = (dt) => {
      world.crumbs = world.crumbs.filter((c) => (c.life -= dt) > 0)
      world.crumbs.forEach((c) => {
        c.vel.y -= GRAVITY * dt
        c.pos.addScaledVector(c.vel, dt)
        c.pos.y = Math.max(VOXEL / 2, c.pos.y)
      })
    }

    // ----- Squirrel -----
    const facing = (s) => new Vector3(Math.cos(s.heading), 0, -Math.sin(s.heading))
    const mouthOf = (s) => s.pos.clone().addScaledVector(facing(s), MOUTH)

    // Only nuts eaten under the player's control score
    const eat = (s, acorn, scores) => {
      world.acorns = world.acorns.filter((a) => a !== acorn)
      if (s.target === acorn) s.target = null
      s.eatFor = 0.9
      s.vel.x = 0
      s.vel.z = 0
      if (scores) {
        // Eaten in quick succession, nuts build a streak worth double or triple
        s.streak = clock - (s.lastEat === undefined ? -99 : s.lastEat) < COMBO_TIME ? (s.streak || 0) + 1 : 1
        s.lastEat = clock
        const mult = comboMultiplier(s.streak)
        world.score += mult
        if (world.score > best) {
          best = world.score
          saveBest(world.difficulty, best)
        }
        showScore()
        popup(`+${mult}`, acorn.super ? 'super' : 'nut')
        showCombo()
        if (!acorn.super) sound.play('eat')
      }
      if (acorn.super && world.staged) {
        s.superFor = SUPER_TIME
        s.poisonFor = 0
        showToast('Super nut!')
        sound.play('super')
      }
      const mouth = mouthOf(s)
      for (let i = 0; i < 6; i++) {
        world.crumbs.push({
          pos: new Vector3(mouth.x, SQ_H - 8, mouth.z),
          vel: new Vector3(rand(-60, 60), rand(40, 140), rand(-60, 60)),
          life: rand(0.4, 0.7),
        })
      }
    }

    // A spider bite or a poison mushroom costs a life where it stands: a bite
    // leaves it flashing and briefly untouchable, poison glows green for a
    // while. Losing the last life ends the game; it flops over and waits.
    const hurt = (s, kind) => {
      if (s.superFor > 0) return
      // A shield acorn makes it untouchable while it lasts
      if (s.powers && s.powers.shield > 0) {
        s.safeFor = Math.max(s.safeFor, 0.6)
        if (clock - (s.blockedAt || -9) > 0.6) {
          popup('Blocked', 'power')
          sound.play('clank')
        }
        s.blockedAt = clock
        return
      }
      s.streak = 0
      showCombo()
      world.lives -= 1
      showScore()
      popup('-1', 'life')
      sound.play(kind === 'poison' ? 'poison' : 'hurt')
      if (kind === 'poison') s.poisonFor = POISON_TIME
      s.safeFor = kind === 'poison' ? POISON_TIME : 1.5
      if (world.lives > 0) return
      s.deadFor = 1.6
      s.climb = null
      s.vel.set(0, 160, 0)
      s.target = null
      s.gameOver = true
    }

    // The mushroom is gone once eaten, for the rest of the run
    const eatMushroom = (s, shroom) => {
      world.eaten.add(shroom.id)
      shroom.chunk.group.remove(shroom.mesh)
      shroom.mesh.geometry.dispose()
      shroom.chunk.mushrooms = shroom.chunk.mushrooms.filter((m) => m !== shroom)
      indexChunks()
      if (s.superFor > 0) sound.play('munch')
      else hurt(s, 'poison')
    }

    const newGame = () => {
      world.seed = Math.floor(Math.random() * 1e9)
      world.bonusLives = 0
      clearBoss()
      bossIn = rand(120, 200)
      clearPickups()
      const sq = world.squirrel
      if (sq) {
        sq.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
        sq.shield = false
        sq.streak = 0
      }
      const others = BIOME_KINDS.filter((k) => k !== world.startBiome)
      startRun(others[Math.floor(Math.random() * others.length)])
      world.eaten = new Set()
      world.felled = new Map()
      world.harvested = new Set()
      world.acorns = []
      world.crumbs = []
      world.score = 0
      world.lives = maxLives()
      clearChunks()
      clearHearts()
      updateChunks(0)
      spiders.forEach((sp) => {
        sp.alive = false
        sp.deadFor = 0
        sp.respawnIn = rand(1, 4)
      })
      showScore()
    }

    // Back on its feet where it fell, clear of mushrooms and briefly untouchable
    function respawn(s, fresh = true) {
      if (s.gameOver) {
        s.gameOver = false
        newGame()
      }
      s.deadFor = 0
      s.tilt = 0
      s.poisonFor = 0
      s.vel.set(0, 0, 0)
      s.pos.y = 0
      if (fresh) {
        const nasty = () => mushrooms.some((m) => Math.hypot(m.x - s.pos.x, m.z - s.pos.z) < 40)
        for (let i = 0; i < 20 && nasty(); i++) s.pos.z += 30
        s.safeFor = 2
      }
    }

    // Shift the whole world so the squirrel lands back on the nav for the flat
    // view. Everything moves together, camera included, so nothing jumps.
    function recentre() {
      const s = world.squirrel
      const dx = clamp(s.pos.x, xMinSq(), xMaxSq()) - s.pos.x
      const dz = -SQ_D / 2 - s.pos.z
      const shift = (v) => {
        v.x += dx
        v.z += dz
      }
      shift(s.pos)
      shift(follow)
      world.acorns.forEach((a) => shift(a.pos))
      world.crumbs.forEach((c) => shift(c.pos))
      spiders.forEach((sp) => shift(sp.pos))
      chunks.forEach((c) => shift(c.group.position))
      origin.x -= dx
      origin.z -= dz
      indexChunks()
      s.wanderTo = null
      s.target = null
      // Acorns left out in the woods don't come back to the nav, and none stay up trees
      world.acorns = world.acorns.filter((a) => a.pos.x > bounds.x0 && a.pos.x < bounds.x1 && (a.kind || 'acorn') === 'acorn')
      world.acorns.forEach((a) => {
        a.hang = null
      })
    }

    const xMinSq = () => bounds.x0 + SQ_W / 2 + 2
    const xMaxSq = () => bounds.x1 - SQ_W / 2 - 2
    const zMinSq = -DEPTH + SQ_D / 2
    const zMaxSq = -SQ_D / 2

    // Finds its own way to acorns, or wanders when there are none
    const autopilot = (s, dt, desired, is3D) => {
      const reachable = world.acorns.filter((a) => !a.hang)
      if (!s.target && reachable.length) {
        const gap = (a) => (is3D ? flat(a.pos.clone().sub(s.pos)) : Math.abs(a.pos.x - s.pos.x))
        s.target = reachable.reduce((best, a) => (gap(a) < gap(best) ? a : best))
      }
      if (s.target) {
        const t = s.target.pos
        const onFloor = t.y <= AC_R + 1
        const grounded = s.pos.y === 0
        if (is3D) {
          const to = t.clone().sub(s.pos).setY(0)
          const reach = to.length() - MOUTH
          if (reach < 4 && onFloor && grounded) eat(s, s.target, false)
          else if (reach >= 4) desired.copy(to.normalize()).multiplyScalar(Math.min(RUN_SPEED, reach * 8))
          else s.heading = Math.atan2(-to.z, to.x)
        } else {
          // Flat view: depth doesn't show, so line the mouth up in x only
          const dx = t.x - (s.pos.x + Math.sign(Math.cos(s.heading) || 1) * MOUTH)
          if (Math.abs(dx) < 4 && onFloor && grounded) eat(s, s.target, false)
          else if (Math.abs(dx) >= 4) desired.x = Math.sign(dx) * Math.min(RUN_SPEED, Math.abs(dx) * 8)
        }
        return
      }
      s.idleFor -= dt
      if (!s.wanderTo && s.idleFor <= 0) {
        s.wanderTo = is3D
          ? new Vector3(s.pos.x + rand(-200, 200), 0, s.pos.z + rand(-200, 200))
          : new Vector3(rand(xMinSq(), xMaxSq()), 0, s.pos.z)
      }
      if (s.wanderTo) {
        const to = s.wanderTo.clone().sub(s.pos).setY(0)
        if (!is3D) to.z = 0
        if (to.length() < 4) {
          s.wanderTo = null
          s.idleFor = rand(1.5, 4)
        } else {
          desired.copy(to.normalize()).multiplyScalar(RUN_SPEED * 0.5)
        }
      }
    }

    const updateSquirrel = (dt) => {
      const s = world.squirrel
      const is3D = view.p > 0.5
      const playing = world.staged && is3D && s.introFor <= 0 && !touchOnly
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0

      s.introFor = Math.max(0, s.introFor - dt)
      s.safeFor = Math.max(0, s.safeFor - dt)
      s.poisonFor = Math.max(0, (s.poisonFor || 0) - dt)
      s.superFor = Math.max(0, (s.superFor || 0) - dt)
      s.attackCd = Math.max(0, s.attackCd - dt)
      s.attackFor = Math.max(0, s.attackFor - dt)
      if (s.target && !world.acorns.includes(s.target)) s.target = null

      if (s.deadFor > 0) {
        // Flopped over; wait to come back, or after the last life, for a click
        s.deadFor -= dt
        s.tilt = Math.min(Math.PI / 2, s.tilt + dt * 8)
        s.vel.x = 0
        s.vel.z = 0
        if (s.deadFor <= 0) {
          if (s.gameOver) {
            s.deadFor = 0.001
            if (overlay.dataset.kind !== 'over') {
              showOverlay('over')
              sound.play('over')
            }
          } else {
            respawn(s)
          }
        }
      }

      const input = new Vector3()
      keys.forEach((k) => {
        input.x += KEYS[k][0]
        input.z += KEYS[k][1]
      })

      if (s.climb && (!playing || s.deadFor > 0 || !trees.includes(s.climb))) s.climb = null
      if (s.climb) {
        climb(s, dt, input)
        return
      }

      const desired = new Vector3()
      if (s.deadFor > 0) {
        // Nothing to do while dead
      } else if (playing) {
        if (input.lengthSq() > 0) {
          // Groggy while the poison works
          // Wading through water is slow going
          const wading = s.pos.y < 1 && waters.some((w) => Math.hypot((s.pos.x - w.x) / w.rx, (s.pos.z - w.z) / w.rz) < 1)
          const pace = (sprinting ? SPRINT_SPEED : PLAYER_SPEED) * (s.powers && s.powers.pepper > 0 ? 1.6 : 1) * (wading ? 0.55 : 1)
          desired.copy(input.normalize()).multiplyScalar(pace * (s.poisonFor > 0 ? 0.6 : 1))
          s.eatFor = 0
        } else if (s.eatFor > 0) {
          s.eatFor -= dt
        }
        s.target = null
        s.wanderTo = null
      } else if (s.eatFor > 0) {
        s.eatFor -= dt
      } else {
        autopilot(s, dt, desired, is3D)
      }

      // Ease toward desired speed. Mid-pounce the keys don't steer: it flies
      // its arc, skids to a stop on landing, then walks on as before.
      if (s.attackFor > 0) {
        if (s.grounded) {
          const skid = Math.exp(-12 * dt)
          s.vel.x *= skid
          s.vel.z *= skid
        }
      } else {
        // Puddles in the rain are slippery: slow to get going, slow to stop
        const accel = (desired.lengthSq() === 0 ? 1200 : 900) * dt * (s.grounded && onPuddle(s) ? 0.08 : 1)
        const change = desired.sub(new Vector3(s.vel.x, 0, s.vel.z))
        if (change.length() > accel) change.setLength(accel)
        s.vel.x += change.x
        s.vel.z += change.z
      }

      // Turn to face travel; the flat view only ever faces left or right
      const speed = flat(s.vel)
      let aim = s.heading
      if (s.deadFor > 0) aim = s.heading
      else if (s.attackFor > 0) {
        // Mid-pounce the keys only turn it on the spot; the arc carries it
        if (playing && input.lengthSq() > 0) aim = Math.atan2(-input.z, input.x)
      }
      else if (speed > 5) aim = is3D ? Math.atan2(-s.vel.z, s.vel.x) : s.vel.x >= 0 ? 0 : Math.PI
      else if (!is3D) aim = Math.cos(s.heading) >= 0 ? 0 : Math.PI
      const diff = ((aim - s.heading + Math.PI * 3) % (Math.PI * 2)) - Math.PI
      s.heading += clamp(diff, -14 * dt, 14 * dt)

      s.pos.x += s.vel.x * dt
      s.pos.z += s.vel.z * dt
      // The flat view keeps it on the nav; the woods have no edges, except
      // a boss's bramble ring
      if (!world.staged) {
        s.pos.x = clamp(s.pos.x, xMinSq(), xMaxSq())
        s.pos.z = clamp(s.pos.z, zMinSq, zMaxSq)
      } else {
        holdInArena(s)
      }
      if (is3D) {
        const trunk = dodgeTrunks(s.pos, SQ_D, null)
        // A super squirrel flattens any tree it runs into
        if (trunk && playing && s.superFor > 0 && s.deadFor <= 0) {
          trunk.hits = TREE_HITS - 1
          hitTree(trunk)
        } else if (trunk && trunk.hits < 2 && trunk.kind !== 'boulder' && playing && s.pos.y === 0 && s.deadFor <= 0 && input.lengthSq() > 0) {
          const toward = new Vector3(trunk.x - s.pos.x, 0, trunk.z - s.pos.z).normalize()
          if (input.clone().normalize().dot(toward) > 0.5) s.climb = trunk
        }
      }

      // Jumps, falls and death hops, onto the floor or a treetop
      const under = is3D && world.staged ? groundAt(s.pos.x, s.pos.z, s.pos.y) : { height: 0, tree: null }
      // Holding Space on the way down spreads its arms and legs into a glide
      s.gliding = playing && spaceHeld && !s.climb && s.deadFor <= 0 && s.vel.y < 0 && s.pos.y > under.height + 2
      if (s.vel.y !== 0 || s.pos.y > under.height) {
        if (s.gliding) s.vel.y = Math.max(s.vel.y - GRAVITY * 0.2 * dt, -GLIDE_FALL)
        else s.vel.y -= GRAVITY * dt
        s.pos.y += s.vel.y * dt
        if (s.pos.y <= under.height && s.vel.y <= 0) {
          s.pos.y = under.height
          s.vel.y = 0
        }
      } else {
        s.pos.y = under.height
      }
      s.grounded = s.vel.y === 0 && s.pos.y === under.height
      s.perch = s.grounded ? under.tree : null
      if (s.grounded) {
        s.doubled = false
        s.gliding = false
      }

      if (s.deadFor <= 0 && is3D) {
        // Eats any acorn it runs over
        const mouth = mouthOf(s)
        const snack = world.acorns.find((a) =>
          a.hang
            ? Math.abs(a.pos.y - AC_R - s.pos.y) < 10 &&
              (flat(a.pos.clone().sub(mouth)) < 10 || flat(a.pos.clone().sub(s.pos)) < 13)
            : a.pos.y <= AC_R + 4 &&
              s.pos.y < 6 &&
              (flat(a.pos.clone().sub(mouth)) < 9 || flat(a.pos.clone().sub(s.pos)) < 12),
        )
        if (snack) eat(s, snack, playing)

        if (hazards && s.pos.y < 6) {
          const shroom = mushrooms.find((m) => Math.hypot(m.x - s.pos.x, m.z - s.pos.z) < 10)
          if (shroom) eatMushroom(s, shroom)
        }
      }

      s.phase += dt * (speed / 12 + (s.eatFor > 0 ? 10 : 0))
    }

    // Hugging the trunk: W climbs, S climbs down, A, D or Space leaps off
    const climb = (s, dt, input) => {
      const t = s.climb
      const away = new Vector3(s.pos.x - t.x, 0, s.pos.z - t.z)
      if (away.lengthSq() === 0) away.set(0, 0, 1)
      away.normalize()
      if (input.x !== 0) {
        letGo(s)
        return
      }
      s.pos.y = clamp(s.pos.y - input.z * CLIMB_SPEED * dt, 0, t.canopyY)
      if (s.pos.y === t.canopyY && input.z < 0) {
        // Up through the leaves and onto the top
        s.climb = null
        s.pos.set(t.x, treeTopAt(t, t.x, t.z), t.z)
        s.vel.set(0, 0, 0)
        return
      }
      s.pos.x = t.x + away.x * (t.trunkRadius + 1)
      s.pos.z = t.z + away.z * (t.trunkRadius + 1)
      s.heading = Math.atan2(away.z, -away.x)
      s.vel.set(0, 0, 0)
      s.phase += dt * Math.abs(input.z) * 10
      if (s.pos.y === 0 && input.z > 0) s.climb = null
    }
    const letGo = (s) => {
      const t = s.climb
      s.climb = null
      const away = new Vector3(s.pos.x - t.x, 0, s.pos.z - t.z).normalize()
      s.vel.set(away.x * 140, 180, away.z * 140)
      s.heading = Math.atan2(-away.z, away.x)
    }

    // A short hop forward that lands claws-first
    const attack = () => {
      const s = world.squirrel
      if (s.deadFor > 0 || s.attackCd > 0 || s.climb || !s.grounded) return
      s.attackFor = POUNCE_TIME
      s.attackCd = POUNCE_COOLDOWN
      s.eatFor = 0
      s.struck = new Set()
      sound.play('pounce')
      const f = facing(s)
      s.vel.set(f.x * 110, 190, f.z * 110)
    }

    // ----- Spiders -----
    // Somewhere in the woods around the squirrel, not on top of it
    const placeSpider = (sp) => {
      const s = world.squirrel
      // On the harder levels many turn up in the squirrel's path. Either way
      // they come from out of sight: off screen, or far off in the haze.
      const ahead = flat(s.vel) > 30 && Math.random() < level().ahead
      for (let i = 0; i < 10; i++) {
        const a = ahead ? Math.atan2(s.vel.z, s.vel.x) + rand(-0.7, 0.7) : rand(0, Math.PI * 2)
        const r = rand(480, 820)
        sp.pos.set(s.pos.x + Math.cos(a) * r, 0, s.pos.z + Math.sin(a) * r)
        if (r > 660 || !onScreen(sp.pos.x, 8, sp.pos.z)) break
      }
      sp.hp = sp.cfg.hp + level().hp
      sp.knock.set(0, 0, 0)
      sp.windup = 0
      sp.leapFor = 0
      sp.vy = 0
      sp.pounceCd = 1
      sp.flashFor = 0
      sp.alive = true
      sp.deadFor = 0
      sp.wanderTo = null
      // By day, some are found asleep
      sp.asleep = daylight > 0.5 && Math.random() < level().sleep
      sp.alertFor = 0
    }

    let daylight = 1
    let dayRoll = Math.random()
    let wasNight = false
    const updateSpiders = (dt, night) => {
      const s = world.squirrel
      const active = view.p > 0.5
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0
      const isNight = night > 0.5
      daylight = 1 - night
      if (wasNight && !isNight) dayRoll = Math.random()
      wasNight = isNight
      // Only the current biome's creatures roam; others slink off
      const enemies = [].concat(BIOMES[biome].enemy)
      const want = (sp) => {
        if (!enemies.includes(sp.kind)) return 0
        if (boss && boss.phase === 'fight') return 0
        const count = isNight ? sp.cfg.night : sp.cfg.day + (dayRoll < 0.25 ? 1 : 0)
        return Math.max(1, Math.round((count * level().creatures) / enemies.length))
      }
      spiders.forEach((sp) => {
        if (sp.boss) return
        const i = sp.index
        const wanted = want(sp)
        const cfg = sp.cfg
        sp.flashFor = Math.max(0, sp.flashFor - dt)
        sp.chargedFor = Math.max(0, (sp.chargedFor || 0) - dt)
        sp.shotCd = Math.max(0, (sp.shotCd === undefined ? rand(1, 3) : sp.shotCd) - dt)
        if (!sp.alive) {
          if (sp.deadFor > 0) {
            sp.deadFor = Math.max(0, sp.deadFor - dt)
            return
          }
          if (i >= wanted || !active) return
          sp.respawnIn -= dt / level().respawn
          if (sp.respawnIn <= 0) placeSpider(sp)
          return
        }
        if (!active) return
        const toSquirrel = s.pos.clone().sub(sp.pos).setY(0)
        const dist = toSquirrel.length()
        if (dist > SPIDER_LEASH) {
          placeSpider(sp)
          return
        }
        // Come daybreak the extra night spiders slink off and vanish into the fog
        const leaving = i >= wanted
        sp.leaving = leaving
        if (leaving && dist > 320 && (dist > 660 || !onScreen(sp.pos.x, 8, sp.pos.z))) {
          sp.alive = false
          sp.respawnIn = rand(2, 6)
          return
        }
        let dir
        let speed = cfg.wander
        sp.pounceCd = Math.max(0, sp.pounceCd - dt)
        const target = toSquirrel.clone().normalize()
        if (sp.asleep) {
          // Creeping past is fine; running close, a pounce nearby, a hit or
          // nightfall wakes it
          const pace = flat(s.vel)
          const stir = pace > PLAYER_SPEED + 20 ? 110 : pace > 30 ? 40 : 16
          if (isNight || sp.hp < cfg.hp + level().hp || (hazards && (dist < stir || (s.attackFor > 0 && dist < 70)))) {
            sp.asleep = false
            sp.alertFor = 0.6
            if (dist < 300) sound.play('alert')
          }
        }
        if (sp.knock.lengthSq() > 1) {
          // Reeling from a bite
          sp.pos.addScaledVector(sp.knock, dt)
          sp.knock.multiplyScalar(Math.exp(-7 * dt))
          sp.leapFor = 0
          sp.windup = 0
          speed = 0
          dir = target
        } else if (sp.asleep || sp.alertFor > 0) {
          // Dozing, or just woken and getting its bearings
          speed = 0
          dir = sp.asleep ? new Vector3(Math.cos(sp.heading), 0, -Math.sin(sp.heading)) : target
          sp.alertFor = Math.max(0, sp.alertFor - dt)
        } else if (sp.leapFor > 0) {
          // Mid-pounce: only a pounce that lands on the squirrel bites
          sp.leapFor -= dt
          sp.pos.addScaledVector(sp.leap, dt)
          speed = 0
          dir = sp.leap.clone().normalize()
          const reach = sp.pos.distanceTo(s.pos.clone().setY(s.pos.y + SQ_H / 2))
          if (!sp.bit && hazards && s.deadFor <= 0 && s.safeFor <= 0 && reach < 18) {
            sp.bit = true
            hurt(s, 'bite')
          }
        } else if (sp.windup > 0) {
          // Crouching to spring
          sp.windup -= dt
          speed = 0
          dir = target
          if (sp.windup <= 0) {
            // Aimed to come down right where the squirrel is, not sail over it
            const airTime = (2 * cfg.lift) / GRAVITY
            // Harder creatures aim ahead of a squirrel on the move
            const aim = s.pos.clone().addScaledVector(s.vel, airTime * level().lead).sub(sp.pos).setY(0)
            const span = aim.length()
            sp.leap = aim.normalize().multiplyScalar(Math.min(cfg.leap * (1 + level().lead * 0.4), span / airTime))
            sp.vy = cfg.lift
            sp.leapFor = cfg.leapTime
            sp.bit = false
          }
        } else if (leaving) {
          dir = target.clone().negate()
          speed = cfg.wander * 1.5
        } else if (s.superFor > 0 && dist < cfg.sight * 1.5) {
          // Scattering from a super squirrel; caught ones are done for
          dir = target.clone().negate()
          speed = cfg.chase * 1.2
          if (dist < 16) {
            sp.alive = false
            sp.deadFor = SPIDER_DEATH
            sp.respawnIn = rand(8, 14)
            return
          }
        } else if (hazards && s.deadFor <= 0 && dist < cfg.sight * level().sight * 1.3) {
          dir = target
          speed = cfg.chase
          const reach = cfg.reach * level().reach
          // In the wet they spit from range; charged ones spark, and more often
          const charged = sp.chargedFor > 0
          const shoots = charged || ((wetness() !== 'clear' || level().spitDry) && sp.kind !== 'wolf')
          if (shoots && sp.shotCd <= 0 && shotsIn <= 0 && dist > reach && s.pos.y < 40) {
            shoot(sp, charged)
            sp.shotCd = (charged ? rand(2.5, 4) : rand(4, 7)) * level().shots
            shotsIn = 1.8 * level().shots // and never two in quick succession from the pack
          }
          if (dist > cfg.sight * level().sight) {
            // Only close enough to shoot from; keep closing in
          } else if (dist < reach && sp.pounceCd <= 0 && s.pos.y < 12) {
            sp.windup = cfg.windup * level().attack
            sp.pounceCd = cfg.cooldown * level().attack
          } else if (dist < reach * 0.6) {
            // Circles in close rather than walking into it
            speed = 0
          }
        } else {
          if (!sp.wanderTo || flat(sp.wanderTo.clone().sub(sp.pos)) < 6) {
            sp.wanderTo = new Vector3(sp.pos.x + rand(-150, 150), 0, sp.pos.z + rand(-150, 150))
          }
          dir = sp.wanderTo.clone().sub(sp.pos).setY(0).normalize()
        }
        // Charged creatures are quicker, but a running squirrel still gets away
        const boost = sp.chargedFor > 0 ? 1.25 : 1
        sp.pos.addScaledVector(dir, Math.min(speed * level().speed * boost, SPRINT_SPEED - 15) * dt)
        if (sp.pos.y > 0 || sp.vy > 0) {
          sp.vy -= GRAVITY * dt
          sp.pos.y = Math.max(0, sp.pos.y + sp.vy * dt)
          if (sp.pos.y === 0) {
            sp.vy = 0
            sp.leapFor = 0
          }
        }
        dodgeTrunks(sp.pos, 8, null)
        sp.heading = Math.atan2(-dir.z, dir.x)
        sp.phase += dt * Math.max(speed, sp.leapFor > 0 ? 120 : 0) * 0.25
        sp.moving = speed > 0

        // A pouncing squirrel bites first: once per pounce, two bites to kill
        if (s.attackFor > 0 && !s.struck.has(sp) && flat(mouthOf(s).sub(sp.pos)) < 22) {
          s.struck.add(sp)
          sp.hp -= 1
          sp.flashFor = 0.25
          sp.knock.copy(toSquirrel).setY(0).normalize().multiplyScalar(-260)
          sound.play(sp.hp <= 0 ? 'kill' : 'hit')
          if (sp.hp <= 0) {
            // A charged creature leaves a nut behind
            if (sp.chargedFor > 0) spawnAcorn(sp.pos.clone().setY(AC_R + 6), Math.random() < 0.25)
            sp.alive = false
            sp.deadFor = SPIDER_DEATH
            sp.respawnIn = rand(8, 14)
            sp.chargedFor = 0
          }
        }
      })
    }

    // Spit, webs and sparks: lobbed at where the squirrel is, dodged by moving
    const SHOT_LOOK = {
      spider: '#f2f2f2', // web
      frog: '#7dff6a', // spit
      snake: '#c45cff', // venom
      croc: '#6ac8ff', // a spray of water
      scorpion: '#d8ff5a', // venom
      iguana: '#9aff7a',
      wolf: '#9ff4ff',
    }
    const shotGeo = keep(new BoxGeometry(3, 3, 3))
    const shotMats = {}
    Object.keys(SHOT_LOOK).forEach((k) => {
      shotMats[k] = new MeshBasicMaterial({ color: SHOT_LOOK[k] })
    })
    const sparkMat = new MeshBasicMaterial({ color: '#bff8ff' })
    let shots = []
    let shotsIn = 0
    const shoot = (sp, charged) => {
      const s = world.squirrel
      const from = sp.pos.clone().setY(sp.pos.y + 8)
      // Not dead-on: a stray shot now and then
      const to = s.pos.clone().setY(s.pos.y + SQ_H / 2).add(new Vector3(rand(-14, 14), 0, rand(-14, 14)))
      // Harder creatures lob it where the squirrel is heading
      to.addScaledVector(new Vector3(s.vel.x, 0, s.vel.z), (from.distanceTo(to) / SHOT_SPEED) * level().lead)
      const time = Math.max(0.35, from.distanceTo(to) / SHOT_SPEED)
      const lift = 260 // a gentle arc
      const vel = to.clone().sub(from).divideScalar(time)
      vel.y += (lift * time) / 2
      const mesh = new Mesh(shotGeo, charged ? sparkMat : shotMats[sp.kind])
      woods.add(mesh)
      shots.push({ pos: from, vel, lift, mesh, life: time + 1, spark: charged })
      sound.play('spit')
    }
    const updateShots = (dt) => {
      const s = world.squirrel
      shotsIn -= dt
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0
      shots = shots.filter((shot) => {
        shot.life -= dt
        shot.vel.y -= shot.lift * dt
        shot.pos.addScaledVector(shot.vel, dt)
        const hit = shot.pos.distanceTo(s.pos.clone().setY(s.pos.y + SQ_H / 2)) < 9 + 3 * (shot.size || 1)
        if (hit && hazards && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
        if (hit || shot.pos.y < 0 || shot.life <= 0 || !world.staged) {
          woods.remove(shot.mesh)
          return false
        }
        shot.mesh.position.copy(shot.pos)
        const wobble = shot.spark ? 0.6 + Math.random() * 0.8 : 1
        shot.mesh.scale.setScalar(Math.max(0.001, view.e * wobble * (shot.size || 1)))
        shot.mesh.rotation.set(shot.life * 7, shot.life * 5, 0)
        return true
      })
    }

    // ----- Sleepy creatures -----
    // By day some creatures doze where they are. Creep past quietly; running
    // close by, pouncing near them or hitting them wakes them up.
    const zGeo = keep(buildSleepZ())
    const zMat = new MeshBasicMaterial({ color: '#f2f6ff', transparent: true })
    const sleepZs = new InstancedMesh(zGeo, zMat, 40)
    sleepZs.frustumCulled = false
    sleepZs.count = 0
    woods.add(sleepZs)
    const updateSleepZs = () => {
      let n = 0
      spiders.forEach((sp) => {
        if (!sp.alive || !sp.asleep || n >= 40) return
        // Each sleeper puffs out a "z" that drifts up and fades, again and again
        const t = (clock * 0.5 + sp.phase) % 1
        bugEuler.set(0, 0.6, 0.2)
        bugQuat.setFromEuler(bugEuler)
        bugScale.setScalar(Math.max(0.001, (0.6 + t * 0.6) * (1 - t) * 1.4 * view.e))
        rainAt.set(sp.pos.x + t * 6, sp.pos.y + 14 + t * 14, sp.pos.z)
        bugMatrix.compose(rainAt, bugQuat, bugScale)
        sleepZs.setMatrixAt(n++, bugMatrix)
      })
      sleepZs.count = n
      sleepZs.instanceMatrix.needsUpdate = true
    }

    // ----- Power-ups -----
    // Every so often a power-up turns up a little way ahead
    const POWERS = {
      magnet: { name: 'Golden nut', time: 12 },
      pepper: { name: 'Pepper', time: 5 },
      feather: { name: 'Feather', time: 20 },
      shield: { name: 'Shield acorn', time: 10 },
    }
    const POWER_KINDS = Object.keys(POWERS)
    const powerGeos = {
      magnet: acornGeo,
      pepper: keep(buildPepper()),
      feather: keep(buildFeather()),
      shield: acornGeo,
    }
    const powerMats = {
      magnet: [0, 1, 2].map(() => new MeshLambertMaterial({ color: '#ffd23f', emissive: '#a07000' })),
      pepper: [new MeshLambertMaterial({ color: '#e8251f', emissive: '#5a0a08' }), lambert('#3fa03a')],
      feather: [new MeshLambertMaterial({ color: '#ffffff', emissive: '#555555' }), lambert('#3a7bff')],
      shield: [0, 1, 2].map(() => new MeshLambertMaterial({ color: '#6ac8ff', emissive: '#1a4a7a' })),
    }
    // A soft bubble around the squirrel while it has a shield
    const bubble = new Mesh(
      keep(new BoxGeometry(SQ_W * 1.25, SQ_H * 1.3, SQ_D * 2.2)),
      new MeshBasicMaterial({ color: '#8fd6ff', transparent: true, opacity: 0.25, depthWrite: false }),
    )
    bubble.visible = false
    woods.add(bubble)
    let pickups = []
    let powerIn = rand(18, 30)
    const clearPickups = () => {
      pickups.forEach((pk) => woods.remove(pk.mesh))
      pickups = []
    }
    const updatePowers = (dt) => {
      const s = world.squirrel
      world.acorns.forEach((a) => {
        a.magnet = false
      })
      const playing = world.staged && !world.paused && view.p > 0.5
      if (!s.powers) s.powers = { magnet: 0, pepper: 0, feather: 0, shield: 0 }
      Object.keys(s.powers).forEach((k) => {
        s.powers[k] = Math.max(0, s.powers[k] - dt)
      })
      if (playing && s.introFor <= 0) {
        // Low on lives, they turn up more often (barely more on Insane), often as a shield
        const low = world.lives <= Math.max(1, Math.floor(level().lives / 2))
        powerIn -= dt * (1 + (low ? level().lowPowers : 0))
        if (powerIn <= 0 && pickups.length < 2) {
          powerIn = rand(22, 38)
          const kind = low && Math.random() < 0.5 ? 'shield' : POWER_KINDS[Math.floor(Math.random() * POWER_KINDS.length)]
          const mesh = new Mesh(powerGeos[kind], powerMats[kind])
          woods.add(mesh)
          const fighting = boss && boss.phase === 'fight'
          const pos = fighting ? nearSquirrel(140) : new Vector3(s.pos.x + rand(-140, 140), 0, s.pos.z - rand(120, 300))
          pickups.push({ kind, mesh, pos, phase: rand(0, 6) })
        }
      }
      pickups = pickups.filter((pk) => {
        const centre = s.pos.clone().setY(s.pos.y + SQ_H / 2)
        const at = pk.pos.clone().setY(10 + Math.sin(clock * 3 + pk.phase) * 3)
        if (playing && s.deadFor <= 0 && at.distanceTo(centre) < 18) {
          s.powers[pk.kind] = POWERS[pk.kind].time
          popup(POWERS[pk.kind].name, 'power')
          sound.play('power')
          woods.remove(pk.mesh)
          return false
        }
        if (!world.staged || flat(pk.pos.clone().sub(s.pos)) > 800) {
          woods.remove(pk.mesh)
          return false
        }
        pk.mesh.position.copy(at)
        pk.mesh.rotation.set(0, clock * 2 + pk.phase, 0)
        pk.mesh.scale.setScalar(Math.max(0.001, view.e * (pk.kind === 'magnet' || pk.kind === 'shield' ? 1.6 : 1.3) * (1 + Math.sin(clock * 6) * 0.08)))
        return true
      })

      // Golden nut: nuts nearby are drawn in to the squirrel
      if (s.powers.magnet > 0 && playing) {
        world.acorns.forEach((a) => {
          const to = s.pos.clone().setY(s.pos.y + AC_R).sub(a.pos)
          const d = to.length()
          if (d > 220) return
          a.hang = null
          a.magnet = true
          a.vel.copy(to.setLength(Math.min(260, d * 6)))
          if (d < 12 && s.deadFor <= 0) eat(s, a, true)
        })
      }
      // Pepper: bowl creatures over just by running into them
      if (s.powers.pepper > 0 && playing) {
        spiders.forEach((sp) => {
          if (!sp.alive || sp.boss || (sp.pepperedFor || 0) > 0) return
          if (flat(sp.pos.clone().sub(s.pos)) > 22 || Math.abs(sp.pos.y - s.pos.y) > 16) return
          sp.pepperedFor = 0.6
          sp.asleep = false
          sp.hp -= 1
          sp.flashFor = 0.25
          sp.knock.copy(sp.pos.clone().sub(s.pos).setY(0).setLength(320))
          sound.play(sp.hp <= 0 ? 'kill' : 'hit')
          if (sp.hp <= 0) {
            sp.alive = false
            sp.deadFor = SPIDER_DEATH
            sp.respawnIn = rand(8, 14)
          }
        })
      }
      spiders.forEach((sp) => {
        sp.pepperedFor = Math.max(0, (sp.pepperedFor || 0) - dt)
      })
      // The bubble flickers as the shield runs out
      const shieldLeft = s.powers.shield || 0
      bubble.visible = shieldLeft > 0 && world.staged && view.p > 0.5 && (shieldLeft > 2 || Math.sin(clock * 20) > 0)
      if (bubble.visible) {
        bubble.position.set(s.pos.x, s.pos.y + SQ_H / 2, s.pos.z)
        bubble.rotation.y = s.heading
        bubble.material.opacity = 0.18 + Math.sin(clock * 5) * 0.06
      }
      showPowers()
    }
    // Badges for what's active, after the hearts
    const showPowers = () => {
      const s = world.squirrel
      const active = []
      if (s.powers) {
        Object.keys(s.powers).forEach((k) => {
          if (s.powers[k] > 0) active.push(`${POWERS[k].name} ${Math.ceil(s.powers[k])}`)
        })
      }
      const text = active.join(' · ')
      if (powersEl.textContent !== text) powersEl.textContent = text
    }

    // ----- Storm hazards -----
    // A warning ring flickers on the ground, then lightning strikes there
    const warnMat = new MeshBasicMaterial({ color: '#ff3b3b', transparent: true, opacity: 0.8, side: DoubleSide, depthWrite: false })
    const warnRing = new Mesh(keep(new RingGeometry(18, 24, 32).rotateX(-Math.PI / 2)), warnMat)
    const warnFill = new Mesh(keep(new CircleGeometry(18, 32).rotateX(-Math.PI / 2)), new MeshBasicMaterial({ color: '#ff3b3b', transparent: true, opacity: 0.2, depthWrite: false }))
    warnRing.visible = false
    warnFill.visible = false
    woods.add(warnRing, warnFill)
    const STRIKE_RADIUS = 24
    let strikeIn = rand(8, 14)
    let warnFor = 0
    const strikeAt = new Vector3()
    const updateStrikes = (dt) => {
      const s = world.squirrel
      const on = world.staged && !world.paused && view.p > 0.5 && atmo.storm > 0.5 && s.introFor <= 0
      if (warnFor > 0) {
        warnFor -= dt
        const blink = Math.sin(clock * 30) > 0
        warnRing.visible = blink
        warnFill.visible = true
        warnFill.material.opacity = 0.15 + (1 - warnFor / 1.3) * 0.3
        if (warnFor <= 0) {
          // Strike: a bolt straight down onto the spot
          warnRing.visible = false
          warnFill.visible = false
          flash = 1
          const points = []
          let x = strikeAt.x
          for (let y = 600; y >= 0; y -= 60) {
            points.push(x, y, strikeAt.z)
            x = strikeAt.x + rand(-14, 14)
          }
          points.push(strikeAt.x, 0, strikeAt.z)
          bolt.geometry.setAttribute('position', new Float32BufferAttribute(points, 3))
          sound.play('thunder', 0.02)
          if (flat(s.pos.clone().sub(strikeAt)) < STRIKE_RADIUS && s.pos.y < 30 && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
          spiders.forEach((sp) => {
            if (sp.alive && flat(sp.pos.clone().sub(strikeAt)) < STRIKE_RADIUS * 1.5) sp.chargedFor = CHARGED_TIME
          })
        }
        return
      }
      if (!on) return
      strikeIn -= dt
      if (strikeIn > 0) return
      strikeIn = rand(8, 15)
      warnFor = 1.3
      // Near enough that standing still is a gamble
      strikeAt.set(s.pos.x + rand(-26, 26), 0.4, s.pos.z + rand(-26, 26))
      warnRing.position.copy(strikeAt)
      warnFill.position.copy(strikeAt)
      sound.play('warn')
    }

    // Rain: slippery puddles on the ground
    const puddleMat = new MeshLambertMaterial({ color: '#7d98b2', emissive: '#22303e', transparent: true, opacity: 0, depthWrite: false })
    const puddles = Array.from({ length: 16 }, () => {
      const mesh = new Mesh(keep(new CircleGeometry(1, 20).rotateX(-Math.PI / 2)), puddleMat)
      mesh.visible = false
      woods.add(mesh)
      return { mesh, pos: null, r: 0 }
    })
    let puddleAmount = 0
    const updatePuddles = (dt) => {
      const s = world.squirrel
      const wetNow = wetness()
      const want = wetNow === 'storm' ? 1 : wetNow === 'rain' ? 0.75 : 0
      puddleAmount = lerp(puddleAmount, want, 1 - Math.exp(-0.5 * dt))
      puddleMat.opacity = 0.55 * puddleAmount * view.p
      const count = Math.round(puddles.length * Math.min(1, puddleAmount * 1.4))
      puddles.forEach((pd, i) => {
        pd.mesh.visible = i < count && view.p > 0.5
        if (!pd.mesh.visible) return
        if (!pd.pos || flat(pd.pos.clone().sub(s.pos)) > 360) {
          const a = rand(0, Math.PI * 2)
          const r = pd.pos ? rand(200, 340) : rand(30, 340)
          pd.pos = new Vector3(s.pos.x + Math.cos(a) * r, 0.35, s.pos.z + Math.sin(a) * r)
          pd.r = rand(14, 30)
        }
        pd.mesh.position.copy(pd.pos)
        pd.mesh.scale.set(pd.r * view.e, 1, pd.r * 0.75 * view.e)
      })
    }
    const onPuddle = (s) =>
      s.pos.y < 1 &&
      puddles.some((pd) => pd.mesh.visible && pd.pos && Math.hypot((s.pos.x - pd.pos.x) / pd.r, (s.pos.z - pd.pos.z) / (pd.r * 0.75)) < 1)

    // ----- Bosses -----
    // Now and then a giant of the biome's creature waits up ahead. Come close
    // and a ring of brambles closes in: no leaving until it's beaten. It can't
    // be hurt while it's on the prowl; dodge its big attack and pounce while
    // it's dazed. Three hits does it. Each kind has its own big attack.
    const BOSS_RADIUS = 210
    const BOSS_HITS = 3
    const BOSS_SIZE = 3
    const BOSS_TITLES = {
      spider: ['Broodmother', 'Silkfang', 'the Weaver'],
      wolf: ['Alpha', 'Greymaw', 'the Howler'],
      frog: ['Bog King', 'Croaker', 'Old Bulge'],
      snake: ['Coilqueen', 'the Hisser', 'Stormfang'],
      croc: ['Old Snapper', 'Mudjaw', 'the Mire King'],
      scorpion: ['Stingtail', 'the Sand King', 'Deathclaw'],
      iguana: ['the Basker', 'Old Crest', 'Scaleback'],
    }
    const BOSS_NAMES = ['Grim', 'Thorn', 'Gloom', 'Mossback', 'Rook', 'Ash', 'Bramble', 'Hollow', 'Iron', 'Mad', 'Gnarl', 'Rot']
    const BOSS_EPITHETS = ['Terror', 'Bane', 'Warden', 'Tyrant', 'Scourge', 'Dread']
    const BOSS_PLACES = { meadow: 'Meadow', fog: 'Mist', rain: 'Mire', storm: 'Storm', jungle: 'Canopy', desert: 'Dunes', tropical: 'Shore' }
    // Each kind's big attack, after which it's dazed:
    //  leap: the frog bounds onto the squirrel and lands with a shockwave
    //  charge: the wolf charges again and again, dazed when the last one hits the brambles
    //  webs: the spider fires fans of webs
    //  venom: the snake sprays rings of venom outward; jump them or slip through a gap
    //  spikes: the scorpion stabs the ground and spikes burst up where the ground flashes
    //  rockfall: the iguana stomps and rocks drop where their shadows flash
    //  ambush: the croc dives under, a ripple follows the squirrel, then it bursts up in a geyser
    const BOSS_MOVES = { frog: 'leap', wolf: 'charge', spider: 'webs', snake: 'venom', scorpion: 'spikes', iguana: 'rockfall', croc: 'ambush' }
    const pickOne = (list) => list[Math.floor(Math.random() * list.length)]
    const bossName = (kind) =>
      `${pickOne(BOSS_NAMES)} ${pickOne(BOSS_TITLES[kind])}, ${pickOne(BOSS_EPITHETS)} of the ${BOSS_PLACES[biome]}`

    // The bramble ring
    const WALLS = 56
    const wallMat = lambert('#4a3a24')
    const walls = new InstancedMesh(keep(new BoxGeometry(12, 1, 12).translate(0, 0.5, 0)), wallMat, WALLS)
    walls.frustumCulled = false
    walls.visible = false
    woods.add(walls)
    const thornMat = lambert('#2f5a26')
    const thorns = new InstancedMesh(keep(new BoxGeometry(5, 5, 5)), thornMat, WALLS * 2)
    thorns.frustumCulled = false
    thorns.visible = false
    woods.add(thorns)
    // Shockwave and dizzy stars
    const quakeMat = new MeshBasicMaterial({ color: '#fff2c4', transparent: true, opacity: 0.8, side: DoubleSide, depthWrite: false })
    const quake = new Mesh(keep(new RingGeometry(0.9, 1, 48).rotateX(-Math.PI / 2)), quakeMat)
    quake.visible = false
    woods.add(quake)
    const starMeshes = [0, 1, 2].map(() => {
      const m = new Mesh(keep(new BoxGeometry(3, 3, 3)), new MeshBasicMaterial({ color: '#ffe14a' }))
      m.visible = false
      woods.add(m)
      return m
    })
    const dazeMat = new MeshBasicMaterial({ color: '#ffe14a' })
    const angryMat = new MeshBasicMaterial({ color: '#ff7a2a' })

    // Telegraphed traps: a marker flashes on the ground, quicker and quicker,
    // then spikes burst up, a rock lands or a geyser erupts there
    const TRAPS = 48
    const TRAP_LOOK = {
      spike: { r: 13, up: 0.9, tall: 28 },
      rock: { r: 15, up: 0.7, tall: 18 },
      geyser: { r: 16, up: 0.9, tall: 70 },
    }
    const markMat = new MeshBasicMaterial({ color: '#ff3b3b', transparent: true, opacity: 0.55, side: DoubleSide, depthWrite: false })
    const marks = new InstancedMesh(keep(new CircleGeometry(1, 20).rotateX(-Math.PI / 2)), markMat, TRAPS)
    const spikeMat = lambert('#e3d2ad')
    const spikes = new InstancedMesh(keep(new ConeGeometry(1, 1, 4).translate(0, 0.5, 0)), spikeMat, TRAPS * 3)
    const rockMat = lambert('#7d7468')
    const rocks = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), rockMat, TRAPS)
    const geyserMat = new MeshBasicMaterial({ color: '#a9e8ff', transparent: true, opacity: 0.75, depthWrite: false })
    const geysers = new InstancedMesh(keep(new BoxGeometry(1, 1, 1).translate(0, 0.5, 0)), geyserMat, TRAPS)
    ;[marks, spikes, rocks, geysers].forEach((m) => {
      m.frustumCulled = false
      m.count = 0
      woods.add(m)
    })
    let traps = []
    let trapSound = 0
    // warn: seconds of flashing before it goes off; follow: seconds before
    // that spent tracking the squirrel; delay: seconds before it appears
    const addTrap = (kind, x, z, warn, delay = 0, follow = 0) => {
      if (traps.length >= TRAPS) return null
      const trap = { kind, pos: new Vector3(x, 0, z), warn, delay, up: 0, hit: false, ...TRAP_LOOK[kind] }
      trap.left = warn + follow
      traps.push(trap)
      return trap
    }
    // Somewhere near the squirrel, inside the ring
    const nearSquirrel = (spread) => {
      const s = world.squirrel
      const at = new Vector3(s.pos.x + rand(-spread, spread), 0, s.pos.z + rand(-spread, spread))
      const out = at.clone().sub(wallCentre).setY(0)
      if (out.length() > BOSS_RADIUS - 20) out.setLength(BOSS_RADIUS - 20)
      return wallCentre.clone().add(out)
    }
    const placeTrap = (mesh, n, x, y, z, sx, sy, sz, spin = 0) => {
      bugEuler.set(0, spin, 0)
      bugQuat.setFromEuler(bugEuler)
      rainAt.set(x, y, z)
      bugScale.set(Math.max(0.001, sx), Math.max(0.001, sy), Math.max(0.001, sz))
      bugMatrix.compose(rainAt, bugQuat, bugScale)
      mesh.setMatrixAt(n, bugMatrix)
    }
    const updateTraps = (dt) => {
      const s = world.squirrel
      const hazards = world.staged && view.p > 0.9 && s.introFor <= 0
      trapSound -= dt
      let nMark = 0
      let nSpike = 0
      let nRock = 0
      let nGeyser = 0
      traps = traps.filter((t) => {
        if (t.delay > 0) {
          t.delay -= dt
          return true
        }
        if (t.left > 0) {
          t.left -= dt
          // A ripple that tracks the squirrel before it settles
          if (t.left > t.warn) t.pos.lerp(new Vector3(s.pos.x, 0, s.pos.z), Math.min(1, dt * 3.5))
          const urgency = 1 - clamp(t.left / t.warn, 0, 1)
          const blink = t.left > t.warn || Math.sin(clock * (10 + 40 * urgency)) > -0.3
          if (blink && nMark < TRAPS) {
            const grow = t.kind === 'rock' ? 0.5 + 0.5 * urgency : 1
            placeTrap(marks, nMark++, t.pos.x, 0.5, t.pos.z, t.r * grow, 1, t.r * grow)
          }
          // A rock is seen falling for its last half second
          if (t.kind === 'rock' && t.left < 0.5 && nRock < TRAPS) {
            placeTrap(rocks, nRock++, t.pos.x, t.r * 0.5 + 400 * t.left * t.left, t.pos.z, t.r, t.r, t.r, t.left * 6)
          }
          if (t.left <= 0) {
            t.up = TRAP_LOOK[t.kind].up
            bossSmash(t.pos, t.r)
            if (trapSound <= 0) {
              sound.play(t.kind === 'rock' ? 'crash' : t.kind === 'spike' ? 'smash' : 'spit')
              trapSound = 0.12
            }
          }
          return true
        }
        t.up -= dt
        if (t.up <= 0) return false
        const life = TRAP_LOOK[t.kind].up
        // Bursts up fast, sinks back at the end
        const rise = ease(clamp((life - t.up) / 0.1, 0, 1)) * clamp(t.up / 0.25, 0, 1)
        const near = flat(s.pos.clone().sub(t.pos)) < t.r
        if (!t.hit && near && s.pos.y < t.tall * rise && hazards && s.deadFor <= 0 && s.safeFor <= 0) {
          t.hit = true
          hurt(s, 'bite')
        }
        if (t.kind === 'spike' && nSpike < TRAPS * 3 - 2) {
          placeTrap(spikes, nSpike++, t.pos.x, 0, t.pos.z, t.r * 0.45, t.tall * rise, t.r * 0.45, 0.4)
          placeTrap(spikes, nSpike++, t.pos.x + t.r * 0.5, 0, t.pos.z + t.r * 0.2, t.r * 0.3, t.tall * 0.6 * rise, t.r * 0.3, 1.1)
          placeTrap(spikes, nSpike++, t.pos.x - t.r * 0.4, 0, t.pos.z - t.r * 0.4, t.r * 0.3, t.tall * 0.7 * rise, t.r * 0.3, 2)
        } else if (t.kind === 'rock' && nRock < TRAPS) {
          placeTrap(rocks, nRock++, t.pos.x, t.r * 0.5 * rise, t.pos.z, t.r * rise, t.r * rise, t.r * rise, 0.3)
        } else if (t.kind === 'geyser' && nGeyser < TRAPS) {
          const wobble = 1 + Math.sin(clock * 30 + t.pos.x) * 0.12
          placeTrap(geysers, nGeyser++, t.pos.x, 0, t.pos.z, t.r * 1.2 * wobble, t.tall * rise, t.r * 1.2 * wobble)
        }
        return true
      })
      const shown = view.p > 0.5
      marks.count = shown ? nMark : 0
      spikes.count = shown ? nSpike : 0
      rocks.count = shown ? nRock : 0
      geysers.count = shown ? nGeyser : 0
      ;[marks, spikes, rocks, geysers].forEach((m) => {
        m.instanceMatrix.needsUpdate = true
      })
    }

    // Bosses flatten trees and mushrooms in their way
    const bossSmash = (pos, radius) => {
      trees
        .filter((t) => Math.hypot(t.x - pos.x, t.z - pos.z) < radius + t.trunkRadius)
        .forEach((t) => {
          while (t.hits < TREE_HITS) hitTree(t)
        })
      mushrooms.filter((m) => Math.hypot(m.x - pos.x, m.z - pos.z) < radius).forEach(smashMushroom)
    }

    // Boss shots: lobbed (lift) or flat (no lift), a little bigger than the pack's
    const bossShot = (from, to, mat, time, lift) => {
      const vel = to.clone().sub(from).divideScalar(time)
      vel.y += (lift * time) / 2
      const mesh = new Mesh(shotGeo, mat)
      woods.add(mesh)
      shots.push({ pos: from.clone(), vel, lift, mesh, life: time + 0.5, spark: false, size: 1.6 })
    }

    // One round of the boss's big attack
    const bossVolley = () => {
      const s = world.squirrel
      const sp = boss.sp
      const pace = level().bossPace
      const count = level().bossCount
      const aim = s.pos.clone().sub(sp.pos).setY(0)
      const dist = aim.length() || 1
      if (boss.move === 'webs') {
        // A fan of webs lobbed at the squirrel, a little ahead of it
        const n = Math.round(4 * count) + 1
        aim.addScaledVector(new Vector3(s.vel.x, 0, s.vel.z), 0.5 * level().lead)
        for (let i = 0; i < n; i++) {
          const dir = aim.clone().applyAxisAngle(new Vector3(0, 1, 0), (i / (n - 1) - 0.5) * 1.2)
          bossShot(sp.pos.clone().setY(14), sp.pos.clone().add(dir).setY(SQ_H / 2), shotMats.spider, Math.max(0.55, dist / 260), 300)
        }
        sound.play('spit')
        boss.gap = 0.5 * pace
      } else if (boss.move === 'venom') {
        // A ring of venom spreading out flat, each ring turned half a step
        const n = Math.round(12 * count)
        const turn = boss.left % 2 ? Math.PI / n : 0
        for (let i = 0; i < n; i++) {
          const a = (i / n) * Math.PI * 2 + turn
          const to = sp.pos.clone().add(new Vector3(Math.cos(a), 0, Math.sin(a)).multiplyScalar(400)).setY(6)
          bossShot(sp.pos.clone().setY(6), to, shotMats.snake, 400 / (130 + 40 / pace), 0)
        }
        sound.play('spit')
        boss.gap = 0.9 * pace
      } else if (boss.move === 'spikes') {
        // Right under the squirrel, scattered around it, and a line out from the boss
        const warn = Math.max(0.65, 1.1 * pace)
        addTrap('spike', s.pos.x, s.pos.z, warn)
        for (let i = 0; i < Math.round(4 * count); i++) {
          const at = nearSquirrel(90)
          addTrap('spike', at.x, at.z, warn)
        }
        const step = aim.clone().divideScalar(dist)
        for (let d = 40; d < dist + 30; d += 26) addTrap('spike', sp.pos.x + step.x * d, sp.pos.z + step.z * d, warn, d / 400)
        sound.play('smash')
        boss.gap = warn + 0.4
      } else if (boss.move === 'rockfall') {
        // Rocks drop where the squirrel is (or is heading) and nearby
        const warn = Math.max(0.75, 1.2 * pace)
        const lead = new Vector3(s.vel.x, 0, s.vel.z).multiplyScalar(warn * 0.5 * level().lead)
        addTrap('rock', s.pos.x + lead.x, s.pos.z + lead.z, warn)
        for (let i = 0; i < Math.round(2 * count) - 1; i++) {
          const at = nearSquirrel(80)
          addTrap('rock', at.x, at.z, warn, rand(0, 0.3))
        }
        boss.gap = 0.45 * pace
      }
    }

    let boss = null
    let bossIn = rand(120, 200)
    let wallRise = 0
    const wallCentre = new Vector3()

    const clearBoss = () => {
      if (boss) {
        woods.remove(boss.sp.group, boss.sp.shadow)
        const i = spiders.indexOf(boss.sp)
        if (i >= 0) spiders.splice(i, 1)
      }
      boss = null
      traps = []
      wallRise = 0
      walls.visible = false
      thorns.visible = false
      quake.visible = false
      starMeshes.forEach((m) => {
        m.visible = false
      })
      bossBar.classList.remove('show', 'exposed')
    }
    const spawnBoss = () => {
      const s = world.squirrel
      const kind = pickOne([].concat(BIOMES[biome].enemy))
      const sp = makeEnemy(kind)
      sp.boss = true
      sp.size = BOSS_SIZE
      sp.alive = true
      sp.deadFor = 0
      sp.hp = BOSS_HITS
      sp.pos.set(s.pos.x + rand(-60, 60), 0, s.pos.z - rand(240, 300))
      sp.vy = 0
      sp.leapFor = 0
      spiders.push(sp)
      boss = { sp, kind, move: BOSS_MOVES[kind], name: bossName(kind), phase: 'waiting', mode: 'stalk', t: 0, hits: 0, ring: 0, left: 0, gap: 0, dir: new Vector3() }
    }
    const showBossBar = () => {
      bossNameEl.textContent = boss.name
      bossFill.style.width = `${(100 * (BOSS_HITS - boss.hits)) / BOSS_HITS}%`
      bossBar.classList.toggle('exposed', boss.mode === 'dazed')
    }
    const startFight = () => {
      boss.phase = 'fight'
      boss.mode = 'stalk'
      boss.t = 1.6 * level().bossPace
      // Centred between the two, so the squirrel starts well inside
      wallCentre.copy(boss.sp.pos).lerp(world.squirrel.pos, 0.5).setY(0)
      // Keep the squirrel inside, even if it was right at the edge
      showBossBar()
      bossBar.classList.add('show')
      sound.play('roar')
    }
    const winBoss = () => {
      const s = world.squirrel
      const sp = boss.sp
      boss.phase = 'won'
      sp.alive = false
      sp.deadFor = SPIDER_DEATH * 1.6
      sp.hidden = false
      traps = []
      bossBar.classList.remove('exposed')
      sound.play('victory')
      // Rewards: a super nut, an extra heart slot and a full heal
      spawnAcorn(sp.pos.clone().setY(AC_R + 30), true)
      world.bonusLives = Math.min(2, (world.bonusLives || 0) + 1)
      world.lives = maxLives()
      showScore()
      popup('+1', 'life')
      showToast(`${boss.name.split(',')[0]} defeated! Extra heart`)
      bossIn = rand(160, 260)
      s.safeFor = 1
    }

    // Inside the bramble ring until the boss is beaten
    const holdInArena = (s) => {
      if (!boss || boss.phase !== 'fight') return
      const out = s.pos.clone().sub(wallCentre).setY(0)
      const d = out.length()
      const limit = BOSS_RADIUS - 16
      if (d <= limit) return
      out.setLength(limit)
      s.pos.x = wallCentre.x + out.x
      s.pos.z = wallCentre.z + out.z
      const n = out.normalize()
      const outward = s.vel.x * n.x + s.vel.z * n.z
      if (outward > 0) {
        s.vel.x -= n.x * outward
        s.vel.z -= n.z * outward
      }
      if (s.climb) letGo(s)
    }

    const updateBoss = (dt) => {
      const s = world.squirrel
      if (!world.staged) {
        if (boss) clearBoss()
        return
      }
      const hazards = view.p > 0.9 && s.introFor <= 0
      if (!boss) {
        if (!world.paused && hazards && s.deadFor <= 0) {
          bossIn -= dt
          if (bossIn <= 0) spawnBoss()
        }
        return
      }
      const sp = boss.sp
      // Walls rise as the fight starts and sink once it's won
      wallRise = clamp(wallRise + (boss.phase === 'fight' ? dt : -dt) * 1.4, 0, 1)
      walls.visible = wallRise > 0 && view.p > 0.5
      thorns.visible = walls.visible
      if (walls.visible) {
        // The ring stays whole, even between the camera and the squirrel, so
        // it never looks like there's a way out
        for (let i = 0; i < WALLS; i++) {
          const a = (i / WALLS) * Math.PI * 2
          const h = (34 + ((i * 37) % 13)) * ease(wallRise)
          bugEuler.set(0, a, 0)
          bugQuat.setFromEuler(bugEuler)
          rainAt.set(wallCentre.x + Math.cos(a) * BOSS_RADIUS, 0, wallCentre.z + Math.sin(a) * BOSS_RADIUS)
          bugScale.set(1, Math.max(0.001, h), 1)
          bugMatrix.compose(rainAt, bugQuat, bugScale)
          walls.setMatrixAt(i, bugMatrix)
          for (let k = 0; k < 2; k++) {
            rainAt.set(
              wallCentre.x + Math.cos(a + (k ? 0.03 : -0.03)) * (BOSS_RADIUS + (k ? 6 : -6)),
              h * (k ? 0.45 : 0.75),
              wallCentre.z + Math.sin(a + (k ? 0.03 : -0.03)) * (BOSS_RADIUS + (k ? 6 : -6)),
            )
            bugScale.setScalar(Math.max(0.001, ease(wallRise)))
            bugMatrix.compose(rainAt, bugQuat, bugScale)
            thorns.setMatrixAt(i * 2 + k, bugMatrix)
          }
        }
        walls.instanceMatrix.needsUpdate = true
        thorns.instanceMatrix.needsUpdate = true
      }
      if (boss.phase === 'won') {
        sp.deadFor = Math.max(0, sp.deadFor - dt)
        starMeshes.forEach((m) => {
          m.visible = false
        })
        if (sp.deadFor <= 0 && wallRise <= 0) {
          bossBar.classList.remove('show')
          clearBoss()
        }
        return
      }
      if (world.paused) return
      const to = s.pos.clone().sub(sp.pos).setY(0)
      const dist = to.length()
      const target = dist > 0 ? to.clone().divideScalar(dist) : new Vector3(1, 0, 0)
      if (boss.phase === 'waiting') {
        // Prowls about on the spot, waiting for the squirrel to come close
        sp.heading = Math.atan2(-target.z, target.x)
        sp.phase += dt * 4
        sp.moving = true
        if (dist < 190 && hazards && s.deadFor <= 0) startFight()
        if (dist > 900) {
          clearBoss()
          bossIn = rand(40, 80)
        }
        return
      }

      // The fight. Harder levels wait less, warn for less and stay dazed for less.
      const pace = level().bossPace
      const count = level().bossCount
      const dazedFor = (t) => {
        boss.mode = 'dazed'
        boss.t = t * level().bossDaze
        boss.dazedLength = boss.t
        sp.hidden = false
      }
      const angry = 1 + boss.hits * 0.25
      boss.t -= dt
      sp.flashFor = Math.max(0, sp.flashFor - dt)
      let speed = 0
      let dir = target
      if (boss.mode === 'stalk') {
        // Prowling closer, untouchable; in its last stand it spits too
        speed = 70 * angry
        if (boss.hits >= level().bossSpit) {
          sp.shotCd = (sp.shotCd || 0) - dt
          if (sp.shotCd <= 0 && dist > 60) {
            shoot(sp, false)
            sp.shotCd = 2.2 * pace
          }
        }
        if (boss.t <= 0) {
          boss.mode = 'windup'
          boss.t = Math.max(0.35, (0.9 / angry + 0.3) * pace)
          // How many leaps, charges or rounds this attack has
          boss.left = {
            leap: Math.round(count),
            charge: Math.round(1 + count),
            webs: Math.round(3 * count),
            venom: Math.round(2 * count),
            spikes: Math.round(2 * count),
            rockfall: Math.round(5 * count),
            ambush: 1,
          }[boss.move]
        }
      } else if (boss.mode === 'windup') {
        // Rears up and glows: the attack is coming
        speed = 0
        if (boss.t <= 0) {
          if (boss.move === 'leap') {
            boss.mode = 'leap'
            const air = 1.1 * Math.sqrt(pace)
            sp.leap = to.clone().divideScalar(air)
            sp.vy = (GRAVITY * air) / 2
            sp.pos.y = 0.1
            sp.leapFor = air
          } else if (boss.move === 'charge') {
            boss.mode = 'charge'
            boss.dir.copy(target)
            boss.t = 2.4
          } else if (boss.move === 'ambush') {
            // Dives under; a ripple tracks the squirrel, settles, then it bursts up
            boss.mode = 'under'
            sp.hidden = true
            sound.play('quake')
            const follow = 1.4 * pace
            const warn = Math.max(0.55, 0.85 * pace)
            boss.trap = addTrap('geyser', s.pos.x, s.pos.z, warn, 0, follow)
            for (let i = 0; i < Math.round(3 * count); i++) {
              const at = nearSquirrel(110)
              addTrap('geyser', at.x, at.z, warn, follow)
            }
            boss.t = follow + warn
          } else {
            if (boss.move === 'rockfall') sound.play('quake')
            boss.mode = 'volley'
            boss.t = 0
          }
        }
      } else if (boss.mode === 'charge') {
        // Straight at where the squirrel was, until it slams into the brambles
        dir = boss.dir
        speed = (330 + boss.hits * 30) / Math.sqrt(pace)
        const out = sp.pos.clone().add(boss.dir.clone().multiplyScalar(speed * dt)).sub(wallCentre).setY(0)
        if (out.length() > BOSS_RADIUS - 24 || boss.t <= 0) {
          sound.play('crash')
          speed = 0
          boss.left -= 1
          if (boss.left > 0) {
            // Shakes it off, turns and goes again
            boss.mode = 'windup'
            boss.t = Math.max(0.3, 0.55 * pace)
          } else {
            dazedFor(2.6)
          }
        }
      } else if (boss.mode === 'leap') {
        dir = sp.leap.clone().normalize()
        sp.pos.addScaledVector(sp.leap, dt)
        sp.vy -= GRAVITY * dt
        sp.pos.y = Math.max(0, sp.pos.y + sp.vy * dt)
        if (sp.pos.y <= 0) {
          // Lands with a shockwave; jump over the ring as it rolls out
          boss.left -= 1
          if (boss.left > 0) {
            boss.mode = 'windup'
            boss.t = Math.max(0.3, 0.5 * pace)
          } else {
            dazedFor(2.4)
          }
          boss.ring = 1
          boss.ringAt = sp.pos.clone()
          sp.leapFor = 0
          sp.vy = 0
          sound.play('quake')
          bossSmash(sp.pos, 60)
        }
      } else if (boss.mode === 'volley') {
        // Webs, venom, spikes or rocks, round after round, then worn out
        speed = 0
        if (boss.t <= 0) {
          if (boss.left > 0) {
            bossVolley()
            boss.left -= 1
            boss.t = boss.gap
          } else {
            dazedFor(2.6)
          }
        }
      } else if (boss.mode === 'under') {
        // Underground, following the ripple
        speed = 0
        if (boss.trap) sp.pos.set(boss.trap.pos.x, 0, boss.trap.pos.z)
        if (boss.t <= 0) {
          boss.mode = 'erupt'
          sp.hidden = false
          sp.pos.y = 0.1
          sp.vy = 240
          boss.trap = null
        }
      } else if (boss.mode === 'erupt') {
        // Bursts up out of the geyser and flops down dazed
        speed = 0
        sp.vy -= GRAVITY * dt
        sp.pos.y = Math.max(0, sp.pos.y + sp.vy * dt)
        if (sp.pos.y <= 0) {
          sp.vy = 0
          sound.play('crash')
          bossSmash(sp.pos, 45)
          dazedFor(2.6)
        }
      } else if (boss.mode === 'dazed') {
        // Dizzy: open to a pounce
        speed = 0
        if (boss.t <= 0) {
          boss.mode = 'stalk'
          boss.t = (rand(1.4, 2.4) / angry) * pace
        }
      } else if (boss.mode === 'recover') {
        // Hurt and furious: bounds back, untouchable for a moment
        speed = -120
        if (boss.t <= 0) {
          boss.mode = 'stalk'
          boss.t = (rand(1.2, 2) / angry) * pace
        }
      }
      sp.pos.addScaledVector(dir, speed * dt)
      // Kept inside its own ring
      const fromCentre = sp.pos.clone().sub(wallCentre).setY(0)
      if (fromCentre.length() > BOSS_RADIUS - 24) {
        fromCentre.setLength(BOSS_RADIUS - 24)
        sp.pos.x = wallCentre.x + fromCentre.x
        sp.pos.z = wallCentre.z + fromCentre.z
      }
      if (boss.mode !== 'charge' && boss.mode !== 'recover') sp.heading = Math.atan2(-target.z, target.x)
      else if (boss.mode === 'charge') sp.heading = Math.atan2(-boss.dir.z, boss.dir.x)
      sp.phase += dt * Math.max(Math.abs(speed), boss.mode === 'leap' ? 200 : boss.mode === 'dazed' ? 250 : 0) * 0.12
      sp.moving = Math.abs(speed) > 0
      // Tramples whatever it moves through
      const body = Math.max(30, sp.cfg.shadow[0] * BOSS_SIZE * 0.45)
      if (!sp.hidden && sp.pos.y < 20 && boss.mode !== 'dazed') bossSmash(sp.pos, body * 0.8)
      sp.windup = boss.mode === 'windup' ? 1 : 0

      // The shockwave ring
      if (boss.ring > 0) {
        const radius = (1 - boss.ring) * 230 + 10
        boss.ring = Math.max(0, boss.ring - dt / 1.05)
        quake.visible = boss.ring > 0 && view.p > 0.5
        quake.position.set(boss.ringAt.x, 0.6, boss.ringAt.z)
        quake.scale.set(radius, 1, radius)
        quakeMat.opacity = 0.85 * boss.ring
        const fromRing = Math.abs(flat(s.pos.clone().sub(boss.ringAt)) - radius)
        if (fromRing < 9 && s.pos.y < 4 && s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
      } else {
        quake.visible = false
      }

      // Touching it hurts, except while it's dazed
      const reach = Math.max(30, sp.cfg.shadow[0] * BOSS_SIZE * 0.45)
      const close = flat(s.pos.clone().sub(sp.pos)) < reach && Math.abs(s.pos.y - sp.pos.y) < 30
      if (close && !sp.hidden && boss.mode !== 'dazed' && s.deadFor <= 0 && s.safeFor <= 0 && !(s.attackFor > 0)) hurt(s, 'bite')
      // Pounces: a hit while it's dazed, a clank off it otherwise
      if (!sp.hidden && s.attackFor > 0 && s.struck && !s.struck.has(sp) && flat(mouthOf(s).sub(sp.pos)) < reach + 6) {
        s.struck.add(sp)
        if (boss.mode === 'dazed') {
          boss.hits += 1
          sp.flashFor = 0.3
          sound.play('kill')
          if (boss.hits >= BOSS_HITS) {
            showBossBar()
            winBoss()
            return
          }
          boss.mode = 'recover'
          boss.t = 1.1
        } else {
          // Untouchable: the pounce clanks off and it bites back
          sound.play('clank')
          const away = s.pos.clone().sub(sp.pos).setY(0).setLength(220)
          s.vel.set(away.x, 160, away.z)
          s.attackFor = 0
          if (s.deadFor <= 0 && s.safeFor <= 0) hurt(s, 'bite')
        }
      }
      showBossBar()

      // Dizzy stars circling its head
      const dazed = boss.mode === 'dazed'
      starMeshes.forEach((m, i) => {
        m.visible = dazed && view.p > 0.5
        if (!m.visible) return
        const a = clock * 4 + (i * Math.PI * 2) / 3
        m.position.set(sp.pos.x + Math.cos(a) * 18, sp.pos.y + 30 * (BOSS_SIZE / 2) + Math.sin(clock * 8 + i) * 2, sp.pos.z + Math.sin(a) * 18)
        m.rotation.set(clock * 3, clock * 5, 0)
      })
    }

    // ----- Smashing things -----
    // Knocked-off voxels hang a moment, top first, then tumble and dissolve away
    const DEBRIS = 900
    const debrisMesh = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), lambert('#ffffff'), DEBRIS)
    debrisMesh.frustumCulled = false
    // Colours first: the colour buffer is sized from the count when it's made
    for (let i = 0; i < DEBRIS; i++) debrisMesh.setColorAt(i, new Color('#ffffff'))
    debrisMesh.count = 0
    woods.add(debrisMesh)
    let debris = []
    const debrisEuler = new Euler()
    const squirrelGlow = new PointLight('#ffe6c0', 0, 120, 2)
    woods.add(squirrelGlow)

    // Little puffs of dust kicked up where its paws land
    const DUST = 80
    const dustMesh = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), lambert('#c9b48a'), DUST)
    dustMesh.frustumCulled = false
    dustMesh.count = 0
    woods.add(dustMesh)
    let dust = []
    const kickDust = (s) => {
      const back = facing(s).multiplyScalar(-SQ_W * 0.3)
      for (let i = 0; i < 4; i++) {
        dust.push({
          pos: new Vector3(s.pos.x + back.x + rand(-4, 4), 1, s.pos.z + back.z + rand(-4, 4)),
          vel: new Vector3(back.x * 0.6 + rand(-12, 12), rand(8, 22), back.z * 0.6 + rand(-12, 12)),
          spin: rand(-3, 3),
          size: rand(1.8, 3),
          age: 0,
          life: rand(0.35, 0.6),
        })
      }
      dust = dust.slice(-DUST)
    }
    // Water thrown up where its paws land in a pool or puddle
    const DROPS = 120
    const dropMat = new MeshBasicMaterial({ color: '#d6f1ff', transparent: true, opacity: 0.85 })
    const dropMesh = new InstancedMesh(keep(new BoxGeometry(1, 1, 1)), dropMat, DROPS)
    dropMesh.frustumCulled = false
    dropMesh.count = 0
    woods.add(dropMesh)
    let splashDrops = []
    const kickSplash = (s, count) => {
      for (let i = 0; i < count; i++) {
        const a = rand(0, Math.PI * 2)
        const out = rand(10, 30) * (count > 8 ? 1.6 : 1)
        splashDrops.push({
          pos: new Vector3(s.pos.x + Math.cos(a) * 3, 1, s.pos.z + Math.sin(a) * 3),
          vel: new Vector3(Math.cos(a) * out + s.vel.x * 0.2, rand(40, 90) * (count > 8 ? 1.3 : 1), Math.sin(a) * out + s.vel.z * 0.2),
          size: rand(1.2, 2.2),
          age: 0,
        })
      }
      splashDrops = splashDrops.slice(-DROPS)
    }
    const inWater = (s) =>
      s.pos.y < 1 && (waters.some((w) => Math.hypot((s.pos.x - w.x) / w.rx, (s.pos.z - w.z) / w.rz) < 1) || onPuddle(s))
    const updateDrops = (dt) => {
      splashDrops = splashDrops.filter((d) => {
        d.age += dt
        d.vel.y -= GRAVITY * 0.8 * dt
        d.pos.addScaledVector(d.vel, dt)
        return d.pos.y > 0 && d.age < 1.2
      })
      splashDrops.forEach((d, i) => {
        bugQuat.identity()
        bugScale.set(d.size, d.size * 1.4, d.size).multiplyScalar(Math.max(0.001, view.e))
        bugMatrix.compose(d.pos, bugQuat, bugScale)
        dropMesh.setMatrixAt(i, bugMatrix)
      })
      dropMesh.count = splashDrops.length
      dropMesh.instanceMatrix.needsUpdate = true
    }

    const updateDust = (dt) => {
      updateDrops(dt)
      dust = dust.filter((d) => {
        d.age += dt
        d.pos.addScaledVector(d.vel, dt)
        d.vel.multiplyScalar(Math.exp(-4 * dt))
        return d.age < d.life
      })
      dust.forEach((d, i) => {
        const t = d.age / d.life
        // Billows out, then thins away
        const size = d.size * (0.6 + t * 1.2) * (1 - t * t)
        debrisEuler.set(0, d.spin * d.age, 0)
        bugQuat.setFromEuler(debrisEuler)
        bugScale.setScalar(Math.max(0.001, size * view.e))
        bugMatrix.compose(d.pos, bugQuat, bugScale)
        dustMesh.setMatrixAt(i, bugMatrix)
      })
      dustMesh.count = dust.length
      dustMesh.instanceMatrix.needsUpdate = true
    }

    const shatter = (pieces) => {
      debris = debris.concat(pieces).slice(-DEBRIS)
    }
    const updateDebris = (dt) => {
      debris = debris.filter((d) => {
        if (d.delay > 0) {
          d.delay -= dt
          return true
        }
        d.age += dt
        d.vel.y -= GRAVITY * 0.35 * dt
        d.pos.addScaledVector(d.vel, dt)
        if (d.pos.y < d.size / 2) {
          d.pos.y = d.size / 2
          d.vel.multiplyScalar(0.4)
          d.vel.y = Math.abs(d.vel.y) * 0.3
        }
        return d.age < d.life
      })
      debris.forEach((d, i) => {
        const left = 1 - d.age / d.life
        debrisEuler.set(d.spin.x * d.age, d.spin.y * d.age, d.spin.z * d.age)
        bugQuat.setFromEuler(debrisEuler)
        bugScale.setScalar(Math.max(0.001, d.size * Math.min(1, left * 1.6) * view.e))
        bugMatrix.compose(d.pos, bugQuat, bugScale)
        debrisMesh.setMatrixAt(i, bugMatrix)
        debrisMesh.setColorAt(i, d.colour)
      })
      debrisMesh.count = debris.length
      debrisMesh.instanceMatrix.needsUpdate = true
      if (debrisMesh.instanceColor) debrisMesh.instanceColor.needsUpdate = true
    }
    const piece = (pos, colour, size, from, delay = 0) => ({
      pos,
      vel: pos.clone().sub(from).setY(0).normalize().multiplyScalar(rand(20, 70)).setY(rand(30, 110)),
      spin: new Vector3(rand(-6, 6), rand(-6, 6), rand(-6, 6)),
      colour,
      size,
      delay,
      age: 0,
      life: rand(0.7, 1.3),
    })

    // A pounced mushroom bursts into red and white bits and stays gone
    const smashMushroom = (shroom) => {
      sound.play('smash')
      world.eaten.add(shroom.id)
      shroom.chunk.group.remove(shroom.mesh)
      shroom.mesh.geometry.dispose()
      shroom.chunk.mushrooms = shroom.chunk.mushrooms.filter((m) => m !== shroom)
      indexChunks()
      const centre = new Vector3(shroom.x, 4, shroom.z)
      shatter(
        Array.from({ length: 24 }, (_, i) =>
          piece(
            new Vector3(shroom.x + rand(-5, 5), rand(2, 10), shroom.z + rand(-5, 5)),
            mushroomMats[i % 3 === 0 ? 1 : 0].color,
            VOXEL,
            centre,
          ),
        ),
      )
    }

    // Three pounces fell a tree: the top of the crown, the rest of it, then
    // the trunk. A nut tree that hasn't grown its acorn yet may shake it loose.
    const NUT_TREES = ['oak', 'wide', 'willow', 'kapok', 'palm', 'spruce']
    const hitTree = (t) => {
      t.hits += 1
      world.felled.set(t.id, t.hits)
      if (t.kind === 'boulder') sound.play(t.hits >= TREE_HITS ? 'crash' : 'smash')
      else sound.play(t.hits >= TREE_HITS ? 'fell' : 'chop')
      const { voxels, removed } = damageTree(t.voxels, t.hits)
      t.voxels = voxels
      const cos = Math.cos(t.rot)
      const sin = Math.sin(t.rot)
      const top = Math.max(0, ...removed.map((v) => v[1]))
      const centre = new Vector3(t.x, t.canopyY, t.z)
      const sample = removed.length > 220 ? removed.filter(() => Math.random() < 220 / removed.length) : removed
      shatter(
        sample.map(([x, y, z, mat]) => {
          const lx = (x + 0.5 + t.offset[0]) * TREE_VOXEL
          const lz = (z + 0.5 + t.offset[2]) * TREE_VOXEL
          const pos = new Vector3(t.x + cos * lx + sin * lz, (y + 0.5) * TREE_VOXEL, t.z - sin * lx + cos * lz)
          return piece(pos, t.mats[mat].color, TREE_VOXEL * 0.9, centre, (top - y) * 0.03 + rand(0, 0.08))
        }),
      )
      hearts.forEach((h) => {
        if (h.tree !== t || h.loose) return
        h.loose = true
        h.vy = 0
      })
      if (NUT_TREES.includes(t.kind) && !world.harvested.has(t.id) && Math.random() < 0.45) {
        world.harvested.add(t.id)
        const a = rand(0, Math.PI * 2)
        spawnAcorn(
          new Vector3(t.x + Math.cos(a) * (t.trunkRadius + 6), t.canopyY * 0.8, t.z + Math.sin(a) * (t.trunkRadius + 6)),
          Math.random() < SUPER_CHANCE,
        )
      }
      t.topY = undefined
      t.mesh.geometry.dispose()
      if (t.hits >= TREE_HITS) {
        t.chunk.group.remove(t.mesh)
        t.mats.forEach((m) => m.dispose())
        t.chunk.trees = t.chunk.trees.filter((other) => other !== t)
        indexChunks()
        return
      }
      const rebuilt = meshTree(voxels, t.offset)
      t.mesh.geometry = rebuilt.geometry
      t.geometry = rebuilt.geometry
      t.tops = rebuilt.tops
      if (t.hits >= 2 || t.kind === 'boulder') t.canopyY = Math.min(t.canopyY, Math.max(0, ...t.tops.values()))
    }

    // What a pounce lands on besides spiders: mushrooms and tree trunks
    const strikeScenery = () => {
      const s = world.squirrel
      if (!world.staged || !(s.attackFor > 0) || !s.struck) return
      const mouth = mouthOf(s)
      if (s.pos.y < 12) {
        const shroom = mushrooms.find((m) => !s.struck.has(m) && Math.hypot(m.x - mouth.x, m.z - mouth.z) < 14)
        if (shroom) {
          s.struck.add(shroom)
          smashMushroom(shroom)
        }
      }
      const tree = trees.find(
        (t) =>
          !s.struck.has(t) &&
          s.pos.y < t.canopyY &&
          Math.hypot(t.x - mouth.x, t.z - mouth.z) < t.trunkRadius + 10,
      )
      if (tree) {
        s.struck.add(tree)
        hitTree(tree)
      }
    }

    // Now and then, while lives are down, a heart ripens in a nearby tree.
    // Climbing to the top of that tree picks it.
    const updateHearts = (dt, now) => {
      const s = world.squirrel
      hearts = hearts.filter((h) => {
        if (h.loose ? flat(h.pos.clone().sub(s.pos)) < LOAD_RADIUS : trees.includes(h.tree)) return true
        woods.remove(h.mesh)
        return false
      })
      if (!world.staged) return
      heartIn -= dt
      if (world.lives < maxLives() && hearts.length < MAX_HEARTS && heartIn <= 0) {
        heartIn = rand(8, 16) * level().hearts
        const near = trees.filter((t) => {
          const d = Math.hypot(t.x - s.pos.x, t.z - s.pos.z)
          return d > 80 && d < 450 && !t.hits && t.kind !== 'boulder' && !hearts.some((h) => h.tree === t)
        })
        const tree = near[Math.floor(Math.random() * near.length)]
        if (tree) {
          const a = rand(0, Math.PI * 2)
          const mesh = new Mesh(heartGeo, heartMat)
          const pos = new Vector3(
            tree.x + Math.cos(a) * tree.canopyRadius * 0.9,
            tree.canopyY + rand(0, 8),
            tree.z + Math.sin(a) * tree.canopyRadius * 0.9,
          )
          woods.add(mesh)
          hearts.push({ tree, mesh, pos })
        }
      }
      // Below the starting lives, a heart now and then drops out of the sky
      // nearby: often on Easy, hardly ever on Insane
      if (world.lives < level().lives && s.introFor <= 0 && s.deadFor <= 0) {
        dropIn -= dt
        if (dropIn <= 0) {
          dropIn = rand(0.7, 1.3) * level().heartDrop
          const fighting = boss && boss.phase === 'fight'
          const at = fighting ? nearSquirrel(120) : new Vector3(s.pos.x + rand(-160, 160), 0, s.pos.z - rand(60, 220))
          const mesh = new Mesh(heartGeo, heartMat)
          woods.add(mesh)
          hearts.push({ tree: null, loose: true, vy: 0, mesh, pos: at.setY(160) })
        }
      }
      hearts.forEach((h) => {
        if (h.loose && h.pos.y > 8) {
          h.vy -= GRAVITY * 0.5 * dt
          h.pos.y = Math.max(8, h.pos.y + h.vy * dt)
        }
        h.mesh.position.set(h.pos.x, h.pos.y + Math.sin(now / 400) * 1.5, h.pos.z)
        h.mesh.rotation.y += dt * 1.5
        h.mesh.scale.setScalar(Math.max(0.001, view.e))
      })
      const centre = s.pos.clone().setY(s.pos.y + SQ_H / 2)
      const ripe = hearts.find(
        (h) =>
          (!h.loose && s.climb === h.tree && s.pos.y >= h.tree.canopyY - 6) ||
          (!h.loose && s.perch === h.tree) ||
          h.pos.distanceTo(centre) < 20,
      )
      if (ripe && world.lives < maxLives()) {
        world.lives += 1
        showScore()
        popup('+1', 'life')
        sound.play('heart')
        woods.remove(ripe.mesh)
        hearts = hearts.filter((h) => h !== ripe)
      }
    }

    // ----- Drawing -----
    // Footprint-sized shadow that shrinks as its owner leaves the ground
    const placeShadow = (mesh, x, z, width, depth, height, heading = 0) => {
      const fade = clamp(1 - height / 120, 0.3, 1)
      mesh.position.set(x, 0.3, z)
      mesh.rotation.y = heading
      mesh.scale.set(width * fade, 1, depth * fade)
    }

    const sky = new Color()
    const dayCycle = () => {
      const angle = world.time * Math.PI * 2
      const height = Math.sin(angle)
      const day = clamp((height + 0.15) / 0.4, 0, 1)
      const dusk = 1 - Math.min(1, Math.abs(height) / 0.25)
      sky.copy(SKY_NIGHT).lerp(SKY_DAY, day).lerp(SKY_DUSK, dusk * 0.55)
      return { angle, day }
    }

    const sync = ({ angle, day }, dt) => {
      const { p, e } = view
      const s = world.squirrel

      // Flat theme colours at rest, natural colours lit by the sun in nut mode
      tinted.forEach((m) => {
        m.color.copy(m.userData.role === 'bg' ? themeBg : themeMain).lerp(m.userData.natural, p)
        m.emissive.copy(m.color)
        m.emissiveIntensity = 1 - p
      })
      superMats.forEach((m) => {
        m.color.setHSL((clock * 0.6 + m.userData.offset) % 1, 1, 0.6)
        m.emissive.copy(m.color)
        m.emissiveIntensity = 0.6 * p + (1 - p)
      })
      // Super: the squirrel cycles the rainbow too, and the whole view trips out
      if (s.superFor > 0) {
        squirrelMats.forEach((m, i) => {
          m.color.setHSL((clock * 0.8 + i * 0.2) % 1, 0.9, 0.6)
          m.emissive.copy(m.color)
          m.emissiveIntensity = 0.45 * p
        })
      }
      const tripping = s.superFor > 0 && p > 0.01
      canvas.style.filter = tripping
        ? `hue-rotate(${Math.round((clock * 140) % 360)}deg) saturate(1.7)`
        : ''
      counter.classList.toggle('super', tripping)
      // Poisoned: a pulsing green glow that fades as it wears off
      const poison = s.poisonFor > 0 ? Math.min(1, s.poisonFor / 0.6) * (0.65 + 0.35 * Math.sin(clock * 14)) : 0
      if (poison > 0) {
        squirrelMats.forEach((m) => {
          m.color.lerp(POISON_GREEN, 0.5 * poison)
          m.emissive.copy(POISON_GREEN)
          m.emissiveIntensity = Math.max(m.emissiveIntensity, 0.55 * poison * p)
        })
      }
      // Ease the weather towards the biome the squirrel is in
      const here = world.staged ? biome : 'meadow'
      const look = BIOMES[here]
      // Passing rain or storms darken the biome's sky and bring the rain
      const wet = world.staged && weather !== 'clear' ? BIOMES[weather] : null
      const settle = 1 - Math.exp(-0.8 * dt)
      atmo.sky.lerp(biomeSky[wet && wet.skyMix > look.skyMix ? weather : here], settle)
      atmo.ground.lerp(biomeGround[here], settle)
      const want = {
        skyMix: wet ? Math.max(look.skyMix, wet.skyMix) : look.skyMix,
        light: wet ? Math.min(look.light, wet.light) : look.light,
        fog: wet ? Math.min(look.fog, wet.fog) : look.fog,
        rain: wet ? Math.max(look.rain, wet.rain) : look.rain,
        storm: wet ? Math.max(look.storm, wet.storm) : look.storm,
      }
      ;['skyMix', 'light', 'fog', 'rain', 'storm'].forEach((k) => {
        atmo[k] = lerp(atmo[k], want[k], settle)
      })
      // Overcast skies take on the weather's colour, darker still at night
      sky.lerp(atmo.sky.clone().lerp(SKY_NIGHT, (1 - day) * 0.85), atmo.skyMix)
      groundMat.color.copy(atmo.ground)
      waterMat.emissiveIntensity = 0.7 + Math.sin(clock * 2) * 0.25
      // Lightning lights up everything for a moment
      const strike = flash * (0.7 + 0.3 * Math.sin(clock * 70))
      sky.lerp(BOLT_WHITE, strike * 0.55)
      hemi.intensity = ((0.5 + 0.3 * day) * atmo.light + strike * 1.4) * p
      hemi.color.copy(sky).lerp(SUN_NIGHT, 1 - day)
      sun.intensity = (0.35 + 0.55 * day) * atmo.light * atmo.light * p
      sun.color.copy(SUN_NIGHT).lerp(SUN_DAY, day)
      // Sun by day, moon by night, both sweeping across the sky
      const arc = day > 0.5 ? angle : angle + Math.PI
      sun.position.set(
        s.pos.x + Math.cos(arc) * 300,
        Math.max(0.25, Math.abs(Math.sin(arc))) * 400,
        s.pos.z + 150,
      )
      sun.target.position.copy(s.pos)
      const horizon = (body, a, lift) => {
        body.position.set(s.pos.x + Math.cos(a) * 750, 20 + Math.sin(a) * lift, s.pos.z - 1700)
        body.visible = Math.sin(a) > -0.1
      }
      horizon(sunMesh, angle, 100)
      horizon(moonMesh, angle + Math.PI, 90)
      // Hidden behind the clouds in bad weather
      if (atmo.skyMix > 0.5) {
        sunMesh.visible = false
        moonMesh.visible = false
      }
      scene.fog.color.copy(sky)
      shadowMat.opacity = 0.3 * p
      starMat.opacity = (1 - day) * p * (1 - atmo.skyMix)

      woods.visible = p > 0.01
      chunks.forEach((c) => {
        c.group.scale.y = Math.max(0.001, e * ease(c.grow))
      })
      ground.position.set(s.pos.x, 0, s.pos.z)
      stars.position.set(s.pos.x, 0, s.pos.z)
      // Fade trees standing between the camera and the squirrel, but never the
      // one it's on, climbing or jumping onto, nor ones the view clears
      const cam = camera.position
      const seg = new Vector3(s.pos.x - cam.x, 0, s.pos.z - cam.z)
      const segLen = seg.lengthSq()
      trees.forEach((t) => {
        if (t.topY === undefined) t.topY = Math.max(0, ...t.tops.values())
        const along = clamp(((t.x - cam.x) * seg.x + (t.z - cam.z) * seg.z) / (segLen || 1), 0, 1)
        const miss = Math.hypot(cam.x + seg.x * along - t.x, cam.z + seg.z * along - t.z)
        const sightY = cam.y + (s.pos.y - cam.y) * along
        const underfoot =
          t === s.perch ||
          t === s.climb ||
          Math.hypot(s.pos.x - t.x, s.pos.z - t.z) < t.canopyRadius + TREE_VOXEL * 3
        const blocks = !underfoot && along < 0.97 && miss < t.canopyRadius + 6 && t.topY > sightY
        t.fade = clamp(t.fade + (blocks ? -dt : dt) * 4, 0.15, 1)
        t.mats.forEach((m) => {
          m.opacity = t.fade
          m.depthWrite = t.fade > 0.99
        })
      })

      const running = flat(s.vel) > 5
      let step = running ? Math.floor(s.phase) % 2 : 1
      // Bounding gait while running, nibbling bob while eating
      let bob = 0
      let hop = 0
      let pitch = 0
      let stretch = 0
      if (p < 0.5) {
        // Flat view: snapped to whole pixels like the sprite it came from
        if (running) bob = Math.round(Math.abs(Math.sin(s.phase * Math.PI)) * 2) * VOXEL
        else if (s.eatFor > 0) bob = -(Math.floor(s.phase) % 2) * VOXEL
      } else {
        // Woods: a squirrel's bound. Hind legs push off nose-up, it arcs
        // through the air stretched out, lands nose-down on its front paws
        // and gathers itself (squashed) for the next push. Eases in and out
        // of it as it speeds up and slows down.
        const want = s.grounded && !s.climb && s.attackFor <= 0 ? clamp(flat(s.vel) / PLAYER_SPEED, 0, 1) : 0
        s.gait = lerp(s.gait || 0, want, 1 - Math.exp(-10 * dt))
        const c = (s.phase / 3) % 1
        const AIR = 0.6 // share of each bound spent in the air
        const air = c < AIR ? c / AIR : 0
        const ground = c < AIR ? 0 : (c - AIR) / (1 - AIR)
        hop = 4 * air * (1 - air) * 7 * s.gait
        pitch =
          (c < AIR ? Math.cos(Math.PI * air) : Math.cos(Math.PI * (1 - ground))) * 0.2 * s.gait
        stretch = (c < AIR ? 0.1 * Math.sin(Math.PI * air) : -0.1 * Math.sin(Math.PI * ground)) * s.gait
        if (s.gait > 0.05) step = c < AIR ? 0 : 1
        // Paws hit the ground: a puff of dust behind it
        const landed = s.lastBound !== undefined && s.lastBound < AIR && c >= AIR
        if (landed && s.gait > 0.4 && s.pos.y < 1) {
          if (inWater(s)) kickSplash(s, 5)
          else kickDust(s)
        }
        s.lastBound = c
        if (s.eatFor > 0 && !running) bob = -Math.abs(Math.sin(s.phase * Math.PI)) * VOXEL * 0.8
      }
      // A big splash on stepping or dropping into the water
      const inPool = world.staged && p > 0.5 && inWater(s)
      if (inPool && !s.wasWet) {
        kickSplash(s, 16)
        sound.play('spit')
      }
      s.wasWet = inPool
      // A soft glow so it can be picked out in the dark and the gloom
      const dark = clamp(1 - daylight * atmo.light, 0, 1)
      squirrelGlow.intensity = world.staged ? (0.25 + 1.3 * dark) * clamp((p - 0.5) * 2, 0, 1) : 0
      squirrelGlow.position.set(s.pos.x, s.pos.y + SQ_H + 10, s.pos.z)
      squirrelGroup.position.copy(s.pos)
      squirrelGroup.position.y += hop
      // Nose up while climbing
      squirrelGroup.rotation.set(s.tilt, s.heading, s.climb ? Math.PI / 2 : 0)
      // Blinks while untouchable after coming back
      // and flashes slowly, then faster, as super power runs out
      const left = s.superFor || 0
      const flashRate = left > 0 && left < SUPER_WARNING ? lerp(3, 14, 1 - left / SUPER_WARNING) : 0
      squirrelGroup.visible =
        (s.safeFor <= 0 || left > 0 || Math.floor(s.safeFor * 10) % 2 === 0) &&
        (flashRate === 0 || Math.floor(clock * flashRate) % 2 === 0)
      squirrelBody.position.y = bob
      // Pounce: rear up, then dive nose-first
      const pounce = s.attackFor > 0 ? 1 - s.attackFor / POUNCE_TIME : 0
      squirrelBody.rotation.z = 0.55 * Math.sin(pounce * Math.PI * 2) + pitch
      squirrelBody.scale.set(1 + 0.18 * Math.sin(pounce * Math.PI) + stretch, 1 - stretch * 0.8, 1)
      squirrelLegs.forEach((legs, i) => {
        legs.visible = i === step && !s.gliding
        legs.rotation.z = pitch
      })
      // Gliding: spread flat and wide like a flying squirrel
      if (s.gliding && p > 0.5) {
        squirrelBody.scale.set(1.15, 0.7, 1.8)
        squirrelBody.rotation.z = -0.1
      }
      squirrelShadow.visible = p > 0.01
      placeShadow(squirrelShadow, s.pos.x, s.pos.z, SQ_W * 0.75, SQ_D, s.pos.y, s.heading)

      acornMeshes.forEach((m, a) => {
        if (world.acorns.includes(a)) return
        scene.remove(m.body, m.shadow)
        acornMeshes.delete(a)
      })
      world.acorns.forEach((a) => {
        const m = meshFor(a)
        m.body.position.copy(a.pos)
        m.body.quaternion.copy(a.quat)
        m.shadow.visible = p > 0.01
        placeShadow(m.shadow, a.pos.x, a.pos.z, AC_R * 1.6, AC_R * 1.6, a.pos.y - AC_R)
      })

      spiders.forEach((sp) => {
        const shown = (sp.alive || sp.deadFor > 0) && !sp.hidden
        sp.group.visible = shown
        sp.shadow.visible = shown
        if (!shown) return
        // Hurt spiders flash red; dead ones roll onto their backs, then shrink away
        const dying = sp.alive ? 0 : 1 - sp.deadFor / SPIDER_DEATH
        const roll = ease(clamp(dying / 0.4, 0, 1))
        const fade = clamp((1 - dying) / 0.4, 0, 1)
        const red = sp.flashFor > 0 || (!sp.alive && dying < 0.6)
        const glow = sp.alive && sp.chargedFor > 0 && Math.sin(clock * 30 + sp.phase) > -0.3
        // Bosses flicker yellow while dazed and orange as they wind up
        let bossLook = null
        if (sp.boss && boss && sp.alive) {
          if (boss.mode === 'dazed' && Math.sin(clock * 12) > 0) bossLook = dazeMat
          else if (boss.mode === 'windup' && Math.sin(clock * 30) > 0) bossLook = angryMat
        }
        sp.parts.forEach(([mesh, mat]) => {
          mesh.material = red ? hurtMat : bossLook || (glow ? glowMat : mat)
        })
        // In thick fog, creatures loom out of it only when close
        const foggy = clamp((0.6 - atmo.fog) / 0.3, 0, 1)
        const near = flat(sp.pos.clone().sub(s.pos))
        const hide = sp.boss ? 1 : lerp(1, clamp((170 - near) / 50, 0, 1), foggy)
        // Frogs get about in hops
        const hop = sp.kind === 'frog' && sp.alive && sp.moving && sp.leapFor <= 0 ? Math.abs(Math.sin(sp.phase * 0.35)) * 6 : 0
        // A dazed boss flips onto its back with its legs flailing, except the
        // wolf, which keels over onto its side, and the snake, which spins
        let flip = 0
        let tip = 0
        let spin = 0
        if (sp.boss && boss && sp.alive && boss.mode === 'dazed') {
          const since = (boss.dazedLength || 1) - boss.t
          const k = ease(clamp(since / 0.35, 0, 1)) * ease(clamp(boss.t / 0.35, 0, 1))
          if (sp.kind === 'wolf') tip = k
          else if (sp.kind === 'snake') spin = k
          else flip = k
        }
        const flail = flip > 0 || tip > 0
        sp.group.position.set(sp.pos.x, sp.pos.y + hop + roll * 3 * VOXEL + flip * 3 * VOXEL * (sp.size || 1), sp.pos.z)
        // Crouches before springing, nose up through the leap
        const pitch = sp.alive && sp.leapFor > 0 ? clamp(sp.vy / 400, -0.5, 0.5) : 0
        sp.group.rotation.set(Math.max(roll, flip) * Math.PI + tip * Math.PI * 0.5, sp.heading + spin * Math.sin(clock * 9) * 1.6, pitch)
        sp.group.scale.setScalar(Math.max(0.001, e * fade * (sp.size || 1) * hide))
        if (sp.asleep && sp.alive) sp.group.scale.y *= 0.8
        if (sp.alive && sp.windup > 0) sp.group.scale.y *= 0.75
        if (sp.kind === 'spider' || sp.kind === 'scorpion') {
          sp.legs.forEach((leg, i) => {
            // Legs curl and twitch once it's over
            leg.rotation.y = sp.alive ? Math.sin(sp.phase + (i % 2) * Math.PI) * 0.45 : 0
            leg.rotation.z = sp.alive ? 0 : 0.6 + Math.sin(dying * 40 + i) * 0.15
          })
        } else if (sp.kind === 'croc' || sp.kind === 'iguana') {
          // A low sprawling waddle, swinging side to side
          sp.legs.forEach((leg, i) => {
            const pair = i === 0 || i === 3 ? 0 : Math.PI
            leg.rotation.z = sp.alive ? Math.sin(sp.phase * 0.5 + pair) * 0.6 * (sp.moving || flail ? 1 : 0) : 1.2
          })
          sp.body.rotation.y = sp.alive && sp.moving ? Math.sin(sp.phase * 0.5) * 0.12 : 0
        } else if (sp.kind === 'wolf') {
          // Diagonal pairs swing together at a lope; stiff once beaten
          sp.legs.forEach((leg, i) => {
            const pair = i === 0 || i === 3 ? 0 : Math.PI
            leg.rotation.z = sp.alive ? Math.sin(sp.phase * 0.6 + pair) * 0.7 * (sp.moving || flail ? 1 : 0) : 0.9
          })
        } else if (sp.kind === 'snake') {
          // A wave runs back along the body from the head
          const wave = sp.alive ? 2.5 : 0.4
          sp.segs.forEach((seg, i) => {
            const along = -(i + 1) * SNAKE_SEGMENT * VOXEL * 0.9
            seg.position.set(along, 0, Math.sin(sp.phase * 0.4 - i * 0.9) * wave * (i + 1) * 0.5)
            seg.rotation.y = Math.cos(sp.phase * 0.4 - i * 0.9) * 0.5 * (sp.alive ? 1 : 0)
          })
        }
        const [sw, sd] = sp.cfg.shadow
        const big = (sp.size || 1) * hide
        placeShadow(sp.shadow, sp.pos.x, sp.pos.z, sw * fade * big, sd * fade * big, 0, sp.heading)
        sp.shadow.visible = hide > 0.01
      })

      while (crumbMeshes.length < world.crumbs.length) {
        const m = new Mesh(crumbGeo, acornMats[0])
        scene.add(m)
        crumbMeshes.push(m)
      }
      crumbMeshes.forEach((m, i) => {
        const c = world.crumbs[i]
        m.visible = !!c
        if (c) m.position.copy(c.pos)
      })
    }

    const render = () => {
      const W = window.innerWidth
      const strip = clamp(Math.round(view.ground), 0, window.innerHeight)
      // Wipe everything, then draw the sky and scene in the strip above the nav
      renderer.setScissorTest(false)
      renderer.setClearColor(0x000000, 0)
      renderer.clear()
      renderer.setScissorTest(true)
      renderer.setScissor(0, window.innerHeight - strip, W, strip)
      renderer.setClearColor(sky, view.p)
      renderer.clear()
      renderer.render(scene, camera)
    }

    const tick = (now) => {
      // rAF timestamps can trail performance.now() on the first frame;
      // a paused game keeps drawing but stops the clock
      const frameDt = Math.max(0, Math.min(0.05, (now - last) / 1000))
      const dt = world.paused ? 0 : frameDt
      last = now

      world.time = (world.time + dt / DAY_LENGTH) % 1
      sound.setPlaying(world.staged && !world.paused && overlay.dataset.kind !== 'over')
      sound.setMode(world.squirrel && world.squirrel.superFor > 0 ? 'super' : 'normal')
      const fighting = boss && boss.phase === 'fight'
      sound.setTrack(fighting ? `boss:${boss.kind}:${biome}` : wetness() === 'clear' ? (world.staged ? biome : 'meadow') : wetness())
      const inWoods = view.p > 0.5
      world.spawnIn -= dt
      if (world.spawnIn <= 0) {
        if (world.acorns.length < (inWoods ? MAX_TREE_ACORNS : MAX_ACORNS)) dropAcorn()
        world.spawnIn = inWoods ? rand(0.5, 1.3) : rand(2.5, 6)
      }

      measure()
      if (world.staged) updateChunks(dt)
      const cycle = dayCycle()
      updateCamera(dt)
      updateAcorns(dt)
      updateCrumbs(dt)
      updateSquirrel(dt)
      updateSpiders(dt, 1 - cycle.day)
      updateHearts(dt, clock * 1000)
      strikeScenery()
      updateDebris(dt)
      updateDust(dt)
      updateCritters(dt, cycle.day)
      if (world.staged && world.squirrel) {
        const here = biomeAt(world.squirrel.pos.x + origin.x, world.squirrel.pos.z + origin.z)
        biome = here
      }
      updateWeather(dt)
      updateRain(dt)
      updateLightning(dt)
      updateShots(dt)
      if (world.squirrel) {
        updatePowers(dt)
        updateStrikes(dt)
        updatePuddles(dt)
        updateBoss(dt)
        updateTraps(dt)
        updateSleepZs()
        const sq = world.squirrel
        if (sq.streak && clock - sq.lastEat > COMBO_TIME) {
          sq.streak = 0
          showCombo()
        }
        comboEl.classList.toggle('ending', !!sq.streak && clock - sq.lastEat > COMBO_TIME - 1)
      }
      sound.setRain(world.staged && view.p > 0.5 && !world.paused ? atmo.rain : 0)
      updateBubbles(dt)
      sync(cycle, frameDt)
      render()
      frame = requestAnimationFrame(tick)
    }

    // ----- Input -----
    const track = (e) => {
      mouse.x = e.clientX
      mouse.y = e.clientY
      updateHover()
    }
    const forget = () => {
      mouse.x = -9999
      mouse.y = -9999
      updateHover()
    }
    // Click the strip to enter nut mode; once in, clicks attack
    const onDown = (e) => {
      if (touchOnly || e.button !== 0) return
      track(e)
      const on = (selector) => e.target.closest && e.target.closest(selector)
      if (on('.colour-toggle') || on('.nut-exit') || on('.nut-sound') || on('.nut-overlay__levels')) return
      if (!world.staged) {
        if (e.clientY < view.ground) setStaged(true)
      } else if (overlay.dataset.kind === 'over') {
        restart()
      } else if (world.paused) {
        // Click anywhere to carry on
        setPaused(false)
      } else if (view.p > 0.5 && !world.paused) {
        attack()
      }
    }
    const exit = () => setStaged(false)
    const toggleSound = () => {
      const enabled = sound.toggle()
      soundButton.classList.toggle('on', enabled)
      soundButton.setAttribute('aria-pressed', enabled)
    }
    const onContextMenu = (e) => {
      if (world.staged) e.preventDefault()
    }

    // Floating +1 / -1 by the squirrel's head that drifts up and fades
    function popup(text, kind) {
      if (!world.staged || view.p < 0.5) return
      const s = world.squirrel
      const at = new Vector3(s.pos.x, s.pos.y + SQ_H + 6, s.pos.z).project(camera)
      const el = document.createElement('span')
      el.className = `nut-pop nut-pop--${kind}`
      el.textContent = text
      // With an icon of what changed: a heart for lives, an acorn for nuts
      const icon = document.createElementNS(SVG_NS, 'svg')
      icon.setAttribute('class', 'nut-pop__icon')
      icon.setAttribute('shape-rendering', 'crispEdges')
      const shapes = kind === 'life' ? [[HEART, 1]] : [[ACORN_CAP, 0.6], [ACORN_NUT, 1]]
      icon.setAttribute('viewBox', kind === 'life' ? '0 0 7 6' : '0 0 7 7')
      shapes.forEach(([d, opacity]) => {
        const path = document.createElementNS(SVG_NS, 'path')
        path.setAttribute('d', d)
        path.setAttribute('opacity', opacity)
        icon.appendChild(path)
      })
      if (kind !== 'power') el.appendChild(icon)
      el.style.left = `${((at.x + 1) / 2) * window.innerWidth + rand(-12, 12)}px`
      el.style.top = `${((1 - at.y) / 2) * window.innerHeight}px`
      el.addEventListener('animationend', () => el.remove())
      popups.appendChild(el)
    }

    // ----- Pause, reset and game over -----
    function showOverlay(kind) {
      overlay.dataset.kind = kind || ''
      overlay.classList.toggle('show', !!kind)
      if (kind === 'over') releasePointer()
      showLevel()
      if (kind === 'over') {
        overlay.querySelector('.nut-overlay__title').textContent = 'Game over'
        overlay.querySelector('.nut-overlay__text').textContent =
          `Nuts ${world.score} / Best ${best}. Click to restart`
      } else if (kind === 'paused') {
        overlay.querySelector('.nut-overlay__title').textContent = 'Paused'
        overlay.querySelector('.nut-overlay__text').textContent = 'Press P or click to resume'
      }
    }
    // Paused with the pause card, or (info) with the controls card instead
    const setPaused = (paused, info = false) => {
      world.paused = paused
      keys.clear()
      if (paused) releasePointer()
      else lockPointer()
      if (paused && info) {
        showOverlay(null)
        clearTimeout(promptTimer)
        prompt.classList.add('show', 'centered')
      } else if (paused) {
        prompt.classList.remove('show', 'centered')
        showOverlay('paused')
      } else {
        if (overlay.dataset.kind === 'paused') showOverlay(null)
        if (prompt.classList.contains('centered')) prompt.classList.remove('show', 'centered')
      }
      pauseButton.classList.toggle('paused', paused)
      pauseButton.setAttribute('aria-label', paused ? 'Resume' : 'Pause')
    }
    // Difficulty buttons: top right cycles through them, the card picks one.
    // A new difficulty starts a new run.
    function showLevel() {
      levelButton.textContent = level().name
      levelButton.classList.toggle('insane', world.difficulty === 'insane')
      levelChoices.forEach((b) => b.classList.toggle('on', b.dataset.level === world.difficulty))
    }
    const setDifficulty = (next) => {
      if (!DIFFICULTIES[next]) return
      const changed = next !== world.difficulty
      world.difficulty = next
      store(DIFFICULTY_KEY, next)
      best = readBest(next)
      showLevel()
      showScore()
      if (changed && world.staged) restart()
    }
    const cycleDifficulty = () => {
      setDifficulty(DIFFICULTY_LEVELS[(DIFFICULTY_LEVELS.indexOf(world.difficulty) + 1) % DIFFICULTY_LEVELS.length])
    }
    const chooseDifficulty = (e) => {
      const choice = e.target.closest('button')
      if (!choice) return
      if (choice.dataset.level === world.difficulty) {
        if (world.paused) setPaused(false)
        else if (overlay.dataset.kind === 'over') restart()
      } else {
        setDifficulty(choice.dataset.level)
      }
    }
    showLevel()
    const toggleInfo = () => {
      if (!world.staged || overlay.dataset.kind === 'over') return
      if (world.paused && prompt.classList.contains('centered')) setPaused(false)
      else setPaused(true, true)
    }
    // Fullscreen: L or the button at the top right
    const toggleFullscreen = () => {
      const el = document.documentElement
      if (!el.requestFullscreen) return
      switchedFullAt = Date.now()
      const request = document.fullscreenElement ? document.exitFullscreen() : el.requestFullscreen()
      if (request && request.catch) request.catch(() => {})
    }
    const onFullscreen = () => {
      const full = !!document.fullscreenElement
      fullButton.classList.toggle('on', full)
      fullButton.setAttribute('aria-label', full ? 'Exit fullscreen' : 'Fullscreen')
      if (Date.now() - switchedFullAt < 1500 && world.staged && !world.paused) lockPointer()
    }
    const togglePause = () => {
      if (world.staged && overlay.dataset.kind !== 'over') setPaused(!world.paused)
    }
    // A fresh run in new woods
    const restart = () => {
      const s = world.squirrel
      setPaused(false)
      showOverlay(null)
      toast.classList.remove('show')
      s.gameOver = true
      s.superFor = 0
      respawn(s)
      s.climb = null
      s.pos.y = 0
    }

    const typing = (e) => /^(input|textarea|select)$/i.test(e.target.tagName) || e.target.isContentEditable
    const onKeyDown = (e) => {
      if (typing(e) || e.metaKey || e.ctrlKey || e.altKey) return
      const k = e.key.toLowerCase()
      if (touchOnly) return
      if (k === 'shift') sprinting = true
      if (k === ' ') spaceHeld = true
      if (k === 'n') {
        // Toggle nut mode from anywhere
        setStaged(!world.staged)
      } else if (k === 'escape' && world.staged) {
        setStaged(false)
      } else if (k === 'p') {
        togglePause()
      } else if (k === 'r' && world.staged) {
        restart()
      } else if (k === 'm') {
        toggleSound()
      } else if (k === 'l' && world.staged) {
        toggleFullscreen()
      } else if (world.staged && !world.paused && KEYS[k]) {
        // Moving straight away skips the squirrel's own little walk
        e.preventDefault()
        keys.add(k)
        world.squirrel.introFor = 0
      } else if (world.staged && view.p > 0.5 && !world.paused) {
        if (k === 'f' && !e.repeat && overlay.dataset.kind !== 'over') {
          attack()
        } else if (k === ' ') {
          e.preventDefault()
          const s = world.squirrel
          if (s.climb) letGo(s)
          else if (s.grounded && s.deadFor <= 0) {
            s.vel.y = JUMP_SPEED
            sound.play('jump')
          } else if (!e.repeat && s.deadFor <= 0 && s.powers && s.powers.feather > 0 && !s.doubled) {
            // The feather's double jump
            s.doubled = true
            s.vel.y = JUMP_SPEED * 0.95
            sound.play('jump')
          }
        }
      }
    }
    const onKeyUp = (e) => {
      const k = e.key.toLowerCase()
      keys.delete(k)
      if (k === 'shift') sprinting = false
      if (k === ' ') spaceHeld = false
    }
    const onBlur = () => {
      keys.clear()
      sprinting = false
      spaceHeld = false
      // Leaving the window or tab pauses the game
      if (world.staged && !world.paused && overlay.dataset.kind !== 'over') setPaused(true)
    }
    const onVisibility = () => {
      if (document.hidden) onBlur()
    }

    // Re-read theme colours whenever the toggles update the root variables
    const observer = new MutationObserver(readColours)
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })

    window.addEventListener('resize', resize)
    window.addEventListener('pointermove', track)
    window.addEventListener('pointerdown', onDown)
    exitBar.addEventListener('click', exit)
    soundButton.addEventListener('click', toggleSound)
    pauseButton.addEventListener('click', togglePause)
    levelButton.addEventListener('click', cycleDifficulty)
    infoButton.addEventListener('click', toggleInfo)
    fullButton.addEventListener('click', toggleFullscreen)
    document.addEventListener('fullscreenchange', onFullscreen)
    levelChoices.forEach((b) => b.addEventListener('click', chooseDifficulty))
    window.addEventListener('contextmenu', onContextMenu)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    document.addEventListener('visibilitychange', onVisibility)
    document.addEventListener('pointerlockchange', onLockChange)
    document.addEventListener('mouseleave', forget)
    frame = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(promptTimer)
      clearTimeout(toastTimer)
      observer.disconnect()
      if (cursorEl) cursorEl.classList.remove('in-sky')
      window.removeEventListener('resize', resize)
      window.removeEventListener('pointermove', track)
      window.removeEventListener('pointerdown', onDown)
      exitBar.removeEventListener('click', exit)
      soundButton.removeEventListener('click', toggleSound)
      pauseButton.removeEventListener('click', togglePause)
      levelButton.removeEventListener('click', cycleDifficulty)
      infoButton.removeEventListener('click', toggleInfo)
      fullButton.removeEventListener('click', toggleFullscreen)
      document.removeEventListener('fullscreenchange', onFullscreen)
      levelChoices.forEach((b) => b.removeEventListener('click', chooseDifficulty))
      window.removeEventListener('contextmenu', onContextMenu)
      document.documentElement.classList.remove('nut-mode')
      sound.dispose()
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      document.removeEventListener('visibilitychange', onVisibility)
      document.removeEventListener('pointerlockchange', onLockChange)
      releasePointer()
      document.documentElement.classList.remove('nut-locked')
      document.removeEventListener('mouseleave', forget)
      clearChunks()
      clearHearts()
      heartMat.dispose()
      ;[...scorpionMats, ...iguanaMats, waterMat, sandMat].forEach((m) => m.dispose())
      ;[...wolfMats, ...frogMats, ...snakeMats, ...crocMats, ...decorMats, rainMat, boltMat, glowMat, sparkMat].forEach((m) => m.dispose())
      Object.values(shotMats).forEach((m) => m.dispose())
      ;[dropMat, markMat, spikeMat, rockMat, geyserMat, zMat, warnMat, warnFill.material, puddleMat, wallMat, thornMat, quakeMat, dazeMat, angryMat, bubble.material, ...starMeshes.map((m) => m.material)].forEach((m) => m.dispose())
      Object.values(powerMats).forEach((mats) => mats.forEach((m) => m.dispose()))
      Object.values(nutMats).forEach((mats) => mats && mats.forEach((m) => m.dispose()))
      Object.values(nutGeos).forEach((g) => g && g.dispose())
      ;[beeMat, dragonflyMat, flyerMat, wingMat, leafMat, mistMat, splashMat].forEach((m) => m.dispose())
      ;[bubbleMat, fireflyMat, beetleMat, butterflyMat, craterMat, sunMesh.material, sunGlow.material, moonMesh.material].forEach(
        (m) => m.dispose(),
      )
      geometries.forEach((g) => g.dispose())
      allMats.forEach((m) => m.dispose())
      renderer.dispose()
    }
  }, [])

  return (
    <>
      <canvas ref={canvasRef} className="squirrel-canvas" aria-hidden="true" />
      <div ref={counterRef} className="nuts-eaten">
        Nuts <span className="nuts-eaten__count">0</span>
        <span className="nuts-eaten__combo"></span>
        <span className="nuts-eaten__label">Best</span> <span className="nuts-eaten__best">0</span>
        <span className="nuts-eaten__lives" aria-label="Lives">
          {Array.from({ length: MAX_LIVES }, (_, i) => (
            <svg key={i} className="nuts-eaten__life" viewBox="0 0 7 6" shapeRendering="crispEdges">
              <path d={HEART} />
            </svg>
          ))}
        </span>
        <span className="nuts-eaten__powers"></span>
      </div>
      <div ref={bossRef} className="boss-bar" aria-live="polite">
        <p className="boss-bar__name">
          <svg className="boss-bar__skull" viewBox="0 0 8 8" shapeRendering="crispEdges" aria-hidden="true">
            <path d="M1 0h6v1H1zM0 1h8v1H0zM0 2h1v2H0zM3 2h2v2H3zM7 2h1v2H7zM0 4h8v1H0zM1 5h2v1H1zM5 5h2v1H5zM1 6h1v1H1zM3 6h2v1H3zM6 6h1v1H6zM2 7h4v1H2z" />
          </svg>
          <span className="boss-bar__title"></span>
        </p>
        <div className="boss-bar__track">
          <div className="boss-bar__fill"></div>
        </div>
      </div>
      <div ref={hintRef} className="nuts-eaten nut-hint">
        Click to enter nut mode
      </div>
      <button ref={exitRef} className="nut-exit">
        Press Escape to exit
      </button>
      <button ref={levelRef} className="nut-sound nut-level" aria-label="Difficulty">
        Easy
      </button>
      <button ref={fullRef} className="nut-sound nut-full" aria-label="Fullscreen">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path className="nut-full__enter" d="M0 0h4v1H1v3H0zM6 0h4v4H9V1H6zM0 6h1v3h3v1H0zM9 6h1v4H6V9h3z" />
          <path className="nut-full__exit" d="M3 0h1v4H0V3h3zM6 0h1v3h3v1H6zM0 6h4v4H3V7H0zM6 6h4v1H7v3H6z" />
        </svg>
      </button>
      <button ref={infoRef} className="nut-sound nut-info" aria-label="How to play">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path fillRule="evenodd" d="M0 0h10v10H0zM1 1v8h8V1z" />
          <path d="M4 2h2v2H4zM4 5h2v3H4z" />
        </svg>
      </button>
      <button ref={pauseRef} className="nut-sound nut-pause" aria-label="Pause">
        <svg viewBox="0 0 10 10" width="15" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path className="nut-pause__bars" d="M2 1h2v8H2zM6 1h2v8H6z" />
          <path className="nut-pause__play" d="M2 1h1v8H2zM3 2h1v6H3zM4 3h1v4H4zM5 4h1v2H5z" />
        </svg>
      </button>
      <button ref={soundRef} className="nut-sound" aria-label="Sound" aria-pressed="false">
        <svg viewBox="0 0 12 10" width="18" height="15" shapeRendering="crispEdges" aria-hidden="true">
          <path className="nut-sound__speaker" d="M0 3h2l3-3h1v10H5L2 7H0z" />
          <path className="nut-sound__waves" d="M8 3h1v4H8zM10 1h1v8h-1z" />
          <path className="nut-sound__off" d="M8 3h1v1H8zM11 3h1v1h-1zM9 4h2v2H9zM8 6h1v1H8zM11 6h1v1h-1z" />
        </svg>
      </button>
      <div ref={promptRef} className="squirrel-prompt" role="status">
        <p className="squirrel-prompt__title">Nut mode</p>
        <p>
          <b>WASD</b> to move, hold <b>Shift</b> to run
        </p>
        <p>
          <b>Space</b> to jump, even onto low treetops; hold it to glide
        </p>
        <p>
          <b>Click</b> or <b>F</b> to pounce on creatures, mushrooms and trees
        </p>
        <p>
          Run into a tree to <b>climb</b> it
        </p>
        <p>
          Avoid <b>red mushrooms</b> and the woods' creatures, and look out for rainbow nuts
        </p>
        <p>
          <b>P</b> to pause, <b>R</b> to reset, <b>M</b> for sound, <b>L</b> for fullscreen. Difficulty is top right
        </p>
        <p className="squirrel-prompt__resume">Click anywhere or press P to resume</p>
      </div>
      <div ref={popupsRef} className="nut-pops" aria-hidden="true"></div>
      <div ref={overlayRef} className="squirrel-prompt nut-overlay" role="status">
        <p className="squirrel-prompt__title nut-overlay__title"></p>
        <p className="nut-overlay__text"></p>
        <div className="nut-overlay__levels">
          {DIFFICULTY_LEVELS.map((d) => (
            <button key={d} type="button" data-level={d}>
              {DIFFICULTIES[d].name}
            </button>
          ))}
        </div>
      </div>
      <div ref={toastRef} className="squirrel-prompt squirrel-toast" role="alert"></div>
    </>
  )
}

export default Squirrel
